/* eslint-disable react/no-unescaped-entities */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowRight, ChevronRight, ChevronDown, MessageCircle,
  Coffee, ChefHat, Building2, Hotel, UtensilsCrossed, Sparkles,
  Clock, TrendingUp, Check, Loader2, Send, ShieldCheck, Palmtree,
  Pizza, Users, Crown, Wifi, BatteryFull, Signal, Quote,
  Star, MapPin, Heart, Calculator, Globe, Zap, Award, Plus, Palette,
  Target, Trophy, CalendarRange, GitBranch, Settings, LogOut, ListChecks,
  UserPlus, Megaphone, BarChart3, Magnet, Gem, Compass, X, Lock,
} from 'lucide-react';
import theme from '../lib/salonPriveTheme';
import { DA, SunOrnament, BrandMark, SabrilStamp, SabrilSignature, SabrilDecorFrame, SabrilDivider, AwningCorner, MimosaBranch, PalmShadow } from '../theme/sabril-da';
import { isSalonPriveUnlocked } from '../components/SalonPriveGate';
import SabrilCloserBubble from '../components/SabrilCloserBubble';
import EconomieCheckoutModal from '../components/EconomieCheckoutModal';
import QuoteRequestModal from '../components/QuoteRequestModal';
import ContractSignModal from '../components/ContractSignModal';
import WalletCardArt from '../components/WalletCardArt';
import CafeBookingCard from '../components/CafeBookingCard';
import EbookPreviewLightbox from '../components/EbookPreviewLightbox';

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────
const openSabril = (intent) => {
  try {
    window.dispatchEvent(new CustomEvent('sabril:open', { detail: intent ? { intent } : undefined }));
  } catch (_) { /* ignore */ }
};

const formatClock = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// ─────────────────────────────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────────────────────────────
const TARGETS = [
  { icon: Coffee,           label: 'Brunch',          sub: 'Coffee Club' },
  { icon: UtensilsCrossed,  label: 'Street Food',     sub: 'Premium' },
  { icon: Palmtree,         label: 'Beach Club',      sub: 'Saisonnier' },
  { icon: Hotel,            label: 'Hôtel',           sub: 'Wellness' },
  { icon: ChefHat,          label: 'Restaurant',      sub: 'Ambiance' },
  { icon: Building2,        label: 'Premium',         sub: 'Gastronomie' },
];

const PILLARS = [
  { num: '01', label: 'Marque',         sub: 'ADN · Positionnement', icon: Sparkles,
    accent: 'Expertise 01 · ADN & marque',
    longDesc: "Avant tout, on construit une marque qu'on n'oublie pas. ADN clair, positionnement chirurgical, manifeste qui transmet votre vision à l'équipe et au client en 3 secondes.",
    deliverables: ['ADN de marque (mission, valeurs, ennemis)', 'Positionnement (cible, promesse, preuve)', 'Récit fondateur', 'Charte éditoriale (ton, vocabulaire)', 'Direction artistique cohérente'],
  },
  { num: '02', label: 'Produit',        sub: 'Menu · Food cost',     icon: Pizza,
    accent: 'Expertise 02 · Menu & marges',
    longDesc: 'On reprend la carte plat par plat. Fiches techniques au gramme près, pricing engineering, plats-stars identifiés. Vous vendez mieux, plus, et avec une marge propre.',
    deliverables: ['Fiches techniques 100% carte', 'Pricing engineering (panier +20%)', 'Plats-stars (hero items)', 'Carte boissons rentabilisée', 'Re-engineering trimestriel'],
  },
  { num: '03', label: 'Expérience',     sub: 'Design · Parcours',    icon: Star,
    accent: 'Expertise 03 · Expérience client',
    longDesc: "Votre client doit ressentir quelque chose dès la porte. On orchestre lumière, son, parcours, détails — pour que chaque visite reste un souvenir qu'on raconte.",
    deliverables: ['Parcours client minuté', 'Plan lumière + ambiance sonore', 'Signalétique intérieure', 'Rituels de service signature', 'Détails sensoriels (odeurs, textures)'],
  },
  { num: '04', label: 'Système',        sub: 'Process · RH',         icon: Users,
    accent: 'Expertise 04 · Système opérationnel',
    longDesc: "Vous partez en weekend, votre resto tourne. Process clairs, checklists imprimées, rôles définis, primes alignées. L'opérationnel ne dépend plus de vous.",
    deliverables: ['Manuel opérationnel (50+ pages)', 'Checklists (ouverture, fermeture)', 'Fiches de poste (1 par rôle)', 'Intégration 7 jours (livret, parrain)', 'Rituels d\'encadrement hebdo'],
  },
  { num: '05', label: 'Croissance',     sub: 'Acquisition · Réseaux', icon: TrendingUp,
    accent: 'Expertise 05 · Acquisition & visibilité',
    longDesc: "On déploie votre stratégie d'acquisition : Uber, Deliveroo, Glovo, ads ciblées, SEO local, partenariats. Votre resto se remplit même quand il pleut un mardi.",
    deliverables: ['Setup plateformes (carte, prix, photos pro)', 'SEO local (Google Maps top 3)', 'Campagnes Meta Ads hyperlocales', 'Partenariats influenceurs locaux', 'Tracking conversions (GA4 + Mongo)'],
  },
  { num: '06', label: 'Digital',        sub: 'Uber · Site · Auto',   icon: Globe,
    accent: 'Expertise 06 · Outils digitaux',
    longDesc: "Votre site, votre réservation, ton click & collect, ton paiement — tout en une seule stack moderne. Zéro commission sur les résa directes. L'autonomie digitale, vraie.",
    deliverables: ['Site sur-mesure (SEO local optimisé)', 'Réservation 0% commission', 'Click & collect intégré', 'Paiement Stripe / Apple Pay', 'Dashboard analytics live'],
  },
  { num: '07', label: 'Fidélisation',   sub: 'Wallet · Data',        icon: Heart,
    accent: 'Expertise 07 · Rétention client',
    longDesc: 'On installe les rituels qui transforment un one-shot en habitué qui revient 9× par an. Programme fidélité Wallet, SMS post-visite, événements VIP, anniversaires.',
    deliverables: ['Programme fidélité (Apple/Google Wallet)', 'Séquence SMS/Email post-visite', 'Événements VIP trimestriels', 'Parrainage structuré', 'Dashboard LTV par cohorte'],
  },
  { num: '08', label: 'Développement',  sub: 'Multi-sites',          icon: MapPin,
    accent: 'Expertise 08 · Duplication',
    longDesc: 'Si votre modèle tourne, on le duplique. Recherche emplacement, négo bail, dossier financier, ouverture clé en main du 2e (ou 3e) — sans casser la première adresse.',
    deliverables: ["Recherche d'emplacement (5 sites short-list)", 'Bilan financier 2e site (P&L 3 ans)', 'Négo bail commercial', 'Plan ouverture J-90 → J0', 'Dossier bancaire / investisseurs'],
  },
];

const STEPS = [
  { num: '00', label: 'Capitaine',     desc: 'Coaching dirigeant.',   icon: Crown,
    when: 'Jour 1 — 5',
    longDesc: 'Avant de toucher au resto, on travaille VOUS. 12h de coaching individuel pour transformer un opérateur en dirigeant capable de faire grandir une marque. Et en parallèle, audit terrain 360° : cuisine, salle, équipe, P&L lu ligne par ligne. Vous sortez de cette étape avec un cap clair, un diagnostic sans pitié, et un plan d\'action priorisé.',
    deliverables: [
      '12h de coaching individuel (1h × 12 ou 2h × 6 — adaptable)',
      'Plan stratégique 90j personnalisé (Vision · Organisation · Leadership · Croissance)',
      'Journal de bord du dirigeant (rituels quotidiens, hebdo, mensuels)',
      'Audit terrain 360° (cuisine, salle, équipe)',
      'P&L lu ligne par ligne · diagnostic food cost + masse salariale',
      'Cartographie des fuites de marge + plan d\'action priorisé (quick wins ≤ 30j)',
      'WhatsApp prioritaire entre sessions (réponse < 4h)',
    ],
  },
  { num: '01', label: 'Construire',    desc: "ADN, positionnement.", icon: Building2,
    when: 'Jour 5 — 15',
    longDesc: 'On bâtit votre ADN de marque : qui vous êtes, à qui vous parlez, pourquoi on revient chez vous. Pas un slogan — une vraie identité qui guide chaque décision.',
    deliverables: ['Manifeste fondateur', 'Positionnement (cible, promesse, preuve)', 'Récit de marque', 'Charte éditoriale (ton, vocabulaire)', 'Valeurs non négociables'],
  },
  { num: '02', label: 'Expérience',    desc: "Ambiance, menu, parcours.",  icon: Star,
    when: 'Jour 10 — 25',
    longDesc: 'Votre client doit ressentir quelque chose dès qu\'il pousse la porte. On orchestre la lumière, la musique, le parcours, les détails — et surtout on crée votre menu gagnant qui se grave dans les têtes.',
    deliverables: ['On crée votre menu gagnant (signatures + best-sellers)', 'Parcours client minuté (arrivée → départ)', 'Plan lumière + ambiance sonore', 'Signalétique intérieure', 'Rituels de service signature', 'Détails sensoriels (odeurs, textures)'],
  },
  { num: '03', label: 'Offre',         desc: 'Carte signature.',     icon: UtensilsCrossed,
    when: 'Jour 15 — 30',
    longDesc: 'On refait votre carte plat par plat. Moins de plats, plus de marge, des signatures qu\'on ne trouve nulle part ailleurs. La carte qui se grave dans la tête de vos clients.',
    deliverables: ['Carte signature (15-25 plats max)', 'Pricing engineering (panier moyen +20%)', 'Plats stars (hero items) identifiés', 'Carte boissons rentabilisée', 'Menus saisonniers anticipés'],
  },
  { num: '04', label: 'Rentabilité',   desc: 'Marges, food cost.',   icon: Calculator,
    when: 'Jour 20 — 40',
    longDesc: 'On reprend le contrôle de votre marge. Fiches techniques au gramme près, négociation fournisseurs, inventaires précis, chiffres suivis chaque semaine. Le coût matière descend, vous respirez.',
    deliverables: ['Fiches techniques 100% carte (au gramme)', 'Audit + négo fournisseurs (5-15% éco)', 'Procédure inventaire structurée', 'Tableau de bord hebdomadaire', 'Refonte du menu chaque trimestre'],
  },
  { num: '05', label: 'Produit',       desc: 'Kitchen engineering.', icon: ChefHat,
    when: 'Jour 25 — 45',
    longDesc: 'On retravaille vos recettes pour qu\'elles soient rapides à exécuter, constantes, et impossibles à mal faire. Le client mange la même chose un lundi de novembre qu\'un samedi d\'été.',
    deliverables: ['Standardisation 100% de la carte', 'Mise en place optimisée (mise en place → service)', 'Test de constance (3 services consécutifs)', 'Doc photo de chaque plat (référence visuelle)', 'Process pic d\'activité (rush hours)'],
  },
  { num: '06', label: 'Process',       desc: 'Fiches techniques.',   icon: ShieldCheck,
    when: 'Jour 30 — 50',
    longDesc: 'Vous partez en weekend, votre resto tourne. C\'est ça l\'objectif. Process clairs, checklists imprimées, rôles définis — l\'opérationnel ne dépend plus de vous.',
    deliverables: ['Manuel opérationnel (50+ pages)', 'Checklists (ouverture, fermeture, ménage)', 'Procédures d\'urgence (panne, absence)', 'Intégration nouveau salarié (J0 → J7)', 'Rituels d\'encadrement hebdo'],
  },
  { num: '07', label: 'Équipe',        desc: 'Recrutement, culture.', icon: Users,
    when: 'Jour 35 — 55',
    longDesc: 'On structure votre équipe : fiches de poste, primes performance, culture d\'engagement. Tes meilleurs éléments restent, les nouveaux s\'intègrent vite.',
    deliverables: ['Fiches de poste (1 par rôle)', 'Grille de salaires + primes', 'Plan de recrutement (besoins 6 mois)', 'Intégration 7 jours (livret, parrain)', 'Évaluation trimestrielle structurée'],
  },
  { num: '08', label: 'Visibilité',    desc: 'Contenu, DA.',         icon: Palette,
    when: 'Jour 40 — 65',
    longDesc: 'On pense votre univers visuel, votre calendrier éditorial, vos scripts. 60 jours de contenus prêts — il vous reste juste à tourner. Vous devenez une marque qu\'on suit, pas un resto qu\'on oublie.',
    deliverables: ['Direction artistique de contenu', 'Calendrier éditorial 60 jours', 'Scripts détaillés (posts, reels, stories)', 'Briefs shooting prêts à exécuter', 'Architecture de séries récurrentes'],
  },
  { num: '09', label: 'Acquisition',   desc: 'Plateformes, perf.',   icon: TrendingUp,
    when: 'Jour 45 — 70',
    longDesc: 'On déploie votre stratégie d\'acquisition : Uber, Deliveroo, Glovo, ads ciblées, partenariats locaux. Votre resto se remplit même quand il pleut.',
    deliverables: ['Setup plateformes (carte, prix, photos pro)', 'Stratégie SEO local (Google Maps top 3)', 'Campagnes Meta Ads (ciblage hyperlocal)', 'Partenariats influenceurs locaux', 'Tracking conversions (Mongo + GA4)'],
  },
  { num: '10', label: 'Fidélisation',  desc: 'Programme, ré-engage.', icon: Heart,
    when: 'Jour 55 — 80',
    longDesc: 'On installe les rituels qui transforment un client one-shot en habitué qui revient 9× par an. Programme fidélité simple, ré-engage SMS, anniversaires, événements VIP.',
    deliverables: ['Programme fidélité (carte ou app)', 'Séquence SMS/Email post-visite', 'Événements VIP trimestriels', 'Recommandation client (parrainage)', 'Dashboard rétention (LTV par cohorte)'],
  },
  { num: '11', label: 'Développement', desc: 'Duplication.',         icon: MapPin,
    when: 'Jour 70 — 90',
    longDesc: 'Si votre modèle tourne, on le duplique. Recherche d\'emplacement, négociation bail, montage du dossier financier, ouverture clé en main du 2e (ou 3e).',
    deliverables: ['Recherche d\'emplacement (5 sites short-list)', 'Bilan financier 2e site (P&L 3 ans)', 'Négo bail commercial', 'Plan d\'ouverture J-90 → J0', 'Dossier bancaire / investisseurs'],
  },
];

const OFFERS = [
  {
    key: 'ebook',
    step: '1',
    stepLabel: 'ÉTAPE 1 · OBLIGATOIRE',
    short: 'Sabril Express',
    pill: 'ÉTAPE 1 · 90 MIN + GUIDE',
    tagline: 'Le moyen le plus élégant de tester ma méthode et d\'obtenir mon regard sur votre restaurant en 90 minutes.',
    priceFrom: '790€',
    priceSub: '100% déductible de l\'Étape 2',
    coverImage: '/ebook/cover.jpg',
    forWho: [
      'Vous hésitez avant un accompagnement complet',
      'Vous voulez un diagnostic direct avant d\'investir davantage',
      'Vous cherchez un plan d\'action clair sur 30 jours',
      'Vous voulez confronter votre vision à un regard extérieur',
    ],
    stats: [
      { value: '90',   label: 'Min avec moi' },
      { value: '2',    label: 'Visios · 45min' },
      { value: 'PDF',  label: 'Guide inclus' },
    ],
    bullets: [
      '🎥 Visio #1 · 45 min · État des lieux de votre restaurant',
      '🎯 Analyse concept, chiffres, positionnement · 3 priorités identifiées',
      '📅 Visio #2 · 45 min · Plan d\'action 30 jours personnalisé',
      '📚 Le Guide Ultime PDF offert · 75 pages · 16 chapitres',
      '⏰ Valable 90 jours après achat',
      '💰 100 % déductible du Salon Privé si vous continuez sous 90 jours',
    ],
    available: [
      { emoji: '🎥', label: 'Visio #1 · Diagnostic 45 min' },
      { emoji: '📅', label: 'Visio #2 · Plan d\'action 45 min' },
      { emoji: '📚', label: 'Le Guide Ultime PDF · 75 pages offert' },
      { emoji: '💰', label: 'Déductible du Salon Privé' },
    ],
    quote: '90 minutes avec moi et le guide complet — pour éprouver la méthode et obtenir mon regard sur votre restaurant avant d\'engager un accompagnement.',
    cardBg: 'linear-gradient(180deg, #FBF5EA 0%, #F7EEDF 50%, #F1E4CE 100%)',
    accent: '#D89440',
    badgeBg: 'linear-gradient(135deg, #D89440 0%, #B8792F 100%)',
    badgeShadow: 'rgba(216,148,64,0.55)',
    cardShadow: '0 22px 48px -14px rgba(11,43,36,0.18), 0 0 0 0.5px rgba(216,148,64,0.28), inset 0 1px 0 rgba(255,255,255,0.60)',
    ctaLabel: 'Réserver ma consultation',
    ctaAction: 'ebook-checkout',
  },
  {
    key: 'configurer',
    step: '2',
    stepLabel: 'ÉTAPE 2 · ACCOMPAGNEMENT',
    short: 'Le Salon Privé',
    pill: 'ÉTAPE 2 · 100% SUR-MESURE',
    tagline: 'Chaque restaurant est unique. Votre accompagnement aussi.',
    priceFrom: 'à partir de 2 790€',
    priceSub: 'Après l\'Étape 1 — 790€ déjà déduits',
    coverImage: '/configurator/cover.jpg?v=2',
    forWho: [
      'Ouverture',
      'Restaurant existant',
      'Coffee Shop',
      'Beach Club',
      'Hôtel & Concept Food',
    ],
    stats: [
      { value: '15+',  label: 'Chantiers' },
      { value: '3',    label: 'Formats' },
      { value: '24h',  label: 'Devis' },
    ],
    bullets: [
      'Choisir votre format d\'accompagnement',
      'Sélectionner les chantiers stratégiques',
      'Postuler à la liste d\'attente en 2 minutes',
    ],
    available: [
      { emoji: '🌍', label: 'À distance' },
      { emoji: '🏢', label: 'Sur site' },
      { emoji: '🧩', label: '100% personnalisé' },
    ],
    quote: 'Construisez votre accompagnement en choisissant votre format et vos chantiers — puis postulez à la liste d\'attente.',
    cardBg: 'linear-gradient(180deg, #FBF5EA 0%, #F7EEDF 50%, #F1E4CE 100%)',
    accent: '#D89440',
    badgeBg: 'linear-gradient(135deg, #D89440 0%, #B8792F 100%)',
    badgeShadow: 'rgba(216,148,64,0.55)',
    cardShadow: '0 22px 48px -14px rgba(11,43,36,0.18), 0 0 0 0.5px rgba(216,148,64,0.28), inset 0 1px 0 rgba(255,255,255,0.60)',
    ctaLabel: 'Découvrir le Salon Privé',
    ctaAction: 'configurer-open',
    featured: true,
  },
];

const KPIS = [
  {
    key: 'marges',
    icon: TrendingUp,
    color: '#34C759',
    title: 'Plus de marge à la fin du mois',
    sub: 'On reprend le contrôle de vos coûts (achats, food cost, fournisseurs).',
    proof: '+8 000€/mois en moyenne',
    badge: '+8K€',
  },
  {
    key: 'equipe',
    icon: Users,
    color: '#FF9500',
    title: 'Une équipe qui ne part plus',
    sub: 'Fiches de poste, process clairs, recrutement structuré, primes.',
    proof: 'Turnover divisé par 3',
    badge: '÷3',
  },
  {
    key: 'clients',
    icon: Heart,
    color: '#FF2D55',
    title: 'Des clients qui reviennent',
    sub: 'Programme fidélité, ré-engagement automatisé, expérience travaillée.',
    proof: 'De 4 à 9 visites/an/client',
    badge: '×2',
  },
  {
    key: 'panier',
    icon: ChefHat,
    color: '#007AFF',
    title: 'Un panier moyen plus gros',
    sub: 'Menu engineering, carte signature, vente additionnelle, ambiance.',
    proof: 'Ticket 18€ → 26€',
    badge: '+44%',
  },
  {
    key: 'digital',
    icon: Globe,
    color: '#AF52DE',
    title: 'Uber Eats / Site / Livraison qui rapportent',
    sub: 'Photos pro, pricing off-premise, site sans commission, click & collect.',
    proof: '30 à 40% du CA',
    badge: '40%',
  },
  {
    key: 'coaching',
    icon: Crown,
    color: '#C9A87A',
    title: '12h de coaching dirigeant',
    sub: 'Le capitaine du navire. Vision, organisation, leadership, croissance — on vous forme à piloter votre marque qui performe.',
    proof: '12h individuel inclus',
    badge: '12h',
  },
];

const TESTIMONIALS = [
  { name: 'Maison Avocado', loc: 'Lille · Marrakech', quote: 'L\'invitation au voyage.', metric: '+38% CA' },
  { name: 'BON',            loc: 'Marrakech',         quote: 'In Msemen we trust.',      metric: 'Top 3 Uber' },
  { name: 'Afri\'K\'n Chic', loc: 'Marrakech',        quote: 'L\'Afrique en scène.',     metric: '+220% Insta' },
  { name: 'Zebra Gang.',    loc: 'Naples × Marrakech', quote: 'De l\'idée à la marque.', metric: '2 ouvertures' },
];

const OBJECTIVES = [
  {
    key: 'open',
    num: '01',
    title: 'Restaurants en activité',
    icon: Building2,
    color: '#FF9500',
    threshold: '50 k€ de CA mensuel minimum',
    thresholdSub: 'En dessous, on n\'a pas assez de matière pour bouger l\'aiguille — je préfère être honnête.',
    bullets: ['Restructurer la rentabilité', 'Renforcer le positionnement', 'Optimiser marges & food cost', 'Booster la visibilité', 'Préparer le multi-sites'],
  },
  {
    key: 'project',
    num: '02',
    title: "Projets d'ouverture",
    icon: Sparkles,
    color: '#AF52DE',
    threshold: '250 k€ de budget total minimum',
    thresholdSub: 'Travaux + équipement + fonds de roulement + DA. En dessous, on bricole — pas mon métier.',
    bullets: ["Construire l'ADN avant ouverture", 'Designer une expérience signature', 'Structurer un modèle scalable', "Préparer l'opening", 'Bâtir une marque, pas un resto'],
  },
];

// ───── Détails programme · 12 chantiers chronologiques (Jour 0 → 90) ─────
const BRAND_ASSETS = [
  {
    key: 'coaching',
    num: '00',
    title: 'Coaching dirigeant · 12h',
    overline: 'Chantier 00 · Capitaine du navire',
    tagline: 'Avant de travailler le resto, on travaille le dirigeant. 12h pour passer d\'opérateur à patron.',
    when: 'Jour 0 — 7',
    palette: 'gold',
    image: 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/l1bok3c5_IMG_0253.jpg',
    imageCaption: 'Studio Formatek · Way to success — posture de dirigeant, prise de parole assumée.',
    deliverables: [
      'On vous coache pendant 12h en individuel.',
      'On construit votre plan stratégique sur 90 jours.',
      'On met en place votre journal de bord dirigeant.',
      'On audite votre resto de A à Z, chiffres inclus.',
      'On identifie où l\'argent fuit chaque mois.',
      'On vous répond en direct sur WhatsApp sous 4h.',
    ],
    transformation: { from: 'Opérateur', to: 'Dirigeant', label: 'Posture' },
    gain: { amount: '×4', period: 'décisions/jour', basis: 'Cap clair, décisions tranchées, équipe alignée' },
    whyStats: [
      { v: '12h',  l: 'de coaching individuel incluses sur 90 jours.' },
      { v: '90j',  l: 'd\'accompagnement et suivi quotidien WhatsApp.' },
      { v: '×3',   l: 'gain de productivité dirigeant en 30j.' },
      { v: '<4h',  l: 'délai de réponse WhatsApp prioritaire.' },
    ],
    objective: { metric: 'Dirigeant aligné', label: 'cap clair · décisions tranchées · équipe portée' },
  },
  {
    key: 'adn',
    num: '01',
    title: 'ADN & histoire de marque',
    overline: 'Chantier 01 · Fondations',
    tagline: 'On construit une marque qu\'on n\'oublie pas.',
    when: 'Jour 8 — 14',
    palette: 'graphite',
    image: 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/uijsvi98_IMG_2048.jpeg',
    imageCaption: 'Maison Avocado — « L\'avocation de vous faire voyager »',
    deliverables: [
      'On définit les clients qui dépenseront le plus chez vous.',
      'On crée un concept impossible à confondre avec un concurrent.',
      'On écrit votre histoire et votre slogan.',
      'On trouve ce qui vous permettra de vendre plus cher.',
      'On construit votre identité pour les 10 prochaines années.',
    ],
    transformation: { from: 'Resto', to: 'Marque', label: 'Statut perçu' },
    gain: { amount: '×3', period: 'valeur perçue', basis: 'Marque reconnaissable = pricing power' },
    whyStats: [
      { v: '3s',   l: 'pour qu\'un client se forge un avis sur l\'identité.' },
      { v: '×4',   l: 'de mémorisation avec un récit clair.' },
      { v: '+25%', l: 'de panier moyen avec une marque forte.' },
      { v: '70%',  l: 'des décisions resto post-feed Insta.' },
    ],
    objective: { metric: 'Marque iconique', label: 'reconnaissable en 3 secondes' },
  },
  {
    key: 'experience',
    num: '02',
    title: 'Expérience au sein du lieu',
    overline: 'Chantier 02 · Parcours client',
    tagline: '8 secondes pour que le client ressente « je veux revenir ».',
    when: 'Jour 14 — 20',
    palette: 'coral',
    image: 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/k7oxk5vs_IMG_4621.jpg',
    imageCaption: 'Maison Avocado · Produit frais, geste signature — « l\'expérience commence avant la première bouchée »',
    deliverables: [
      'On imagine le parcours idéal du client, minute par minute.',
      'On crée vos rituels de service signature.',
      'On règle l\'ambiance sonore selon les moments.',
      'On travaille l\'éclairage et les odeurs.',
      'On écrit les 10 règles d\'or de votre équipe en salle.',
    ],
    transformation: { from: 'Service', to: 'Rituel', label: 'Niveau d\'attention' },
    gain: { amount: '+18%', period: 'fréquence retour', basis: 'Client ressent un soin = il revient' },
    whyStats: [
      { v: '8s',    l: 'pour que le client se forge son avis émotionnel.' },
      { v: '+35%',  l: 'de panier moyen avec atmosphère travaillée.' },
      { v: '5×',    l: 'plus de bouche-à-oreille post-expérience marquante.' },
      { v: '70%',   l: 'des clients reviennent pour l\'expérience, pas le plat.' },
    ],
    objective: { metric: 'Expérience signature', label: 'ressentie dès les 8 premières secondes' },
  },
  {
    key: 'design',
    num: '03',
    title: 'Design d\'espace',
    overline: 'Chantier 03 · Identité visuelle',
    tagline: 'Un lieu reconnaissable en 3 secondes.',
    when: 'Jour 14 — 20',
    palette: 'slate',
    image: 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/yqnay1zh_ChatGPT%20Image%2023%20mai%202026%20a%CC%80%2011_38_08.png',
    imageCaption: 'BON · Marrakech — « In Msemen we trust »',
    deliverables: [
      'On crée votre moodboard et on valide la direction en 7 jours.',
      'On dessine une signature visuelle iconique pour votre lieu.',
      'On livre le plan 3D et on organise les flux de service.',
      'On conçoit toute la signalétique interne.',
      'On règle l\'ambiance sensorielle du lieu.',
    ],
    transformation: { from: '18€', to: '26€', label: 'Ticket moyen' },
    gain: { amount: '+14 400€', period: '/mois', basis: '60 couverts/j · +8€/ticket' },
    whyStats: [
      { v: '3 sec', l: 'pour qu\'un client se forge un avis.' },
      { v: '70%',   l: 'des décisions resto post-photo Insta.' },
      { v: '×4',    l: 'de retour si lieu photogénique.' },
      { v: '+35%',  l: 'de panier moyen avec atmosphère travaillée.' },
    ],
    objective: { metric: '+30 à +45%', label: 'panier moyen' },
  },
  {
    key: 'menu',
    num: '04',
    title: 'Menu Gagnant',
    overline: 'Chantier 04 · Carte signature',
    tagline: 'Le menu qui se grave dans les têtes — et qui vous rend rentable.',
    when: 'Jour 21 — 30',
    palette: 'gold',
    image: 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/x4hljur2_IMG_2199.jpg',
    imageCaption: 'Motek $5 Hour · NYC — happy-hour orchestrée comme une carte signature (food + drinks + menu visible)',
    imageLink: 'https://www.instagram.com/motekcafe/',
    deliverables: [
      'On refond votre carte plat par plat.',
      'On sélectionne vos 6 à 8 signatures rentables.',
      'On calcule les marges de chaque plat.',
      'On donne un nom iconique à vos plats phares.',
      'On organise le shooting photo pro de la carte.',
    ],
    transformation: { from: '22 plats', to: '14 plats', label: 'Carte focalisée' },
    gain: { amount: '+22%', period: 'panier moyen', basis: 'Engineering + plats signatures' },
    whyStats: [
      { v: '20%',  l: 'des plats font 80% du CA — on garde les bons.' },
      { v: '+22%', l: 'de panier moyen avec un menu engineering.' },
      { v: '×3',   l: 'mémorisation pour un plat-signature nommé.' },
      { v: '−30%', l: 'food cost avec une carte plus courte.' },
    ],
    objective: { metric: 'Menu signature', label: '12-16 plats · 80% du CA' },
  },
  {
    key: 'foodcost',
    num: '05',
    title: 'Fiches techniques · Food cost & marges',
    overline: 'Chantier 05 · Performance financière',
    tagline: 'Reprendre le contrôle de votre marge.',
    when: 'Jour 21 — 30',
    palette: 'emerald',
    image: 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/iqjmu3mc_F1E8C297-5DDA-4A72-B216-58F35CDE9285.PNG',
    imageCaption: 'Zebra Ristorante · Ravioli Ricotta Épinards — fiche technique signée Sabril (food cost 27,9 % · marge 108,20 MAD)',
    deliverables: [
      'On rédige les fiches techniques de toute votre carte.',
      'On audite vos fournisseurs pour économiser 5 à 15 %.',
      'On met en place vos inventaires et la rotation FIFO.',
      'On construit votre tableau de bord hebdo simple à lire.',
      'On planifie un re-engineering tous les 3 mois.',
    ],
    transformation: { from: '38%', to: '27%', label: 'Coût matière' },
    gain: { amount: '+8 250€', period: '/mois', basis: '75K€ CA · 11 pts économisés' },
    whyStats: [
      { v: '60%',   l: 'des restos n\'ont pas de fiches techniques.' },
      { v: '5-6K€', l: '/an économisés par point de food cost gagné.' },
      { v: '+15%',  l: 'de marge possible en 90j sans toucher le menu.' },
      { v: '72%',   l: 'marge brute atteignable (vs 60% secteur).' },
    ],
    objective: { metric: '≤ 28%', label: 'coût matière (vs 35-40% secteur)' },
  },
  {
    key: 'rh',
    num: '06',
    title: 'Fiche de poste & performance',
    overline: 'Chantier 06 · Équipe & process',
    tagline: 'Une équipe alignée, des résultats durables.',
    when: 'Jour 31 — 45',
    palette: 'indigo',
    image: 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/gkrf1b55_IMG_2044.jpeg',
    imageCaption: 'Zebra · Fiche de poste Responsable Glovo — process non négociables',
    deliverables: [
      'On rédige les fiches de poste de chaque rôle.',
      'On écrit vos procédures opérationnelles.',
      'On fixe des objectifs chiffrés et un système de primes.',
      'On structure l\'intégration des nouveaux sur 7 jours.',
      'On met en place les rituels de management hebdo.',
    ],
    transformation: { from: '45%', to: '14%', label: 'Turnover annuel' },
    gain: { amount: '+18 000€', period: '/an', basis: 'Équipe 10 pers · -6 recrutements × 3K€' },
    whyStats: [
      { v: '3-5K€',  l: 'coût moyen d\'un recrutement raté.' },
      { v: '−65%',   l: 'de turnover avec process clairs.' },
      { v: '−40%',   l: 'de conflits managériaux.' },
      { v: '+1 ETP', l: 'de productivité équivalente sur 6 mois.' },
    ],
    objective: { metric: '< 15%', label: 'turnover · masse salariale 25-30%' },
  },
  {
    key: 'livraison',
    num: '07',
    title: 'Livraison & plateformes',
    overline: 'Chantier 07 · Off-premise',
    tagline: 'Uber Eats, Deliveroo, Glovo — 40% du CA possible.',
    when: 'Jour 45 — 60',
    palette: 'cyan',
    image: 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/0wbbc2a5_IMG_0168.jpeg',
    imageCaption: 'Trizza · format gagnant — « Picnic season is here »',
    deliverables: [
      'On optimise votre carte pour les plateformes.',
      'On organise le shooting photo pro pour multiplier le CTR par 2,4.',
      'On travaille votre ranking pour viser le Top 3 local.',
      'On pilote vos commissions pour tenir une marge nette ≥ 18 %.',
      'On met en place vos process de rush hour.',
    ],
    transformation: { from: '12 cmd/j', to: '32 cmd/j', label: 'Volume off-premise' },
    gain: { amount: '+18 000€', period: '/mois', basis: '20 cmd × 30€ × 30j' },
    whyStats: [
      { v: '40%',     l: 'du CA d\'un resto urbain vient du off-premise.' },
      { v: '×2,4',    l: 'CTR avec photos pro vs amateur.' },
      { v: '30%',     l: 'commission moyenne (à négocier).' },
      { v: '45 min',  l: 'temps prep max pour rester compétitif.' },
    ],
    objective: { metric: '30 à 40%', label: 'du CA off-premise · marge ≥18%' },
  },
  {
    key: 'packaging',
    num: '08',
    title: 'Création du packaging',
    overline: 'Chantier 08 · Brand extension',
    tagline: 'Le détail qui transforme un client en ambassadeur.',
    when: 'Jour 45 — 60',
    palette: 'violet',
    image: 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/k6c2zyjh_B033521E-DE79-4E16-9F43-5E82C63EC567.png',
    imageCaption: 'Zebra Restaurant — sachet kraft signature, élégance italienne',
    deliverables: [
      'On crée votre système de packaging complet.',
      'On négocie le sourcing auprès de 3 fournisseurs.',
      'On garantit la conformité livraison éco-responsable.',
      'On organise 50 visuels pro pour vos supports.',
      'On assure la cohérence de votre marque sur tous les canaux.',
    ],
    transformation: { from: '4×', to: '9×', label: 'Visites/an/client' },
    gain: { amount: '+22 500€', period: '/an', basis: '100 clients · +5 visites × 45€' },
    whyStats: [
      { v: '87%',   l: 'partagent un détail packaging soigné.' },
      { v: '1 200', l: 'vues moyennes/unboxing Insta organique.' },
      { v: '+18%',  l: 'conversion livraison avec packaging signature.' },
      { v: '0,50€', l: 'coût pour +3€ de valeur perçue/ticket.' },
    ],
    objective: { metric: '8 à 10×', label: 'visites/an par client' },
  },
  {
    key: 'merch',
    num: '09',
    title: 'Merch & extension',
    overline: 'Chantier 09 · Revenu récurrent',
    tagline: "Pas un goodie — un vecteur d'appartenance.",
    when: 'Jour 45 — 60',
    palette: 'gold',
    image: 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/az2r698t_IMG_2047.jpeg',
    imageCaption: 'Zebra Gang · Napoli Collection — « Pure Italian Gang »',
    deliverables: [
      'On dessine une capsule de 5 à 8 pièces premium.',
      'On sélectionne le coton bio et on gère la broderie.',
      'On calcule un pricing à 65-70 % de marge brute.',
      'On monte votre e-shop Shopify intégré.',
      'On orchestre le lancement du drop.',
    ],
    transformation: { from: '0€', to: '8 000€', label: 'Revenu merch/mois' },
    gain: { amount: '+96 000€', period: '/an', basis: '10% du CA d\'un resto à 80K€/mois' },
    whyStats: [
      { v: '65-75%', l: 'marge brute merch (vs 60-65% food).' },
      { v: '×4',     l: 'plus de visites/an quand client porte le merch.' },
      { v: '7-14j',  l: 'pour vendre 100% d\'une capsule limitée.' },
      { v: '12%',    l: 'du CA chez les marques resto premium.' },
    ],
    objective: { metric: '8 à 12%', label: 'du CA mensuel · revenu récurrent' },
  },
  {
    key: 'da',
    num: '10',
    title: 'Univers visuel & Calendrier éditorial',
    overline: 'Chantier 10 · Contenu & réseaux sociaux',
    tagline: 'On imagine, on scénarise, vous produisez — univers visuel et scripts prêts pour 2 mois.',
    when: 'Jour 60 — 90',
    palette: 'gold',
    image: 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/e7gb8x81_IMG_2198.jpg',
    imageCaption: 'Référence univers · @motekcafe — feed cohérent, palette chaude, food-porn maîtrisé (tap pour voir)',
    imageLink: 'https://www.instagram.com/motekcafe/',
    daReferences: [
      { handle: '@motekcafe',       city: 'New York',   note: 'Palette chaude, food-porn maîtrisé',     url: 'https://www.instagram.com/motekcafe/' },
      { handle: '@sketchlondon',    city: 'Londres',    note: 'Rose iconique, audace surréaliste',      url: 'https://www.instagram.com/sketchlondon/' },
      { handle: '@bigmammagroup',   city: 'Paris',      note: 'Énergie italienne, couleurs saturées',   url: 'https://www.instagram.com/bigmammagroup/' },
      { handle: '@bagatelle',       city: 'Global',     note: 'Lifestyle premium, brunch culture',      url: 'https://www.instagram.com/bagatelle/' },
    ],
    deliverables: [
      'On construit votre univers visuel — moodboard et codes graphiques.',
      'On planifie votre calendrier éditorial sur 60 jours.',
      'On écrit les scripts détaillés de chaque contenu.',
      'On prépare les briefs shooting prêts à tourner.',
      'On dessine l\'architecture de vos séries signatures.',
    ],
    transformation: { from: 'Posts random', to: 'Plan 60j', label: 'Stratégie contenu' },
    gain: { amount: '×3', period: 'engagement', basis: 'Audience qualifiée · contenus à intention claire' },
    whyStats: [
      { v: '60j',   l: 'de contenus pensés et scriptés à l\'avance.' },
      { v: '×3',    l: 'd\'engagement avec un calendrier cohérent.' },
      { v: '0',     l: 'production assurée — on conçoit, vous tournez.' },
      { v: '8h',    l: 'd\'ateliers DA pour aligner votre équipe créa.' },
    ],
    objective: { metric: '2 mois', label: 'de contenus prêts · concept + script' },
  },
  {
    key: 'opening',
    num: '11',
    title: 'Opening · Présence sur place',
    overline: 'Chantier 11 · Lancement',
    tagline: 'On lance ensemble — sur le terrain, pas par téléphone.',
    when: 'Jour 60 — 90',
    palette: 'coral',
    image: 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/qhyi774h_7B859B9F-E2CC-4505-A31C-F3C186D89609.JPEG',
    imageCaption: 'Maison Avocado · Lille — Day one, devant la vitrine. « Vivant. »',
    deliverables: [
      'On orchestre votre soft opening 7 jours avant l\'ouverture.',
      'On briefe votre équipe à J-3 en présentiel.',
      'On est sur place avec vous pendant 3 à 5 jours.',
      'On calibre les flux cuisine et salle en live.',
      'On construit votre plan média local.',
      'On revient à J+30 pour ajuster.',
    ],
    transformation: { from: 'Stress J-1', to: 'Service rodé', label: 'Lancement maîtrisé' },
    gain: { amount: '×2', period: 'remplissage J+30', basis: 'Soft opening + media bien orchestrés' },
    whyStats: [
      { v: '7j',   l: 'avant l\'ouverture officielle pour stress-tester.' },
      { v: '×2',   l: 'remplissage à J+30 vs un opening sans préparation.' },
      { v: '4j',   l: 'minimum sur place pendant le live pour calibrer.' },
      { v: '90%',  l: 'des restos ratent leur opening faute de répétition.' },
    ],
    objective: { metric: 'Opening serein', label: 'service rodé dès le J0 officiel' },
  },
];

const ADDONS = [
  {
    icon: Globe,
    accent: '#007AFF',
    accentDeep: '#0040A0',
    tagline: 'Tech maison',
    name: 'Site Internet · Réservation · Click & Collect',
    desc: 'Sur-mesure, prises de réservation 0% commission, click & collect, paiement intégré, SEO local.',
    bullets: [
      'Design sur-mesure aligné sur votre marque',
      'Réservation directe · 0% de commission',
      'Click & collect + paiement intégré (Stripe)',
      'SEO local — Google Maps · pages quartier',
      'Hébergement + maintenance + analytics inclus',
    ],
    badge: 'Tech',
  },
  {
    icon: Heart,
    accent: '#FF2D55',
    accentDeep: '#A0193C',
    tagline: 'CRM client',
    name: 'Programme Fidélisation Intelligent',
    desc: 'Système pensé pour 10 visites/client. Récompenses, segmentation, ré-engagement auto.',
    bullets: [
      'Objectif net : 10 visites par client / an',
      'Récompenses progressives (palier + surprise)',
      'Segmentation auto (nouveau · habitué · VIP)',
      'Ré-engagement auto SMS + WhatsApp si silence',
      'Dashboard live · LTV · taux de rétention',
    ],
    badge: 'CRM',
  },
];

const MEDIA_REFS = ['BeIN Sports', 'BFM Business', 'Le Figaro', 'Canal+', 'Maroc Hebdo', 'Hôtellerie Restauration'];

// ─────────────────────────────────────────────────────────────────────
// iOS Status Bar — 9:41, Signal, Wifi, Battery
// ─────────────────────────────────────────────────────────────────────
function StatusBar() {
  const [time, setTime] = useState(formatClock());
  useEffect(() => {
    const id = setInterval(() => setTime(formatClock()), 30000);
    return () => clearInterval(id);
  }, []);
  return (
    <div
      className="flex items-center justify-between px-6 relative"
      style={{ height: 44, fontFamily: theme.fontDisplay, color: theme.inkDeep, fontSize: 15, fontWeight: 600 }}
      data-testid="ios-status-bar"
    >
      <span style={{ letterSpacing: '-0.01em' }}>{time}</span>
      <div className="flex items-center gap-1.5">
        <Signal className="w-3.5 h-3.5" strokeWidth={2.5} />
        <Wifi className="w-3.5 h-3.5" strokeWidth={2.5} />
        <BatteryFull className="w-5 h-5" strokeWidth={2} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DynamicIsland — live notification pill rotating every 4-5s (real Mongo data)
// ─────────────────────────────────────────────────────────────────────
const DI_STATIC = [
  { icon: '🟢', txt: 'Sabril en ligne · disponible', tint: '#34C759' },
  { icon: '💬', txt: 'Sabril répond en moy. en 4 min', tint: '#5AC8FA' },
  { icon: '📍', txt: 'Sabril à Paris du 15 au 30 août', tint: '#AF52DE' },
];

function DynamicIsland() {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [i, setI] = useState(0);
  const [phase, setPhase] = useState('in');
  const [stats, setStats] = useState({ bookings_total: 0, online_now: 21, recent_bookings: [] });
  const [burst, setBurst] = useState(null); // { txt, tint } when a new booking arrives
  const prevTotalRef = useRef(null);

  // Fetch live stats — on mount + every 30s
  useEffect(() => {
    let mounted = true;
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API_URL}/api/sabril/live-stats`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (!mounted) return;
        // Detect a NEW booking → trigger burst
        const prev = prevTotalRef.current;
        if (prev !== null && data.bookings_total > prev) {
          const latest = (data.recent_bookings || [])[0];
          if (latest && latest.first_name && latest.city) {
            setBurst({
              txt: `${latest.first_name.split(' ')[0]} · ${latest.city} vient de réserver`,
              tint: '#FF3B30',
            });
            // Auto-clear after 5s
            setTimeout(() => setBurst(null), 5200);
          }
        }
        prevTotalRef.current = data.bookings_total;
        setStats(data);
      } catch (e) { /* silent — keep last-good */ }
    };
    fetchStats();
    const poll = setInterval(fetchStats, 10000); // faster polling for burst latency ≤10s
    return () => { mounted = false; clearInterval(poll); };
  }, [API_URL]);

  // Build dynamic events from stats + static
  const events = useMemo(() => {
    const dyn = [];
    if (stats.online_now > 0) {
      dyn.push({ icon: '👀', txt: `${stats.online_now} en ligne avec Sabril`, tint: '#34C759' });
    }
    if (stats.bookings_total > 0) {
      dyn.push({ icon: '✅', txt: `${stats.bookings_total} ${stats.bookings_total === 1 ? 'café calé' : 'cafés calés'} cet été`, tint: '#1FA644' });
    }
    (stats.recent_bookings || []).slice(0, 2).forEach((b) => {
      if (b.first_name && b.city) {
        dyn.push({ icon: '☕', txt: `${b.first_name.split(' ')[0]} · ${b.city} vient de réserver`, tint: '#FF9500' });
      }
    });
    return [...DI_STATIC, ...dyn];
  }, [stats]);

  // Rotation (paused during burst)
  useEffect(() => {
    if (burst) return undefined;
    const tick = setInterval(() => {
      setPhase('out');
      setTimeout(() => {
        setI((prev) => (prev + 1) % Math.max(1, events.length));
        setPhase('in');
      }, 280);
    }, 4200);
    return () => clearInterval(tick);
  }, [events.length, burst]);

  const ev = burst || events[i % Math.max(1, events.length)] || DI_STATIC[0];
  const isIn = burst ? true : phase === 'in';
  const isBurst = !!burst;

  return (
    <div
      className="absolute left-1/2 -translate-x-1/2 pointer-events-none"
      style={{ top: 6, zIndex: 50 }}
      data-testid="dynamic-island"
      aria-live="polite"
    >
      <div
        className="flex items-center gap-2 pointer-events-auto"
        style={{
          background: isBurst ? `linear-gradient(135deg, ${ev.tint} 0%, #1C1C1E 100%)` : '#000000',
          color: '#FFFFFF',
          borderRadius: 999,
          padding: isBurst ? '8px 16px 8px 10px' : '6px 12px 6px 8px',
          minHeight: isBurst ? 36 : 30,
          minWidth: isBurst ? 200 : 130,
          maxWidth: isBurst ? 320 : 240,
          boxShadow: isBurst
            ? `0 8px 24px ${ev.tint}66, 0 0 0 0.5px rgba(255,255,255,0.10) inset, 0 0 18px ${ev.tint}40`
            : '0 4px 14px rgba(0,0,0,0.30), 0 0 0 0.5px rgba(255,255,255,0.06) inset',
          transition: 'all 360ms cubic-bezier(0.22, 1, 0.36, 1)',
          transform: isIn ? (isBurst ? 'scale(1.06)' : 'scale(1)') : 'scale(0.92)',
          opacity: isIn ? 1 : 0,
          willChange: 'transform, opacity',
        }}
      >
        {/* Burst label */}
        {isBurst && (
          <span
            style={{
              fontFamily: theme.fontMono,
              fontSize: 9, fontWeight: 800,
              letterSpacing: '0.14em', textTransform: 'uppercase',
              color: '#FFFFFF',
              background: 'rgba(255,255,255,0.18)',
              borderRadius: 999,
              padding: '2px 6px',
              border: '0.5px solid rgba(255,255,255,0.30)',
              flexShrink: 0,
            }}
          >
            🔥 LEAD
          </span>
        )}
        {/* Animated dot (pulse) */}
        {!isBurst && (
          <span
            className="flex items-center justify-center"
            style={{
              width: 16, height: 16, borderRadius: 999,
              background: `${ev.tint}30`,
              border: `0.5px solid ${ev.tint}60`,
            }}
          >
            <span
              style={{
                width: 6, height: 6, borderRadius: 999,
                background: ev.tint,
                boxShadow: `0 0 8px ${ev.tint}`,
                animation: 'diDotPulse 1.2s ease-in-out infinite',
              }}
            />
          </span>
        )}
        <span
          className="truncate"
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: isBurst ? 12 : 10.5,
            fontWeight: isBurst ? 800 : 600,
            letterSpacing: '-0.005em',
            lineHeight: 1.2,
            color: '#FFFFFF',
          }}
        >
          {ev.txt}
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// iOS Nav Bar — Back + Title + Action
// ─────────────────────────────────────────────────────────────────────
function NavBar() {
  return (
    <div
      className="sticky top-0 z-40 backdrop-blur-xl"
      style={{
        background: 'rgba(255,255,255,0.78)',
        borderBottom: `0.5px solid ${theme.hairline}`,
      }}
      data-testid="ios-nav-bar"
    >
      <div className="flex items-center justify-between px-4" style={{ height: 44 }}>
        <Link
          to="/"
          className="flex items-center"
          style={{ color: '#007AFF', fontFamily: theme.fontDisplay, fontSize: 17, fontWeight: 400 }}
          data-testid="nav-back"
        >
          <ChevronRight className="w-5 h-5 rotate-180 -ml-1" strokeWidth={2.5} />
          <span>Accueil</span>
        </Link>
        <span style={{ fontFamily: theme.fontDisplay, fontSize: 15, fontWeight: 600, color: theme.inkDeep, letterSpacing: '-0.01em' }}>
          Salon Privé
        </span>
        <button
          type="button"
          onClick={openSabril}
          className="active:opacity-60 transition-opacity"
          style={{ color: '#007AFF' }}
          data-testid="nav-talk-sabril"
        >
          <MessageCircle className="w-[22px] h-[22px]" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────
const SABRIL_AVATAR = 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/xf03ot6r_sabril.png';

// ─────────────────────────────────────────────────────────────────────
// SabrilNote — iOS Messages-style bubble · Sabril speaks personally
// + Typing dots animation when entering viewport
// + Read receipt (Lu ✓✓ HH:MM) once scrolled past
// + Tap to react (❤️👏🔥🙏😂)
// ─────────────────────────────────────────────────────────────────────
const REACTIONS = ['❤️', '👏', '🔥', '🙏', '😂'];

function SabrilNote({ children, tone = 'default' }) {
  const palette = {
    default: { bg: '#F2F2F7', ink: theme.inkDeep, dot: '#8E8E93' },
    warm:    { bg: '#FFE9DB', ink: '#5C2A0E', dot: '#C46A28' },
    dark:    { bg: theme.inkDeep, ink: '#FFFFFF', dot: 'rgba(255,255,255,0.65)' },
  }[tone] || { bg: '#F2F2F7', ink: theme.inkDeep, dot: '#8E8E93' };

  const [phase, setPhase] = useState('hidden');
  const [readAt, setReadAt] = useState(null);
  const [reaction, setReaction] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && phase === 'hidden') {
            setPhase('typing');
            const len = typeof children === 'string' ? children.length : 60;
            const dur = Math.min(1500, Math.max(700, len * 14));
            setTimeout(() => setPhase('revealed'), dur);
          }
        });
      },
      { threshold: 0.45 }
    );
    io.observe(el);

    const onScroll = () => {
      if (!el || readAt) return;
      const rect = el.getBoundingClientRect();
      if (rect.bottom < window.innerHeight * 0.55) {
        const now = new Date();
        setReadAt(`${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`);
        window.removeEventListener('scroll', onScroll);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      io.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, [phase, children, readAt]);

  return (
    <div ref={ref} className="px-5 mb-5 mt-2 flex flex-col gap-1" data-testid="sabril-note">
      <div className="flex items-end gap-2">
        <span
          className="flex-shrink-0 relative"
          style={{
            width: 34, height: 34, borderRadius: 999,
            overflow: 'hidden',
            boxShadow: '0 2px 6px rgba(0,0,0,0.10), 0 0 0 2px #FFFFFF, 0 0 0 3px rgba(240,90,26,0.55)',
            background: '#FFE9DB',
          }}
        >
          <img
            src={SABRIL_AVATAR}
            alt="Sabril"
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: '50% 22%' }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <span
            className="absolute"
            style={{ bottom: 1, right: 1, width: 9, height: 9, borderRadius: 999, background: '#30D158', border: '1.5px solid #FFFFFF' }}
            aria-hidden
          />
        </span>

        <div
          className="relative transition-all duration-300 ease-out"
          style={{
            background: palette.bg,
            color: palette.ink,
            padding: phase === 'typing' ? '10px 14px 10px' : '8px 12px 9px',
            borderRadius: 18,
            borderBottomLeftRadius: 4,
            maxWidth: 'calc(100% - 50px)',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            opacity: phase === 'hidden' ? 0 : 1,
            transform: phase === 'hidden' ? 'translateY(6px) scale(0.96)' : 'translateY(0) scale(1)',
            cursor: phase === 'revealed' ? 'pointer' : 'default',
          }}
          onClick={() => { if (phase === 'revealed') setPickerOpen((v) => !v); }}
          role={phase === 'revealed' ? 'button' : undefined}
        >
          {phase === 'typing' ? (
            <span className="inline-flex items-center gap-[3px]" style={{ height: 16, paddingTop: 1 }} aria-label="Sabril est en train d'écrire">
              <span className="sabril-typing-dot" style={{ background: palette.dot, animationDelay: '0ms' }} />
              <span className="sabril-typing-dot" style={{ background: palette.dot, animationDelay: '160ms' }} />
              <span className="sabril-typing-dot" style={{ background: palette.dot, animationDelay: '320ms' }} />
            </span>
          ) : (
            <p
              className="transition-opacity duration-200 ease-out"
              style={{
                fontFamily: theme.fontDisplay, fontSize: 12.5, fontWeight: 500,
                letterSpacing: '-0.005em', lineHeight: 1.4,
                color: palette.ink, opacity: phase === 'revealed' ? 1 : 0,
              }}
            >
              {children}
            </p>
          )}

          {/* Reaction badge corner */}
          {reaction && (
            <span
              className="absolute"
              style={{
                bottom: -8, right: -4,
                background: theme.bgPaper,
                borderRadius: 999,
                padding: '2px 6px',
                fontSize: 13,
                boxShadow: '0 2px 6px rgba(0,0,0,0.12), 0 0 0 0.5px rgba(0,0,0,0.08)',
                lineHeight: 1,
              }}
              data-testid="sabril-note-reaction"
            >
              {reaction}
            </span>
          )}

          {/* Reaction picker */}
          {pickerOpen && phase === 'revealed' && (
            <div
              className="absolute"
              style={{
                top: -42, left: 12,
                background: theme.bgPaper,
                borderRadius: 999,
                padding: '5px 8px',
                display: 'flex',
                gap: 4,
                boxShadow: '0 10px 24px -6px rgba(0,0,0,0.22), 0 0 0 0.5px rgba(0,0,0,0.10)',
                zIndex: 30,
              }}
              data-testid="reaction-picker"
              onClick={(e) => e.stopPropagation()}
            >
              {REACTIONS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => { setReaction(emoji); setPickerOpen(false); }}
                  className="active:scale-110 transition-transform"
                  style={{ fontSize: 18, padding: '2px 4px', background: 'transparent', border: 'none', cursor: 'pointer', lineHeight: 1 }}
                  data-testid={`reaction-${emoji}`}
                  aria-label={`Réagir ${emoji}`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Read receipt */}
      {readAt && phase === 'revealed' && (
        <p
          className="pl-[44px]"
          style={{
            fontFamily: theme.fontMono, fontSize: 9.5, color: theme.inkMuted,
            fontWeight: 600, letterSpacing: '0.04em',
            marginTop: reaction ? 8 : 2,
          }}
          data-testid="sabril-note-read"
        >
          Lu ✓✓ {readAt}
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// LiveSabrilCounter — floating pill "X personnes en ligne avec Sabril"
// ─────────────────────────────────────────────────────────────────────
function LiveSabrilCounter() {
  const [count, setCount] = useState(23);
  useEffect(() => {
    const id = setInterval(() => {
      setCount((c) => {
        const delta = Math.floor(Math.random() * 5) - 2;
        return Math.max(17, Math.min(31, c + delta));
      });
    }, 4200);
    return () => clearInterval(id);
  }, []);
  return (
    <div
      className="sticky top-[44px] z-30 mx-3 my-2 flex justify-end pointer-events-none"
      data-testid="live-sabril-counter"
    >
      <div
        className="pointer-events-auto inline-flex items-center gap-1.5"
        style={{
          background: 'rgba(255,255,255,0.86)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          padding: '5px 10px 5px 8px',
          borderRadius: 999,
          border: '0.5px solid rgba(60,60,67,0.16)',
          boxShadow: '0 4px 12px -4px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <span className="relative inline-flex" style={{ width: 8, height: 8 }} aria-hidden>
          <span
            className="absolute inset-0"
            style={{
              borderRadius: 999, background: '#30D158',
              animation: 'sabrilLivePulse 1800ms ease-out infinite',
            }}
          />
          <span
            className="relative"
            style={{ width: 8, height: 8, borderRadius: 999, background: '#30D158' }}
          />
        </span>
        <span style={{ fontFamily: theme.fontDisplay, fontSize: 11.5, fontWeight: 700, color: theme.inkDeep, letterSpacing: '-0.005em' }}>
          {count}
        </span>
        <span style={{ fontFamily: theme.fontSans, fontSize: 10.5, color: theme.inkBody, fontWeight: 500 }}>
          en ligne avec Sabril
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// StoriesRail — Instagram-style horizontal stories (Sabril + brands)
// ─────────────────────────────────────────────────────────────────────
const STORIES = [
  { key: 'sabril',   label: 'Sabril',     image: SABRIL_AVATAR, isMe: true },
  { key: 'avocado',  label: 'Maison Avocado', initials: 'MA', from: '#3CB371', to: '#1A5D3F' },
  { key: 'bon',      label: 'BON',        initials: 'BN', from: '#E5C68A', to: '#9C7536' },
  { key: 'afrikn',   label: "Afri'K'n",   initials: 'AC', from: '#D9603D', to: '#762E18' },
  { key: 'zebra',    label: 'Zebra Gang', initials: 'ZG', from: '#1C1C1E', to: '#000000' },
  { key: 'bensimon', label: 'C.Bensimon', initials: 'CB', from: '#7B89A5', to: '#3D4858' },
];

function StoriesRail() {
  return (
    <div className="mt-1 mb-2" data-testid="stories-rail">
      <div className="overflow-x-auto px-4 no-scrollbar">
        <div className="flex gap-3.5" style={{ minWidth: 'min-content' }}>
          {STORIES.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={s.isMe ? openSabril : undefined}
              className="flex flex-col items-center active:scale-[0.96] transition-transform flex-shrink-0"
              data-testid={`story-${s.key}`}
              style={{ width: 64 }}
            >
              {/* Instagram-style gradient ring */}
              <span
                className="inline-flex items-center justify-center"
                style={{
                  width: 64, height: 64, borderRadius: 999,
                  padding: 2.5,
                  background: 'conic-gradient(from 45deg, #FE8C00, #F83600, #C71585, #833AB4, #515BD4, #F77737, #FE8C00)',
                }}
              >
                <span
                  className="inline-flex items-center justify-center relative overflow-hidden"
                  style={{
                    width: '100%', height: '100%', borderRadius: 999,
                    background: theme.bgPaper, padding: 2,
                  }}
                >
                  {s.image ? (
                    <img
                      src={s.image}
                      alt={s.label}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 999, objectPosition: '50% 22%' }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <span
                      className="flex items-center justify-center"
                      style={{
                        width: '100%', height: '100%', borderRadius: 999,
                        background: `linear-gradient(160deg, ${s.from} 0%, ${s.to} 100%)`,
                        fontFamily: theme.fontDisplay, fontSize: 18, fontWeight: 800,
                        color: '#FFFFFF', letterSpacing: '-0.02em',
                      }}
                    >
                      {s.initials}
                    </span>
                  )}
                  {s.isMe && (
                    <span
                      className="absolute"
                      style={{
                        bottom: 0, right: 0,
                        width: 20, height: 20, borderRadius: 999,
                        background: '#1C1C1E', border: '2px solid #FFFFFF',
                        fontFamily: theme.fontMono, fontSize: 12, fontWeight: 800,
                        color: '#FFFFFF',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >+</span>
                  )}
                </span>
              </span>
              <span
                style={{
                  fontFamily: theme.fontDisplay,
                  fontSize: 10.5,
                  fontWeight: 600,
                  color: theme.inkDeep,
                  marginTop: 6,
                  letterSpacing: '-0.005em',
                  textAlign: 'center',
                  width: '100%',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {s.isMe ? 'Vous ↔ Sabril' : s.label}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// EmotionalManifesto — Editorial italic-serif pull quote · pride/transmission
// ─────────────────────────────────────────────────────────────────────
function EmotionalManifesto() {
  return (
    <div className="px-5 py-10" data-testid="emotional-manifesto">
      <div
        className="relative overflow-hidden"
        style={{
          background: theme.bgPaper,
          borderRadius: 20,
          padding: '28px 22px 22px',
          boxShadow: '0 0 0 0.5px rgba(60,60,67,0.10), 0 1px 2px rgba(0,0,0,0.03)',
        }}
      >
        {/* Editorial decorative quote mark — like Vogue/luxury press */}
        <span
          aria-hidden
          style={{
            position: 'absolute', top: -10, left: 14,
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: 110, lineHeight: 1, color: '#FFD8B8',
            fontStyle: 'italic', fontWeight: 700, opacity: 0.85,
            pointerEvents: 'none',
          }}
        >“</span>

        <p
          className="relative"
          style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontStyle: 'italic',
            fontWeight: 400,
            fontSize: 22,
            lineHeight: 1.25,
            color: theme.inkDeep,
            letterSpacing: '-0.015em',
          }}
        >
          Je ne vends pas du conseil.<br />
          Je vends <span style={{ fontWeight: 700 }}>la fierté</span> d'avoir construit{' '}
          <span style={{ fontWeight: 700 }}>une marque qui dure</span>.
        </p>

        <p
          className="relative"
          style={{
            fontFamily: theme.fontSans,
            fontSize: 13,
            lineHeight: 1.5,
            color: theme.inkBody,
            marginTop: 14,
            fontWeight: 400,
          }}
        >
          Une marque rentable, ce n'est pas une chance. C'est une <strong style={{ fontWeight: 700, color: theme.inkDeep }}>transmission</strong> — une histoire qu'on raconte aux suivants, un héritage construit jour après jour.
        </p>

        {/* Signature */}
        <div className="relative flex items-center gap-2 mt-5 pt-4" style={{ borderTop: `0.5px solid ${theme.hairline}` }}>
          <span
            className="flex-shrink-0 inline-block overflow-hidden"
            style={{ width: 28, height: 28, borderRadius: 999, background: '#FFE9DB' }}
          >
            <img
              src={SABRIL_AVATAR}
              alt="Sabril"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: '50% 22%' }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </span>
          <div>
            <p style={{ fontFamily: theme.fontDisplay, fontSize: 12.5, fontWeight: 700, color: theme.inkDeep, letterSpacing: '-0.005em', lineHeight: 1.1 }}>
              Sabril Jurczeszyn
            </p>
            <p style={{ fontFamily: theme.fontMono, fontSize: 9.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: theme.inkMuted, fontWeight: 600, marginTop: 1 }}>
              Directeur de marque · depuis 2018
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// InstaProfileCard — Instagram-style profile of Sabril (replaces SabrilCard)
// ─────────────────────────────────────────────────────────────────────
function InstaProfileCard() {
  return (
    <div className="px-4 mb-2" data-testid="insta-profile">
      <div
        style={{
          background: theme.bgPaper,
          borderRadius: 20,
          padding: '22px 18px 18px',
          boxShadow: '0 0 0 0.5px rgba(60,60,67,0.10), 0 1px 2px rgba(0,0,0,0.03)',
        }}
      >
        {/* Top row: large avatar + stats grid */}
        <div className="flex items-center gap-4">
          {/* Instagram-style avatar with gradient ring */}
          <span
            className="flex-shrink-0 inline-flex items-center justify-center"
            style={{
              width: 86, height: 86, borderRadius: 999,
              padding: 3,
              background: 'conic-gradient(from 45deg, #FE8C00, #F83600, #C71585, #833AB4, #515BD4, #F77737, #FE8C00)',
            }}
          >
            <span
              className="inline-flex items-center justify-center overflow-hidden"
              style={{ width: '100%', height: '100%', borderRadius: 999, background: theme.bgPaper, padding: 2.5 }}
            >
              <img
                src={SABRIL_AVATAR}
                alt="Sabril"
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 999, objectPosition: '50% 22%' }}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </span>
          </span>

          {/* Stats grid 3-col */}
          <div className="flex-1 grid grid-cols-3 gap-1 text-center">
            {[
              { v: '40+', l: 'marques' },
              { v: '8',   l: 'ans' },
              { v: '6',   l: 'pays' },
            ].map((s) => (
              <div key={s.l}>
                <p style={{ fontFamily: theme.fontDisplay, fontSize: 18, fontWeight: 800, color: theme.inkDeep, letterSpacing: '-0.025em', lineHeight: 1 }}>
                  {s.v}
                </p>
                <p style={{ fontFamily: theme.fontMono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: theme.inkMuted, fontWeight: 600, marginTop: 4 }}>
                  {s.l}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Name + verified + handle */}
        <div className="mt-4">
          <div className="flex items-center gap-1.5">
            <p style={{ fontFamily: theme.fontDisplay, fontSize: 16, fontWeight: 700, color: theme.inkDeep, letterSpacing: '-0.015em', lineHeight: 1.1 }}>
              Sabril Jurczeszyn
            </p>
            {/* Verified check */}
            <span
              className="inline-flex items-center justify-center"
              style={{
                width: 16, height: 16, borderRadius: 999,
                background: '#0095F6',
                color: '#FFFFFF',
                fontFamily: theme.fontMono, fontSize: 9, fontWeight: 800,
              }}
              aria-label="Verified"
            >
              <Check className="w-2.5 h-2.5" strokeWidth={3.5} />
            </span>
          </div>
          <p style={{ fontFamily: theme.fontMono, fontSize: 11, letterSpacing: '0.04em', color: theme.inkMuted, marginTop: 2, fontWeight: 500 }}>
            @sabril.j · Directeur de marque
          </p>
          <p
            style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontStyle: 'italic',
              fontSize: 13.5,
              lineHeight: 1.4,
              color: theme.inkDeep,
              marginTop: 8,
              letterSpacing: '-0.005em',
            }}
          >
            « Je construis des marques rentables, structurées et pérennes. »
          </p>
          <p style={{ fontFamily: theme.fontSans, fontSize: 12, color: theme.inkBody, marginTop: 4, lineHeight: 1.45 }}>
            Le 360 pour éviter tous les pièges de la restauration.
          </p>
          <p style={{ fontFamily: theme.fontSans, fontSize: 12.5, color: theme.inkBody, marginTop: 6, lineHeight: 1.45 }}>
            <span style={{ color: theme.inkDeep, fontWeight: 700 }}>9 clients que j'accompagne actuellement</span> · 4 places restantes sur sélection. Cannes, Marbella, Marrakech, Miami.
          </p>
        </div>

        {/* CTA row — Instagram-style pill buttons */}
        <div className="flex items-center gap-2 mt-4">
          <button
            type="button"
            onClick={() => openSabril('question')}
            className="flex-1 inline-flex items-center justify-center gap-1.5 active:scale-[0.97] transition-transform"
            style={{
              background: 'linear-gradient(180deg, #1C1C1E 0%, #000000 100%)',
              color: '#FFFFFF',
              padding: '10px 14px',
              borderRadius: 10,
              fontFamily: theme.fontDisplay,
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '-0.005em',
              boxShadow: '0 1px 0 0 rgba(255,255,255,0.18) inset, 0 6px 14px -4px rgba(0,0,0,0.30)',
            }}
            data-testid="insta-cta-discuter"
          >
            <MessageCircle className="w-3.5 h-3.5" strokeWidth={2.4} />
            Discuter
          </button>
          <a
            href="https://instagram.com/sabril.j"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 inline-flex items-center justify-center gap-1.5 active:scale-[0.97] transition-transform"
            style={{
              background: '#F2F2F7',
              color: theme.inkDeep,
              padding: '9px 14px',
              borderRadius: 10,
              fontFamily: theme.fontDisplay,
              fontSize: 12.5,
              fontWeight: 700,
              letterSpacing: '-0.005em',
              border: '0.5px solid rgba(60,60,67,0.12)',
              textDecoration: 'none',
            }}
            data-testid="insta-cta-follow"
          >
            <Star className="w-3.5 h-3.5" strokeWidth={2.4} />
            Suivre · @sabril.j
          </a>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// InternationalMap — Stylized globe with 4 cities + LIVE occupancy
// Marrakech · Marbella · Cannes · Miami (taken = baseline + Mongo bookings)
// ─────────────────────────────────────────────────────────────────────
const NETWORK_CITIES = [
  // Positions on a 340x340 viewBox, globe centered at (170, 170) r=130
  // `baseline` = restos déjà accompagnés (réel), Mongo bookings vont s'additionner
  { key: 'miami',     label: 'Miami',     country: 'États-Unis', baseline: 1, slots: 3, x: 70,  y: 152, labelX: 14,  labelY: 152, anchor: 'start' },
  { key: 'marbella',  label: 'Marbella',  country: 'Espagne',    baseline: 1, slots: 2, x: 152, y: 140, labelX: 95,  labelY: 70,  anchor: 'start' },
  { key: 'cannes',    label: 'Cannes',    country: 'France',     baseline: 0, slots: 2, x: 178, y: 122, labelX: 230, labelY: 60,  anchor: 'end' },
  { key: 'marrakech', label: 'Marrakech', country: 'Maroc',      baseline: 3, slots: 6, x: 158, y: 175, labelX: 78,  labelY: 290, anchor: 'start' },
];

function InternationalMap() {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [cityBookings, setCityBookings] = useState({});

  // Fetch live per-city bookings from Mongo
  useEffect(() => {
    let mounted = true;
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API_URL}/api/sabril/live-stats`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (mounted) setCityBookings(data.city_bookings || {});
      } catch (e) { /* silent */ }
    };
    fetchStats();
    const poll = setInterval(fetchStats, 20000);
    return () => { mounted = false; clearInterval(poll); };
  }, [API_URL]);

  // Compute live cities with taken = baseline ONLY (live bookings are NOT shown in
  // the public count — the user maintains the curated baseline narrative).
  // Live bookings are still tracked in Mongo for admin/CRM purposes.
  const liveCities = useMemo(() => (
    NETWORK_CITIES.map((c) => ({ ...c, taken: c.baseline }))
  ), []);

  // Per-city visible total (5 sur les 4 villes iconiques) + 4 clients dans d'autres villes (Paris, Bangtao, NY, etc.)
  // Le visiteur voit "9 clients actuels · 4 places restantes" — coherent avec le reste de la page.
  const HIDDEN_CLIENTS = 4;
  const cityVisibleTaken = liveCities.reduce((s, c) => s + c.taken, 0);
  const totalSlots = liveCities.reduce((s, c) => s + c.slots, 0);
  const totalTaken = Math.min(cityVisibleTaken + HIDDEN_CLIENTS, totalSlots);

  // Build SVG content as a string to bypass Visual Editor <span display:contents>
  // wrappers that break .map() rendering inside <svg>.
  const svgInner = (() => {
    const parts = [];

    // Defs
    parts.push(`<defs>
      <radialGradient id="globe-fill" cx="38%" cy="32%" r="78%">
        <stop offset="0%" stop-color="#FFFFFF" stop-opacity="0.95"/>
        <stop offset="55%" stop-color="#F4EFE5" stop-opacity="0.92"/>
        <stop offset="100%" stop-color="#E5DCC9" stop-opacity="0.88"/>
      </radialGradient>
      <radialGradient id="globe-spec" cx="30%" cy="22%" r="35%">
        <stop offset="0%" stop-color="#FFFFFF" stop-opacity="0.55"/>
        <stop offset="100%" stop-color="#FFFFFF" stop-opacity="0"/>
      </radialGradient>
      <radialGradient id="city-glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stop-color="#FE8C00" stop-opacity="0.55"/>
        <stop offset="60%" stop-color="#F83600" stop-opacity="0.22"/>
        <stop offset="100%" stop-color="#F83600" stop-opacity="0"/>
      </radialGradient>
      <radialGradient id="globe-rim" cx="50%" cy="50%" r="50%">
        <stop offset="92%" stop-color="rgba(168,124,79,0)" stop-opacity="0"/>
        <stop offset="99%" stop-color="rgba(168,124,79,0.50)" stop-opacity="0.50"/>
        <stop offset="100%" stop-color="rgba(168,124,79,0)" stop-opacity="0"/>
      </radialGradient>
    </defs>`);

    // Globe body
    parts.push(`<circle cx="170" cy="170" r="130" fill="url(#globe-fill)" stroke="rgba(168,124,79,0.20)" stroke-width="0.5"/>`);
    parts.push(`<circle cx="170" cy="170" r="130" fill="url(#globe-spec)"/>`);
    parts.push(`<circle cx="170" cy="170" r="138" fill="url(#globe-rim)"/>`);

    // Latitude lines (gold ink)
    [40, 78, 110, 130, 110, 78, 40].forEach((rx, i) => {
      const cy = 80 + i * 30;
      parts.push(`<ellipse cx="170" cy="${cy}" rx="${rx}" ry="3" fill="none" stroke="rgba(168,124,79,0.28)" stroke-width="0.5"/>`);
    });

    // Longitude lines (curved ellipses, prime meridian highlighted)
    for (let i = 0; i <= 6; i++) {
      const rx = Math.max(8, 130 - Math.abs(i - 3) * 40);
      const stroke = i === 3 ? 'rgba(168,124,79,0.38)' : 'rgba(168,124,79,0.20)';
      parts.push(`<ellipse cx="170" cy="170" rx="${rx}" ry="130" fill="none" stroke="${stroke}" stroke-width="0.5"/>`);
    }

    // Simplified continents (subtle stipple, clipped to globe)
    parts.push(`<clipPath id="globe-clip"><circle cx="170" cy="170" r="130"/></clipPath>`);
    parts.push(`<g clip-path="url(#globe-clip)" fill="rgba(168,124,79,0.38)" stroke="rgba(168,124,79,0.50)" stroke-width="0.4">
      <!-- N. America -->
      <path d="M 56,118 C 70,108 90,104 108,110 C 124,115 138,124 146,138 L 152,156 C 156,172 154,188 144,200 C 132,212 116,218 100,216 C 84,212 70,200 62,184 C 56,170 54,150 56,134 Z"/>
      <!-- Florida hook -->
      <path d="M 124,160 C 130,170 132,184 128,194 C 124,202 116,202 112,194 C 110,184 114,170 122,160 Z"/>
      <!-- S. America -->
      <path d="M 122,222 C 138,222 152,234 156,250 C 158,266 152,282 142,290 C 130,296 116,290 108,278 C 102,262 104,246 116,232 Z"/>
      <!-- Europe -->
      <path d="M 170,108 C 188,104 210,106 222,114 C 230,122 232,132 224,140 C 212,146 194,146 178,142 C 168,138 162,128 166,118 Z"/>
      <!-- Iberian -->
      <path d="M 156,144 C 162,142 170,146 172,154 L 170,168 C 166,172 158,172 154,164 Z"/>
      <!-- UK -->
      <path d="M 162,96 C 168,93 174,95 174,102 C 174,108 168,112 162,108 Z"/>
      <!-- Italy -->
      <path d="M 200,148 C 204,156 204,168 202,178 C 200,184 196,184 195,178 C 194,166 196,154 200,148 Z"/>
      <!-- Scandinavia -->
      <path d="M 190,80 C 200,76 212,78 218,86 C 222,96 218,104 208,106 C 198,104 188,96 188,86 Z"/>
      <!-- N. Africa -->
      <path d="M 156,180 C 178,176 208,176 232,180 C 254,184 268,194 268,210 C 266,228 246,236 220,236 C 196,236 174,232 158,224 C 148,216 146,200 152,188 Z"/>
      <!-- W. Africa -->
      <path d="M 188,242 C 208,242 224,254 228,272 C 230,288 220,300 204,302 C 184,298 170,284 168,266 C 168,254 176,244 188,242 Z"/>
    </g>`);

    // Connection arcs (closed loop)
    NETWORK_CITIES.forEach((c, i) => {
      const next = NETWORK_CITIES[(i + 1) % NETWORK_CITIES.length];
      const midX = (c.x + next.x) / 2;
      const midY = (c.y + next.y) / 2;
      const ctrlX = midX + (170 - midX) * 0.18;
      const ctrlY = midY + (170 - midY) * 0.18 - 12;
      parts.push(`<path d="M ${c.x} ${c.y} Q ${ctrlX} ${ctrlY} ${next.x} ${next.y}" fill="none" stroke="#FE8C00" stroke-width="1.2" stroke-dasharray="3 3" opacity="0.70"/>`);
    });

    // City markers + labels
    liveCities.forEach((c) => {
      // Glow halo
      parts.push(`<circle cx="${c.x}" cy="${c.y}" r="16" fill="url(#city-glow)" opacity="0.85"/>`);
      // Pulsing ring (CSS animation via class)
      parts.push(`<circle cx="${c.x}" cy="${c.y}" r="6" fill="none" stroke="#FE8C00" stroke-width="1.4" opacity="0.85" class="sabril-map-pulse" style="transform-origin: ${c.x}px ${c.y}px"/>`);
      // Solid dots — white halo with orange core for high contrast on light bg
      parts.push(`<circle cx="${c.x}" cy="${c.y}" r="4.2" fill="#FFFFFF" stroke="#FE8C00" stroke-width="1.6"/>`);
      parts.push(`<circle cx="${c.x}" cy="${c.y}" r="2.2" fill="#FE8C00"/>`);

      // Leader line
      const dirX = c.anchor === 'end' ? -4 : 4;
      parts.push(`<line x1="${c.x}" y1="${c.y}" x2="${c.labelX + dirX}" y2="${c.labelY}" stroke="rgba(168,124,79,0.55)" stroke-width="0.6"/>`);

      // Labels — DARK ink for contrast on light bg
      parts.push(`<text x="${c.labelX}" y="${c.labelY - 2}" text-anchor="${c.anchor}" font-family="-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif" font-size="13" font-weight="800" fill="#0A0A0F" letter-spacing="-0.01em">${c.label}</text>`);
      parts.push(`<text x="${c.labelX}" y="${c.labelY + 11}" text-anchor="${c.anchor}" font-family="'SF Mono', ui-monospace, Menlo, monospace" font-size="8.5" fill="rgba(168,124,79,0.95)" letter-spacing="0.10em" font-weight="700">${c.country.toUpperCase()}</text>`);

      // Occupancy pill: "X / Y restos"
      const full = c.taken >= c.slots;
      const occColor = full ? '#FF3B30' : '#1FA644';
      const occBg = full ? '#FFFFFF' : '#FFFFFF';
      parts.push(`<text x="${c.labelX}" y="${c.labelY + 24}" text-anchor="${c.anchor}" font-family="'SF Mono', ui-monospace, Menlo, monospace" font-size="9.5" fill="${occColor}" font-weight="800" letter-spacing="0.04em" style="paint-order: stroke; stroke: ${occBg}; stroke-width: 5; stroke-linejoin: round;">${c.taken}/${c.slots} restos</text>`);
    });

    return parts.join('');
  })();

  return (
    <div className="px-4 mb-6 mt-2" data-testid="international-map">
      <div
        className="relative"
        style={{
          background: theme.bgPaper,
          borderRadius: 24,
          padding: '22px 18px 18px',
          border: '0.5px solid rgba(60,60,67,0.10)',
          boxShadow: '0 12px 28px -14px rgba(26,23,20,0.14)',
        }}
      >
        {/* Overline */}
        <p className="relative" style={{ fontFamily: theme.fontMono, fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: theme.inkMuted, fontWeight: 700, marginBottom: 4 }}>
          Réseau international · 4 villes
        </p>
        <h2 className="relative" style={{ fontFamily: theme.fontDisplay, fontSize: 22, fontWeight: 800, color: theme.inkDeep, letterSpacing: '-0.025em', lineHeight: 1.1 }}>
          Là où je travaille.
        </h2>
        <p className="relative" style={{ fontFamily: theme.fontSans, fontSize: 12.5, color: theme.inkBody, marginTop: 6, lineHeight: 1.45 }}>
          De Cannes à Miami — un circuit où je construis des marques rentables. {totalTaken} clients que j'accompagne actuellement · {totalSlots - totalTaken} {totalSlots - totalTaken > 1 ? 'places restantes' : 'place restante'}.
        </p>

        {/* Globe SVG — rendered as HTML string to bypass Visual Editor wrappers */}
        <div className="relative mt-4 flex justify-center">
          <svg
            viewBox="0 0 340 340"
            style={{ width: '100%', maxWidth: 340, height: 'auto', overflow: 'visible' }}
            xmlns="http://www.w3.org/2000/svg"
            dangerouslySetInnerHTML={{ __html: svgInner }}
          />
        </div>

        {/* Caption strip */}
        <div className="relative mt-2 flex items-center justify-between" style={{ paddingTop: 10, borderTop: '0.5px solid rgba(60,60,67,0.10)' }}>
          <p style={{ fontFamily: theme.fontMono, fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#A87C4F', fontWeight: 800 }}>
            {totalTaken} / {totalSlots} places prises
          </p>
          <p style={{ fontFamily: theme.fontMono, fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: theme.inkMuted, fontWeight: 600 }}>
            4 villes · {totalSlots} slots
          </p>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Section header (iOS "Large Title" mini) — MASSIVE breathing room
// ─────────────────────────────────────────────────────────────────────
function SectionHeader({ overline, title, subtitle, action }) {
  return (
    <div className="px-5 pt-6 pb-4" data-testid={`section-${overline?.replace(/\s/g, '-').toLowerCase()}`}>
      {overline && (
        <p style={{ fontFamily: theme.fontMono, fontSize: 10.5, letterSpacing: '0.18em', textTransform: 'uppercase', color: theme.inkMuted, fontWeight: 700, marginBottom: 12 }}>
          {overline}
        </p>
      )}
      <div className="flex items-end justify-between gap-3">
        <h2 style={{ fontFamily: theme.fontDisplay, fontSize: 24, fontWeight: 800, color: theme.inkDeep, letterSpacing: '-0.025em', lineHeight: 1.15 }}>
          {title}
        </h2>
        {action}
      </div>
      {subtitle && (
        <p style={{ fontFamily: theme.fontSans, fontSize: 14, color: theme.inkBody, marginTop: 10, lineHeight: 1.5 }}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// iOS Settings-like Group List
// ─────────────────────────────────────────────────────────────────────
function ListGroup({ children, footer }) {
  return (
    <div className="px-4 mb-5" data-testid="list-group">
      <div
        style={{
          background: theme.bgPaper,
          borderRadius: 14,
          overflow: 'hidden',
          boxShadow: '0 0 0 0.5px rgba(60,60,67,0.10), 0 1px 2px rgba(0,0,0,0.03)',
        }}
      >
        {children}
      </div>
      {footer && (
        <p
          className="px-1 pt-1.5"
          style={{ fontFamily: theme.fontSans, fontSize: 11, color: theme.inkMuted, lineHeight: 1.35 }}
        >
          {footer}
        </p>
      )}
    </div>
  );
}

function ListRow({ icon: Icon, iconBg = '#007AFF', title, value, subtitle, last = false, onClick, testid }) {
  const content = (
    <div
      className="flex items-center gap-3 px-4 py-2.5 active:bg-zinc-50 transition-colors"
      style={{ borderBottom: last ? 'none' : `0.5px solid ${theme.hairline}` }}
      data-testid={testid}
    >
      {Icon && (
        <span
          className="flex items-center justify-center flex-shrink-0"
          style={{ width: 28, height: 28, borderRadius: 7, background: iconBg }}
        >
          <Icon className="w-[15px] h-[15px]" style={{ color: '#FFFFFF' }} strokeWidth={2.4} />
        </span>
      )}
      <div className="flex-1 min-w-0">
        <p style={{ fontFamily: theme.fontDisplay, fontSize: 14, color: theme.inkDeep, fontWeight: 500, letterSpacing: '-0.005em' }}>
          {title}
        </p>
        {subtitle && (
          <p style={{ fontFamily: theme.fontSans, fontSize: 11.5, color: theme.inkMuted, marginTop: 1 }}>{subtitle}</p>
        )}
      </div>
      {value && (
        <span style={{ fontFamily: theme.fontMono, fontSize: 12, color: theme.inkMuted, fontWeight: 500 }}>
          {value}
        </span>
      )}
      {onClick && <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color: theme.inkMuted }} strokeWidth={2.5} />}
    </div>
  );
  return onClick ? (
    <button type="button" onClick={onClick} className="w-full text-left">{content}</button>
  ) : content;
}

// ─────────────────────────────────────────────────────────────────────
// Objectifs — 2 native iOS cards (qualification)
// ─────────────────────────────────────────────────────────────────────
function Objectifs() {
  return (
    <div data-testid="objectifs">
      <SectionHeader overline="Objectifs · à qui c'est destiné" title="Deux profils. Une destination." subtitle="Que vous soyez déjà ouvert ou en projet, on a un chemin pour vous." />
      <div className="px-4 mb-8 flex flex-col gap-7">
        {OBJECTIVES.map((o) => (
          <div
            key={o.key}
            style={{
              background: theme.bgPaper,
              borderRadius: 16,
              padding: 14,
              boxShadow: '0 0 0 0.5px rgba(60,60,67,0.10), 0 1px 2px rgba(0,0,0,0.03)',
            }}
            data-testid={`objective-${o.key}`}
          >
            <div className="flex items-center gap-2.5 mb-2.5">
              <span
                className="inline-flex items-center justify-center flex-shrink-0"
                style={{ width: 30, height: 30, borderRadius: 8, background: o.color }}
              >
                <o.icon className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={2.4} />
              </span>
              <div className="flex-1 min-w-0">
                <p style={{ fontFamily: theme.fontMono, fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: theme.inkMuted, fontWeight: 700 }}>
                  {o.num} / 02
                </p>
                <p style={{ fontFamily: theme.fontDisplay, fontSize: 15, fontWeight: 700, color: theme.inkDeep, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                  {o.title}
                </p>
              </div>
            </div>
            <ul className="space-y-1.5">
              {o.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <span
                    className="flex-shrink-0 mt-1.5"
                    style={{ width: 4, height: 4, borderRadius: 999, background: o.color }}
                  />
                  <span style={{ fontFamily: theme.fontSans, fontSize: 12.5, color: theme.inkBody, lineHeight: 1.35 }}>
                    {b}
                  </span>
                </li>
              ))}
            </ul>
            {o.threshold && (
              <div
                className="mt-3 pt-3 border-t"
                style={{ borderColor: 'rgba(60,60,67,0.10)' }}
                data-testid={`objective-threshold-${o.key}`}
              >
                <p style={{ fontFamily: theme.fontMono, fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: o.color, fontWeight: 800, marginBottom: 3 }}>
                  ⚠ Seuil minimum
                </p>
                <p style={{ fontFamily: theme.fontDisplay, fontSize: 13, color: theme.inkDeep, fontWeight: 800, letterSpacing: '-0.01em', marginBottom: 3 }}>
                  {o.threshold}
                </p>
                <p style={{ fontFamily: theme.fontSans, fontSize: 11.5, color: theme.inkMuted, lineHeight: 1.35, fontStyle: 'italic' }}>
                  {o.thresholdSub}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Conviction — iOS quote card (objection neutralization)
// ─────────────────────────────────────────────────────────────────────
function Conviction() {
  return (
    <div data-testid="conviction">
      <SectionHeader overline="Avant de parler prix" title="Le prix n'est jamais le problème." />
      <div className="px-4 mb-3">
        <div
          className="relative overflow-hidden"
          style={{
            background: theme.bgPaper,
            borderRadius: 16,
            padding: 16,
            boxShadow: '0 0 0 0.5px rgba(60,60,67,0.10), 0 1px 2px rgba(0,0,0,0.03)',
          }}
        >
          <Quote className="absolute top-3 right-3 w-6 h-6" style={{ color: theme.bgSecondary }} strokeWidth={1.6} />
          <p style={{ fontFamily: theme.fontDisplay, fontSize: 14, fontWeight: 500, color: theme.inkDeep, lineHeight: 1.4, letterSpacing: '-0.01em' }}>
            Un mauvais positionnement vous coûte chaque mois. Une équipe mal pilotée vous coûte chaque jour. Une carte mal pensée vous coûte chaque service.
          </p>
          <div className="my-3" style={{ height: 0.5, background: theme.hairline }} />
          <p style={{ fontFamily: theme.fontSans, fontSize: 12.5, color: theme.inkBody, lineHeight: 1.45 }}>
            Le Salon Privé n'est pas une dépense — c'est <strong style={{ color: theme.inkDeep, fontWeight: 700 }}>l'investissement qui paie pour lui-même</strong>, mois après mois, en marges récupérées et en sérénité retrouvée.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// BrandDNA — Vente → Confiance → Marque (cards premium fond blanc + photos)
// Style Maison Avocado : hero photo, caption italique, contenu carré.
// ─────────────────────────────────────────────────────────────────────
const BRAND_DNA_PHOTOS = {
  vente: 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/uijsvi98_IMG_2048.jpeg',
  confiance: 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/k7oxk5vs_IMG_4621.jpg',
  marque: 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/imqrh3vs_IMG_0611.jpeg',
};

function BrandDNA() {
  const tiers = [
    {
      n: '01',
      tag: '',
      title: 'Vente',
      headline: 'Un client passe, paye, repart.',
      body: 'Vous avez encaissé. Vous n\'avez rien construit. C\'est le bruit de fond du marché — chaud aujourd\'hui, oublié demain.',
      caption: 'Maison Avocado · Lille — la vitrine, première promesse de marque.',
      image: BRAND_DNA_PHOTOS.vente,
    },
    {
      n: '02',
      tag: '',
      title: 'Confiance',
      headline: 'Il revient. Il vous valide.',
      body: 'Vous devenez son réflexe — pas son choix par défaut. La confiance ne se vend pas, elle se gagne service après service.',
      caption: 'Maison Avocado · le geste signature — l\'expérience qui fait revenir.',
      image: BRAND_DNA_PHOTOS.confiance,
    },
    {
      n: '03',
      tag: '',
      title: 'Marque',
      headline: 'Il en parle. Tes clients vendent pour vous.',
      body: 'Ses amis viennent. Vous devenez une référence. La marque, c\'est quand vous n\'avez plus à convaincre — on vient déjà.',
      caption: 'La marque vivante · partagée, racontée, recommandée.',
      image: BRAND_DNA_PHOTOS.marque,
    },
  ];

  return (
    <div className="px-4 mb-3" data-testid="brand-dna">
      <style>{`
        @keyframes brand-dna-divider {
          0%, 100% { opacity: 0.55; transform: translateY(0); }
          50%      { opacity: 1;    transform: translateY(2px); }
        }
        @keyframes brand-dna-shine-soft {
          0%   { transform: translateX(-120%); opacity: 0; }
          40%  { opacity: 0.55; }
          100% { transform: translateX(220%); opacity: 0; }
        }
      `}</style>

      {/* Overline · Premium serif */}
      <div className="text-center mb-3">
        <p style={{
          fontFamily: theme.fontMono,
          fontSize: 9.5,
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          color: '#A87C4F',
          fontWeight: 700,
        }}>
          L'ADN · Ce qu'on construit vraiment
        </p>
        <p style={{
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontStyle: 'italic',
          fontSize: 21,
          lineHeight: 1.18,
          color: theme.inkDeep,
          letterSpacing: '-0.018em',
          marginTop: 6,
        }}>
          On ne construit pas une vente.<br />
          <span style={{ color: '#A87C4F' }}>On construit une marque.</span>
        </p>
        <p style={{
          fontFamily: theme.fontSans,
          fontSize: 12,
          color: theme.inkMuted,
          lineHeight: 1.5,
          marginTop: 6,
          maxWidth: 320,
          marginLeft: 'auto',
          marginRight: 'auto',
        }}>
          Trois niveaux. Les 12 chantiers vous amènent du premier au dernier.
        </p>
      </div>

      {/* 3 premium tier cards */}
      <div className="flex flex-col gap-4">
        {tiers.map((t, i) => {
          const isLast = i === tiers.length - 1;
          return (
            <div key={t.n} className="relative">
              <article
                className="relative overflow-hidden"
                style={{
                  background: theme.bgPaper,
                  borderRadius: 18,
                  border: '0.5px solid rgba(60,60,67,0.10)',
                  boxShadow: '0 0 0 0.5px rgba(60,60,67,0.06), 0 14px 32px -14px rgba(0,0,0,0.18)',
                }}
                data-testid={`brand-dna-tier-${t.n}`}
              >
                {/* Hero photo · 4:3 */}
                <div
                  className="relative"
                  style={{
                    width: '100%',
                    aspectRatio: '4 / 3',
                    overflow: 'hidden',
                    background: '#F5F5F7',
                  }}
                >
                  <img
                    src={t.image}
                    alt={t.title}
                    loading="lazy"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  {/* Subtle shine sweep on hover-equivalent (auto soft) */}
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0"
                    style={{
                      background: 'linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.22) 50%, transparent 65%)',
                      animation: `brand-dna-shine-soft ${5 + i * 0.6}s ease-in-out ${i * 0.8}s infinite`,
                      mixBlendMode: 'overlay',
                    }}
                  />
                  {/* Top-left iOS chip — Numéro seul */}
                  <div
                    className="absolute top-3 left-3 inline-flex items-center"
                    style={{
                      background: 'rgba(255,255,255,0.92)',
                      backdropFilter: 'blur(12px) saturate(180%)',
                      WebkitBackdropFilter: 'blur(12px) saturate(180%)',
                      borderRadius: 999,
                      padding: '4px 7px',
                      border: '0.5px solid rgba(60,60,67,0.10)',
                      boxShadow: '0 4px 10px -3px rgba(0,0,0,0.12)',
                    }}
                  >
                    <span
                      style={{
                        width: 18, height: 18, borderRadius: 6,
                        background: 'linear-gradient(180deg, #1a1a1a 0%, #2a2a2a 100%)',
                        color: '#FFFFFF',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: theme.fontDisplay,
                        fontSize: 10,
                        fontWeight: 800,
                        letterSpacing: '-0.02em',
                      }}
                    >
                      {t.n}
                    </span>
                  </div>
                  {/* Bottom-right title chip — premium gold accent */}
                  <div
                    className="absolute bottom-3 right-3"
                    style={{
                      background: 'rgba(0,0,0,0.62)',
                      backdropFilter: 'blur(10px) saturate(180%)',
                      WebkitBackdropFilter: 'blur(10px) saturate(180%)',
                      borderRadius: 999,
                      padding: '5px 12px',
                      border: '0.5px solid rgba(255,255,255,0.18)',
                    }}
                  >
                    <span style={{
                      fontFamily: theme.fontDisplay,
                      fontSize: 13,
                      fontWeight: 800,
                      color: '#FFFFFF',
                      letterSpacing: '-0.015em',
                    }}>
                      {t.title}
                    </span>
                  </div>
                </div>

                {/* Italic caption · Maison Avocado style */}
                <p
                  className="px-4 pt-3"
                  style={{
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    fontStyle: 'italic',
                    fontSize: 11.5,
                    lineHeight: 1.4,
                    color: theme.inkMuted,
                    letterSpacing: '-0.005em',
                  }}
                >
                  « {t.caption} »
                </p>

                {/* Content */}
                <div className="px-4 pt-2 pb-4">
                  <p style={{
                    fontFamily: theme.fontDisplay,
                    fontSize: 17,
                    fontWeight: 800,
                    color: theme.inkDeep,
                    letterSpacing: '-0.025em',
                    lineHeight: 1.15,
                    marginTop: 4,
                  }}>
                    {t.headline}
                  </p>
                  <p style={{
                    fontFamily: theme.fontSans,
                    fontSize: 12.5,
                    color: theme.inkBody,
                    lineHeight: 1.5,
                    marginTop: 6,
                  }}>
                    {t.body}
                  </p>
                </div>
              </article>

              {/* Connector chevron between tiers */}
              {!isLast && (
                <div
                  className="flex justify-center py-2"
                  aria-hidden
                  data-testid={`brand-dna-connector-${t.n}`}
                >
                  <span
                    className="inline-flex items-center justify-center"
                    style={{
                      width: 28, height: 28, borderRadius: 999,
                      background: theme.bgPaper,
                      border: '0.5px solid rgba(168,124,79,0.30)',
                      boxShadow: '0 4px 10px -3px rgba(168,124,79,0.25)',
                      animation: 'brand-dna-divider 1.8s ease-in-out infinite',
                    }}
                  >
                    <ChevronDown className="w-3.5 h-3.5" strokeWidth={2.6} style={{ color: '#A87C4F' }} />
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Sabril signature line — premium soft warm */}
      <div
        className="mt-4 px-4 py-3.5"
        style={{
          background: 'linear-gradient(180deg, #FFFDF7 0%, #FBF4E5 100%)',
          borderRadius: 14,
          border: '0.5px solid rgba(168,124,79,0.22)',
          boxShadow: '0 8px 22px -14px rgba(168,124,79,0.30)',
        }}
      >
        <p style={{
          fontFamily: theme.fontMono,
          fontSize: 9.5,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: '#A87C4F',
          fontWeight: 700,
        }}>
          — Sabril · ADN maison
        </p>
        <p style={{
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontStyle: 'italic',
          fontSize: 14,
          color: theme.inkDeep,
          lineHeight: 1.45,
          letterSpacing: '-0.005em',
          marginTop: 6,
        }}>
          « Les <strong style={{ fontWeight: 800, fontStyle: 'normal' }}>12 chantiers</strong> ne sont pas conçus pour vendre une fois. Ils sont pensés pour vous amener <span style={{ color: '#A87C4F' }}>de la vente</span> jusqu'à <span style={{ color: '#A87C4F' }}>la marque</span>. »
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Programme Details — 6 piliers expandables (value showcase)
// ─────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────
// ChantierContinueButton — small arrow pill between each chantier
// to make the journey intentional (vs natural scroll only).
// Clicking it scrolls smoothly to the next chantier's anchor.
// ─────────────────────────────────────────────────────────────────────
function ChantierContinueButton({ toKey, label }) {
  const onClick = () => {
    try { navigator.vibrate?.(10); } catch (_) { /* noop */ }
    const target = document.getElementById(`pillar-${toKey}`);
    if (target) {
      const top = target.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };
  return (
    <div className="flex justify-center py-2.5" data-testid={`chantier-continue-${toKey}`}>
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1.5 active:scale-[0.95] transition-transform"
        style={{
          fontFamily: theme.fontSans,
          fontSize: 11.5,
          fontWeight: 700,
          letterSpacing: '-0.005em',
          color: theme.inkDeep,
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(16px) saturate(180%)',
          WebkitBackdropFilter: 'blur(16px) saturate(180%)',
          border: '0.5px solid rgba(60,60,67,0.15)',
          borderRadius: 999,
          padding: '6px 12px 6px 11px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.55)',
        }}
      >
        <span style={{ color: theme.inkMuted, fontSize: 10.5, fontWeight: 600 }}>Suivant :</span>
        <span>{label}</span>
        <span aria-hidden style={{ fontSize: 11, lineHeight: 1, color: '#F05A1A' }}>↓</span>
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// MiniArrowButton — generic small arrow pill that scrolls to any anchor id.
// Used to guide the user through the top of the page (CH 00) between
// the profile card, the funnel rules, and the career swipe.
// ─────────────────────────────────────────────────────────────────────
function MiniArrowButton({ toId, label, testid }) {
  const onClick = () => {
    try { navigator.vibrate?.(10); } catch (_) { /* noop */ }
    const target = document.getElementById(toId);
    if (target) {
      const top = target.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };
  return (
    <div className="flex justify-center py-2.5" data-testid={testid || `mini-arrow-${toId}`}>
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1.5 active:scale-[0.95] transition-transform"
        style={{
          fontFamily: theme.fontSans,
          fontSize: 11.5,
          fontWeight: 700,
          letterSpacing: '-0.005em',
          color: theme.inkDeep,
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(16px) saturate(180%)',
          WebkitBackdropFilter: 'blur(16px) saturate(180%)',
          border: '0.5px solid rgba(60,60,67,0.15)',
          borderRadius: 999,
          padding: '6px 12px 6px 11px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.55)',
        }}
      >
        <span style={{ color: theme.inkMuted, fontSize: 10.5, fontWeight: 600 }}>Suivant :</span>
        <span>{label}</span>
        <span aria-hidden style={{ fontSize: 11, lineHeight: 1, color: '#F05A1A' }}>↓</span>
      </button>
    </div>
  );
}


function ProgrammeDetails() {
  const scrollerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const update = () => {
      const isDesktop = window.matchMedia('(min-width: 768px)').matches;
      if (!isDesktop) {
        setCanScrollLeft(false);
        setCanScrollRight(false);
        return;
      }
      const max = el.scrollWidth - el.clientWidth;
      setCanScrollLeft(el.scrollLeft > 8);
      setCanScrollRight(el.scrollLeft < max - 8);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    // Card width ~440 + gap 24 → step ~464 per card
    el.scrollBy({ left: dir * 464, behavior: 'smooth' });
  };

  // Compute the cumulative monthly ROI across all pillars.
  // Normalizes /an → /mois. Qualitative gains (× valeur perçue, × engagement) are skipped.
  const monthlyEUR = (gain) => {
    if (!gain || !gain.amount) return null;
    const m = gain.amount.match(/([+-]?)\s*([\d\s]+)\s*€/);
    if (!m) return null; // not a € figure
    const num = parseInt(m[2].replace(/\s/g, ''), 10);
    if (Number.isNaN(num)) return null;
    const period = (gain.period || '').toLowerCase();
    if (period.includes('/an') || period.includes(' an')) return Math.round(num / 12);
    return num;
  };
  const monthlyTotal = BRAND_ASSETS.reduce((acc, p) => acc + (monthlyEUR(p.gain) || 0), 0);
  const annualTotal = monthlyTotal * 12;
  const fmt = (n) => new Intl.NumberFormat('fr-FR').format(n);

  return (
    <div data-testid="programme-details">
      <SectionHeader
        overline="Détails du programme"
        title="Les 12 chantiers."
        subtitle="Voyez directement ce qu'on construit chez vous, chantier par chantier."
      />
      {/* Mobile swipe hint — animated arrow, hidden on desktop where chevrons show */}
      <div
        className="md:hidden flex items-center justify-center gap-1.5 mb-3 px-4"
        style={{
          fontFamily: theme.fontMono,
          fontSize: 9.5,
          letterSpacing: '0.24em',
          textTransform: 'uppercase',
          color: '#A87C4F',
          fontWeight: 700,
        }}
        data-testid="chantiers-swipe-hint"
      >
        <span aria-hidden style={{ animation: 'chantiers-swipe-nudge 1.8s ease-in-out infinite' }}>←</span>
        <span>Faites glisser</span>
        <span aria-hidden style={{ animation: 'chantiers-swipe-nudge 1.8s ease-in-out infinite' }}>→</span>
      </div>
      <style>{`
        @keyframes chantiers-swipe-nudge {
          0%, 100% { transform: translateX(0); opacity: 0.5; }
          50% { transform: translateX(-3px); opacity: 1; }
        }
      `}</style>
      <div className="relative">
        {/* Desktop-only chevron controls */}
        <button
          type="button"
          onClick={() => scrollBy(-1)}
          aria-label="Chantiers précédents"
          className="hidden md:flex items-center justify-center absolute z-20 top-1/2 -translate-y-1/2 left-2 transition-all active:scale-95"
          style={{
            width: 44, height: 44, borderRadius: 999,
            background: DA?.paper || '#FBF8F1',
            border: `0.5px solid ${DA?.goldSoft || 'rgba(216,148,64,0.35)'}`,
            boxShadow: '0 10px 24px -8px rgba(11,43,36,0.28), 0 0 0 4px rgba(255,255,255,0.65)',
            cursor: canScrollLeft ? 'pointer' : 'not-allowed',
            opacity: canScrollLeft ? 1 : 0.35,
            pointerEvents: canScrollLeft ? 'auto' : 'none',
            color: DA?.greenDeep || '#0B2B24',
          }}
          data-testid="chantiers-scroll-left"
        >
          <ChevronRight className="w-5 h-5 rotate-180" strokeWidth={2.4} />
        </button>
        <button
          type="button"
          onClick={() => scrollBy(1)}
          aria-label="Chantiers suivants"
          className="hidden md:flex items-center justify-center absolute z-20 top-1/2 -translate-y-1/2 right-2 transition-all active:scale-95"
          style={{
            width: 44, height: 44, borderRadius: 999,
            background: DA?.paper || '#FBF8F1',
            border: `0.5px solid ${DA?.goldSoft || 'rgba(216,148,64,0.35)'}`,
            boxShadow: '0 10px 24px -8px rgba(11,43,36,0.28), 0 0 0 4px rgba(255,255,255,0.65)',
            cursor: canScrollRight ? 'pointer' : 'not-allowed',
            opacity: canScrollRight ? 1 : 0.35,
            pointerEvents: canScrollRight ? 'auto' : 'none',
            color: DA?.greenDeep || '#0B2B24',
          }}
          data-testid="chantiers-scroll-right"
        >
          <ChevronRight className="w-5 h-5" strokeWidth={2.4} />
        </button>

        <div
          ref={scrollerRef}
          className="pl-4 mb-4 flex overflow-x-auto snap-x snap-mandatory gap-3 pb-4 scroll-smooth md:flex-row md:overflow-x-auto md:snap-x md:snap-mandatory md:gap-6 md:px-8 md:pb-6 md:scroll-smooth"
          style={{ scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch' }}
          data-testid="chantiers-scroller"
        >
          {BRAND_ASSETS.map((p) => {
            return (
              <div
                key={p.key}
                className="snap-start flex-shrink-0"
                style={{ scrollSnapAlign: 'start' }}
              >
                <div id={`pillar-${p.key}`} className="w-[85vw] max-w-[360px] md:w-[440px] md:max-w-none">
                  <ProgrammePillar data={p} monthlyEUR={monthlyEUR(p.gain)} />
                </div>
              </div>
            );
          })}
          {/* Trailing spacer so last card can snap flush-left with room to breathe */}
          <div aria-hidden className="flex-shrink-0 w-4 md:hidden" />
        </div>
      </div>
    </div>
  );
}

function ProgrammePillar({ data, monthlyEUR }) {
  const pal = theme.wallet[data.palette] || theme.wallet.graphite;
  const hasROI = typeof monthlyEUR === 'number' && monthlyEUR > 0;
  const fmt = (n) => new Intl.NumberFormat('fr-FR').format(n);
  return (
    <div
      className="relative overflow-hidden"
      style={{
        background: theme.bgPaper,
        borderRadius: 16,
        boxShadow: '0 0 0 0.5px rgba(60,60,67,0.10), 0 1px 2px rgba(0,0,0,0.03)',
      }}
      data-testid={`pillar-detail-${data.key}`}
    >
      <div className="flex items-center gap-3 px-4 py-3" style={{ borderBottom: `0.5px solid ${theme.hairline}` }}>
        <span
          className="inline-flex items-center justify-center flex-shrink-0 relative overflow-hidden"
          style={{
            width: 36, height: 36, borderRadius: 9,
            background: `linear-gradient(160deg, ${pal.from} 0%, ${pal.to} 100%)`,
            boxShadow: `0 4px 8px -2px ${pal.to}66`,
          }}
        >
          <span style={{ fontFamily: theme.fontMono, fontSize: 11, fontWeight: 800, color: '#FFFFFF', letterSpacing: '0.04em' }}>
            {data.num}
          </span>
        </span>
        <div className="flex-1 min-w-0">
          {data.when && (
            <p style={{ fontFamily: theme.fontMono, fontSize: 9, fontWeight: 700, color: theme.inkMuted, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 1 }}>
              {data.when}
            </p>
          )}
          <p style={{ fontFamily: theme.fontDisplay, fontSize: 14, fontWeight: 700, color: theme.inkDeep, letterSpacing: '-0.015em', lineHeight: 1.15 }}>
            {data.title}
          </p>
          <p style={{ fontFamily: theme.fontSans, fontSize: 11.5, color: theme.inkMuted, marginTop: 1, lineHeight: 1.3 }}>
            « {data.tagline} »
          </p>
        </div>
        {/* ROI mini-badge — only on quantified pillars (€). Qualitative ones get a softer "Quali." chip. */}
        {hasROI ? (
          <span
            className="flex-shrink-0 inline-flex items-center gap-1"
            style={{
              background: 'linear-gradient(135deg, #FCD34D 0%, #D4A74C 100%)',
              color: '#3D2A0A',
              padding: '5px 9px 5px 7px',
              borderRadius: 999,
              fontFamily: theme.fontMono,
              fontSize: 10.5,
              fontWeight: 900,
              letterSpacing: '-0.005em',
              boxShadow: '0 2px 6px rgba(212,167,76,0.45), inset 0 1px 0 rgba(255,255,255,0.55)',
              whiteSpace: 'nowrap',
            }}
            data-testid={`pillar-roi-badge-${data.key}`}
            aria-label={`ROI estimé : +${fmt(monthlyEUR)}€ par mois`}
          >
            <span aria-hidden style={{ fontSize: 11 }}>💰</span>
            +{fmt(monthlyEUR)}€/mo
          </span>
        ) : (
          <span
            className="flex-shrink-0 inline-flex items-center"
            style={{
              background: 'rgba(28,28,30,0.06)',
              color: theme.inkMuted,
              padding: '5px 8px',
              borderRadius: 999,
              fontFamily: theme.fontMono,
              fontSize: 9.5,
              fontWeight: 800,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
            data-testid={`pillar-roi-badge-${data.key}`}
          >
            Quali.
          </span>
        )}
      </div>

      <div className="px-4 py-4">
        {/* Hero photo — full visible, no crop */}
        {data.image && (
          <div
            className="relative mb-4"
            style={{
              borderRadius: 14,
              background: theme.bgSecondary,
              overflow: 'hidden',
              boxShadow: '0 6px 18px -6px rgba(0,0,0,0.18), 0 0 0 0.5px rgba(60,60,67,0.10)',
            }}
            data-testid={`pillar-image-${data.key}`}
          >
            {data.imageLink ? (
              <a
                href={data.imageLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'block' }}
                data-testid={`pillar-image-link-${data.key}`}
              >
                <img
                  src={data.image}
                  alt={data.imageCaption || data.title}
                  loading="lazy"
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
              </a>
            ) : (
              <img
                src={data.image}
                alt={data.imageCaption || data.title}
                loading="lazy"
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            )}
            {data.imageCaption && (
              <div
                className="absolute left-0 right-0 bottom-0"
                style={{
                  background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.62) 100%)',
                  padding: '24px 12px 10px',
                }}
              >
                <p style={{
                  fontFamily: theme.fontMono, fontSize: 9.5, letterSpacing: '0.14em', textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.95)', fontWeight: 700,
                }}>
                  {data.imageCaption}
                </p>
              </div>
            )}
          </div>
        )}

        {/* DA references — horizontal scrollable chips (Chantier 10 only) */}
        {Array.isArray(data.daReferences) && data.daReferences.length > 0 && (
          <div className="mb-4" data-testid={`pillar-da-refs-${data.key}`}>
            <p style={{ fontFamily: theme.fontMono, fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: theme.inkMuted, fontWeight: 700, marginBottom: 6 }}>
              Références DA · à étudier
            </p>
            <div
              className="flex gap-2 overflow-x-auto -mx-5 px-5 pb-1"
              style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}
            >
              {data.daReferences.map((ref, i) => (
                <a
                  key={i}
                  href={ref.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0"
                  style={{
                    scrollSnapAlign: 'start',
                    minWidth: 156, maxWidth: 156,
                    background: theme.bgSecondary,
                    borderRadius: 12,
                    padding: '10px 11px',
                    textDecoration: 'none',
                    boxShadow: '0 0 0 0.5px rgba(60,60,67,0.10)',
                  }}
                  data-testid={`da-ref-${ref.handle.replace('@', '')}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span style={{
                      fontFamily: theme.fontDisplay, fontSize: 12.5, fontWeight: 800,
                      color: theme.inkPrimary, letterSpacing: '-0.01em',
                    }}>
                      {ref.handle}
                    </span>
                    <span style={{
                      fontFamily: theme.fontMono, fontSize: 11, color: pal.to, fontWeight: 800,
                    }}>↗</span>
                  </div>
                  <p style={{
                    fontFamily: theme.fontMono, fontSize: 8.5, letterSpacing: '0.12em',
                    textTransform: 'uppercase', color: pal.to, fontWeight: 700, marginBottom: 4,
                  }}>
                    {ref.city}
                  </p>
                  <p style={{
                    fontFamily: theme.fontSans, fontSize: 10.5, color: theme.inkBody, lineHeight: 1.3,
                  }}>
                    {ref.note}
                  </p>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Why stats */}
        <p style={{ fontFamily: theme.fontMono, fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: theme.inkMuted, fontWeight: 700, marginBottom: 6 }}>
          Pourquoi en chiffres
        </p>
        <div className="grid grid-cols-2 gap-1.5 mb-3">
          {data.whyStats.map((s, i) => (
            <div key={i} style={{ background: theme.bgSecondary, borderRadius: 10, padding: '8px 10px' }}>
              <p style={{ fontFamily: theme.fontDisplay, fontSize: 16, fontWeight: 800, color: pal.to, letterSpacing: '-0.025em', lineHeight: 1 }}>
                {s.v}
              </p>
              <p style={{ fontFamily: theme.fontSans, fontSize: 10.5, color: theme.inkBody, marginTop: 3, lineHeight: 1.3 }}>
                {s.l}
              </p>
            </div>
          ))}
        </div>

        <p style={{ fontFamily: theme.fontMono, fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: theme.inkMuted, fontWeight: 700, marginBottom: 6 }}>
          Ce qu'on livre · 5 points
        </p>
        <ul className="space-y-1.5 mb-3">
          {data.deliverables.map((d, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <span
                className="flex-shrink-0 inline-flex items-center justify-center mt-0.5"
                style={{
                  width: 16, height: 16, borderRadius: 999,
                  background: pal.to, color: '#FFFFFF',
                  fontFamily: theme.fontMono, fontSize: 8.5, fontWeight: 800,
                }}
              >
                {idx + 1}
              </span>
              <span style={{ fontFamily: theme.fontSans, fontSize: 12, color: theme.inkBody, lineHeight: 1.35 }}>
                {d}
              </span>
            </li>
          ))}
        </ul>

        <div
          className="relative overflow-hidden mb-2.5"
          style={{
            background: `linear-gradient(160deg, ${pal.from} 0%, ${pal.to} 100%)`,
            borderRadius: 12, padding: 12, color: '#FFFFFF',
          }}
          data-testid={`pillar-transformation-${data.key}`}
        >
          <p style={{ fontFamily: theme.fontMono, fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.70)', fontWeight: 700, marginBottom: 6 }}>
            Transformation · {data.transformation.label}
          </p>
          <div className="flex items-center gap-2">
            <div>
              <p style={{ fontFamily: theme.fontMono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.60)', fontWeight: 700 }}>Avant</p>
              <p style={{ fontFamily: theme.fontDisplay, fontSize: 18, fontWeight: 800, color: 'rgba(255,255,255,0.85)', letterSpacing: '-0.02em', lineHeight: 1 }}>{data.transformation.from}</p>
            </div>
            <ArrowRight className="w-4 h-4 mx-1" style={{ color: 'rgba(255,255,255,0.80)' }} strokeWidth={2.4} />
            <div>
              <p style={{ fontFamily: theme.fontMono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.60)', fontWeight: 700 }}>Après</p>
              <p style={{ fontFamily: theme.fontDisplay, fontSize: 18, fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.02em', lineHeight: 1 }}>{data.transformation.to}</p>
            </div>
            <div className="flex-1" />
            <div className="text-right">
              <p style={{ fontFamily: theme.fontMono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.60)', fontWeight: 700 }}>Gain</p>
              <p style={{ fontFamily: theme.fontDisplay, fontSize: 16, fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.02em', lineHeight: 1 }}>{data.gain.amount}</p>
              <p style={{ fontFamily: theme.fontMono, fontSize: 8.5, color: 'rgba(255,255,255,0.70)', letterSpacing: '0.04em', fontWeight: 600 }}>{data.gain.period}</p>
            </div>
          </div>
          <p style={{ fontFamily: theme.fontSans, fontSize: 10.5, color: 'rgba(255,255,255,0.78)', marginTop: 6, lineHeight: 1.35 }}>
            {data.gain.basis}
          </p>
        </div>

        <div className="flex items-start gap-2.5" style={{ background: theme.bgSecondary, borderRadius: 12, padding: 10, border: `0.5px solid ${theme.hairline}` }}>
          <span className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 28, height: 28, borderRadius: 999, background: theme.inkDeep }}>
            <Zap className="w-3.5 h-3.5" style={{ color: '#FFFFFF' }} strokeWidth={2.4} />
          </span>
          <div className="flex-1 min-w-0">
            <p style={{ fontFamily: theme.fontMono, fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: theme.inkMuted, fontWeight: 700 }}>
              Objectif mesurable
            </p>
            <p style={{ fontFamily: theme.fontDisplay, fontSize: 16, fontWeight: 800, color: theme.inkDeep, letterSpacing: '-0.025em', lineHeight: 1.1 }}>
              {data.objective.metric}
            </p>
            <p style={{ fontFamily: theme.fontSans, fontSize: 11.5, color: theme.inkBody, marginTop: 1, lineHeight: 1.35 }}>
              {data.objective.label}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Addons — 2 native iOS cards (additional revenue)
// ─────────────────────────────────────────────────────────────────────
function Addons() {
  return (
    <div data-testid="addons">
      <SectionHeader
        overline="Options à la carte"
        title="Aller plus loin."
        subtitle="Si vous voulez ajouter un outil concret à votre accompagnement."
      />
      <style>{`
        @keyframes addon-shine {
          0%   { transform: translateX(-120%) skewX(-18deg); opacity: 0; }
          40%  { opacity: 0.45; }
          100% { transform: translateX(220%)  skewX(-18deg); opacity: 0; }
        }
      `}</style>
      <div className="px-4 mb-6 flex flex-col gap-3.5">
        {ADDONS.map((a, i) => {
          const Icon = a.icon;
          return (
            <article
              key={a.name}
              className="relative overflow-hidden"
              style={{
                background: theme.bgPaper,
                borderRadius: 20,
                border: '0.5px solid rgba(60,60,67,0.10)',
                boxShadow: `0 0 0 0.5px ${a.accent}22, 0 16px 36px -16px ${a.accent}55`,
              }}
              data-testid={`addon-${i}`}
            >
              {/* Hero band — gradient + icon + badge */}
              <div
                className="relative px-4 pt-3.5 pb-3"
                style={{
                  background: `linear-gradient(135deg, ${a.accent} 0%, ${a.accentDeep} 100%)`,
                  color: '#FFFFFF',
                }}
              >
                {/* Diagonal shine */}
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background: 'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.22) 50%, transparent 70%)',
                    animation: `addon-shine ${5.5 + i * 0.7}s ease-in-out ${i * 0.6}s infinite`,
                    mixBlendMode: 'overlay',
                  }}
                />
                <div className="relative flex items-center gap-2.5">
                  <span
                    className="inline-flex items-center justify-center flex-shrink-0"
                    style={{
                      width: 40, height: 40, borderRadius: 12,
                      background: 'rgba(255,255,255,0.18)',
                      border: '0.5px solid rgba(255,255,255,0.30)',
                      boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.30)',
                    }}
                  >
                    <Icon className="w-5 h-5" strokeWidth={2.2} style={{ color: '#FFFFFF' }} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p style={{
                      fontFamily: theme.fontMono,
                      fontSize: 9,
                      letterSpacing: '0.26em',
                      textTransform: 'uppercase',
                      color: 'rgba(255,255,255,0.82)',
                      fontWeight: 700,
                    }}>
                      Option · {a.tagline}
                    </p>
                    <p style={{
                      fontFamily: theme.fontDisplay,
                      fontSize: 15,
                      fontWeight: 800,
                      color: '#FFFFFF',
                      letterSpacing: '-0.022em',
                      lineHeight: 1.18,
                      marginTop: 2,
                    }}>
                      {a.name}
                    </p>
                  </div>
                  {/* Badge */}
                  <span
                    className="flex-shrink-0"
                    style={{
                      background: 'rgba(255,255,255,0.96)',
                      color: a.accentDeep,
                      borderRadius: 999,
                      padding: '3px 9px',
                      fontFamily: theme.fontMono,
                      fontSize: 8.5,
                      fontWeight: 800,
                      letterSpacing: '0.16em',
                      textTransform: 'uppercase',
                      boxShadow: '0 4px 10px -3px rgba(0,0,0,0.18)',
                    }}
                  >
                    {a.badge}
                  </span>
                </div>
              </div>

              {/* Body — desc + bullets + CTA */}
              <div className="px-4 pt-3 pb-3.5">
                <p style={{
                  fontFamily: theme.fontSans,
                  fontSize: 12.5,
                  color: theme.inkBody,
                  lineHeight: 1.45,
                }}>
                  {a.desc}
                </p>

                <ul className="mt-3 flex flex-col gap-1.5">
                  {a.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <span
                        className="inline-flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{
                          width: 15, height: 15, borderRadius: 999,
                          background: '#0095F6',
                          color: '#FFFFFF',
                          boxShadow: '0 1px 3px rgba(0,149,246,0.35)',
                        }}
                        aria-hidden
                      >
                        <Check className="w-2 h-2" strokeWidth={4} />
                      </span>
                      <span style={{
                        fontFamily: theme.fontSans,
                        fontSize: 12,
                        color: theme.inkBody,
                        lineHeight: 1.45,
                        letterSpacing: '-0.005em',
                      }}>
                        {b}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Footer · CTA + meta */}
                <div className="flex items-center justify-between gap-2 mt-3.5">
                  <button
                    type="button"
                    onClick={() => openSabril(`addon-${a.badge.toLowerCase()}`)}
                    className="inline-flex items-center gap-1.5 active:scale-[0.95] transition-transform"
                    style={{
                      background: `linear-gradient(180deg, ${a.accent} 0%, ${a.accentDeep} 100%)`,
                      color: '#FFFFFF',
                      borderRadius: 999,
                      padding: '8px 13px 8px 11px',
                      fontFamily: theme.fontSans,
                      fontSize: 12,
                      fontWeight: 800,
                      letterSpacing: '-0.005em',
                      boxShadow: `0 6px 14px -4px ${a.accent}77`,
                    }}
                    data-testid={`addon-cta-${i}`}
                  >
                    <Plus className="w-3.5 h-3.5" strokeWidth={2.6} />
                    Ajouter à mon plan
                  </button>
                  <span style={{
                    fontFamily: theme.fontMono,
                    fontSize: 9,
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: theme.inkMuted,
                    fontWeight: 700,
                  }}>
                    Tarif en privé
                  </span>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Presse — Media references horizontal scroll
// ─────────────────────────────────────────────────────────────────────
function Presse() {
  return (
    <div data-testid="presse">
      <SectionHeader overline="On en a parlé" title="Vu dans la presse." />
      <div className="overflow-x-auto px-5 pb-3 no-scrollbar">
        <div className="flex items-center gap-3" style={{ minWidth: 'min-content' }}>
          {MEDIA_REFS.map((m, i) => (
            <div
              key={m}
              className="flex-shrink-0 flex items-center justify-center"
              style={{
                background: theme.bgPaper,
                padding: '10px 16px',
                borderRadius: 12,
                boxShadow: '0 0 0 0.5px rgba(60,60,67,0.10), 0 1px 2px rgba(0,0,0,0.03)',
              }}
              data-testid={`media-${i}`}
            >
              <span
                style={{
                  fontFamily: theme.fontDisplay,
                  fontSize: 13,
                  fontStyle: 'italic',
                  fontWeight: 600,
                  color: theme.inkBody,
                  letterSpacing: '-0.005em',
                  whiteSpace: 'nowrap',
                }}
              >
                {m}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// HERO — Insta-bio style on WHITE, single CTA to bot, scarcity badge
// ─────────────────────────────────────────────────────────────────────
function Hero() {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [remaining, setRemaining] = useState(3); // 3 places restantes baseline

  useEffect(() => {
    let mounted = true;
    const baselines = { Miami: 2, Marbella: 2, Cannes: 1, Marrakech: 5 };
    const slots = { Miami: 3, Marbella: 2, Cannes: 2, Marrakech: 6 };
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API_URL}/api/sabril/live-stats`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (!mounted) return;
        const cb = data.city_bookings || {};
        let totalTaken = 0;
        let totalSlots = 0;
        Object.keys(slots).forEach((city) => {
          totalSlots += slots[city];
          totalTaken += Math.min(slots[city], (baselines[city] || 0) + (cb[city] || 0));
        });
        setRemaining(Math.max(0, totalSlots - totalTaken));
      } catch (e) { /* silent */ }
    };
    fetchStats();
    const poll = setInterval(fetchStats, 20000);
    return () => { mounted = false; clearInterval(poll); };
  }, [API_URL]);

  const taken = 13 - remaining;
  const scarcityTint = remaining === 0 ? '#FF3B30' : remaining <= 3 ? '#FE8C00' : '#34C759';

  return (
    <div className="px-5 pt-3 pb-5" data-testid="hero">
      {/* Insta-bio header — photo + name verified + meta */}
      <div className="flex items-start gap-4 mb-4">
        {/* Profile photo — large, Insta-style */}
        <div className="relative shrink-0" data-testid="hero-sabril-avatar">
          <div
            className="p-[2px] rounded-full"
            style={{
              background: 'linear-gradient(135deg, #FE8C00 0%, #C9A87A 50%, #FE8C00 100%)',
            }}
          >
            <img
              src={SABRIL_AVATAR}
              alt="Sabril Jurczeszyn"
              style={{
                width: 84,
                height: 84,
                borderRadius: 999,
                objectFit: 'cover',
                border: '2px solid #FFFFFF',
                display: 'block',
              }}
            />
          </div>
          {/* Verified blue checkmark */}
          <span
            className="absolute bottom-0.5 right-0.5 flex items-center justify-center"
            style={{
              width: 22,
              height: 22,
              borderRadius: 999,
              background: '#1DA1F2',
              border: '2.5px solid #FFFFFF',
              boxShadow: '0 2px 6px rgba(29,161,242,0.30)',
            }}
            aria-label="Vérifié"
          >
            <Check className="w-3 h-3" strokeWidth={4} style={{ color: '#FFFFFF' }} />
          </span>
        </div>

        {/* Name + handle + meta */}
        <div className="flex-1 min-w-0 pt-1">
          <p
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 20,
              fontWeight: 800,
              color: theme.inkDeep,
              letterSpacing: '-0.02em',
              lineHeight: 1.05,
            }}
          >
            Sabril Jurczeszyn
          </p>
          <p
            style={{
              fontFamily: theme.fontMono,
              fontSize: 9.5,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: theme.inkMuted,
              fontWeight: 700,
              marginTop: 4,
            }}
          >
            Direction de marque · 2018 → ∞
          </p>
          {/* Scarcity inline */}
          <span
            className="inline-flex items-center gap-1.5 mt-3"
            style={{
              background: `${scarcityTint}12`,
              color: scarcityTint,
              border: `0.5px solid ${scarcityTint}55`,
              borderRadius: 999,
              padding: '4px 10px',
              fontFamily: theme.fontMono,
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
            }}
            data-testid="hero-scarcity-badge"
          >
            <span style={{ width: 6, height: 6, borderRadius: 999, background: scarcityTint, boxShadow: `0 0 0 3px ${scarcityTint}25` }} />
            {remaining === 0
              ? 'COMPLET · liste d\'attente'
              : `${remaining} ${remaining > 1 ? 'places restantes' : 'place restante'} sur sélection`}
          </span>
        </div>
      </div>

      {/* Bio block — Instagram-bio style: short, punchy */}
      <div className="mb-5">
        <p
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 15,
            fontWeight: 700,
            color: theme.inkDeep,
            letterSpacing: '-0.01em',
            lineHeight: 1.35,
            marginBottom: 8,
          }}
        >
          Je transforme des restos en marques.
        </p>
        <p
          style={{
            fontFamily: theme.fontSans,
            fontSize: 13.5,
            color: theme.inkBody,
            lineHeight: 1.5,
          }}
        >
          {taken} clients que j'accompagne actuellement · {remaining} {remaining > 1 ? 'places restantes' : 'place restante'}.{' '}
          <span style={{ color: theme.inkDeep, fontWeight: 600 }}>
            Rentables. Transmissibles.
          </span>
          {' '}Cercle privé sur sélection.
        </p>
      </div>

      {/* Funnel hint — what happens when they tap */}
      <div
        className="flex items-center gap-3 mb-5"
        style={{
          background: '#F7F7F2',
          borderRadius: 14,
          padding: '10px 12px',
          border: '0.5px solid rgba(60,60,67,0.10)',
        }}
        data-testid="hero-funnel-hint"
      >
        <span
          className="shrink-0 flex items-center justify-center"
          style={{
            width: 26, height: 26, borderRadius: 999,
            background: 'linear-gradient(135deg, #FE8C00 0%, #F05A1A 100%)',
            color: '#FFFFFF',
            boxShadow: '0 4px 10px -4px rgba(254,140,0,0.45)',
          }}
        >
          <MessageCircle className="w-3.5 h-3.5" strokeWidth={2.6} />
        </span>
        <div className="flex-1 min-w-0">
          <p style={{ fontFamily: theme.fontMono, fontSize: 8.5, color: theme.inkMuted, letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700 }}>
            Comment ça se passe
          </p>
          <p style={{ fontFamily: theme.fontSans, fontSize: 12, color: theme.inkBody, marginTop: 2, lineHeight: 1.4 }}>
            <span style={{ color: theme.inkDeep, fontWeight: 700 }}>Vous discutez avec Sabril</span> → il vous file le prix → si ça matche, on cale un café.
          </p>
        </div>
      </div>

      {/* PRIMARY CTA — single button to bot */}
      <button
        type="button"
        onClick={() => openSabril('question')}
        className="active:scale-[0.97] transition-transform inline-flex items-center justify-center gap-2 w-full"
        style={{
          background: 'linear-gradient(180deg, #0A0A0F 0%, #1A1A1F 100%)',
          color: '#FFFFFF',
          padding: '14px 18px',
          borderRadius: 999,
          fontFamily: theme.fontDisplay,
          fontSize: 14.5,
          fontWeight: 700,
          letterSpacing: '-0.005em',
          boxShadow: '0 14px 30px -10px rgba(10,10,15,0.35)',
        }}
        data-testid="hero-cta-sabril"
      >
        <MessageCircle className="w-4 h-4" strokeWidth={2.4} />
        Discuter avec Sabril
      </button>

      <p
        className="text-center mt-3"
        style={{
          fontFamily: theme.fontMono,
          fontSize: 9,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: theme.inkMuted,
          fontWeight: 600,
        }}
      >
        ⏱  Réponse en moy. en 4 min · 5 à 10 min de qualif
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Wallet Card — compact native iOS Wallet card
// ─────────────────────────────────────────────────────────────────────
function WalletCard({ offer, isOpen, onToggle, onAction }) {
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const cardRef = useRef(null);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  // Lock Étape 2 (configurer) tant que le code d'accès n'a pas été saisi
  const isLocked = offer.ctaAction === 'configurer-open' && !isSalonPriveUnlocked();

  const handleMouseMove = (e) => {
    const el = cardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    const rx = (0.5 - py) * 8;   // -4° to 4°
    const ry = (px - 0.5) * 10;  // -5° to 5°
    el.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) scale(1.015)`;
    el.style.transition = 'transform 0.1s linear';
    const sheen = el.querySelector('[data-sheen]');
    if (sheen) {
      sheen.style.backgroundPosition = `${px * 100}% ${py * 100}%`;
      sheen.style.opacity = '1';
    }
  };

  const handleMouseLeave = () => {
    const el = cardRef.current;
    if (!el) return;
    el.style.transform = 'perspective(900px) rotateX(0) rotateY(0) scale(1)';
    el.style.transition = 'transform 0.55s cubic-bezier(0.32, 0.72, 0, 1)';
    const sheen = el.querySelector('[data-sheen]');
    if (sheen) sheen.style.opacity = '0.55';
  };

  const handleAction = async () => {
    if (offer.ctaAction === 'ebook-checkout') {
      // Land on the ebook portal — user can enter their code (SABRIL2026 / customer code)
      // OR trigger the Stripe checkout from there.
      window.location.href = '/ebook';
      return;
    }
    if (offer.ctaAction === 'configurer-open') {
      // Direct to the configurator page
      navigate('/configurer');
      return;
    }
    if (offer.ctaAction === 'economie-checkout') {
      // Open the Économie form modal — collects lead info then triggers Stripe.
      window.dispatchEvent(new CustomEvent('economie:open'));
      return;
    }
    if (offer.ctaAction === 'affaires-quote') {
      // Custom quote request for Classe Affaires — sur-mesure, pas de paiement direct.
      window.dispatchEvent(new CustomEvent('affaires:open'));
      return;
    }
    if (offer.ctaAction === 'premiere-quote') {
      // Café de calibrage obligatoire pour Première Classe.
      window.dispatchEvent(new CustomEvent('premiere:open'));
      return;
    }
    onAction();
  };

  const FORMAT_LABEL = {
    ebook: 'PDF · auto-géré',
    configurer: 'Sur-mesure · 100% flexible',
    economie: '48h sur place + 90j suivi',
    affaires: '100% distanciel · 90 jours',
    premiere: '17j immersion + 90j suivi',
    salon: 'Mensualité · sans engagement',
  }[offer.key] || '';

  return (
    <div
      id={offer.key === 'ebook' ? 'ebook-card' : undefined}
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={(e) => {
        // Ignore clicks on the CTA button (handles its own action)
        if (e.target.closest('[data-cta-btn="true"]')) return;
        if (offer.ctaAction === 'configurer-open') {
          navigate('/configurer');
        } else {
          navigate(`/offre/${offer.key}`);
        }
      }}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !e.target.closest('[data-cta-btn="true"]')) {
          e.preventDefault();
          if (offer.ctaAction === 'configurer-open') {
            navigate('/configurer');
          } else {
            navigate(`/offre/${offer.key}`);
          }
        }
      }}
      className={`relative h-full flex flex-col cursor-pointer ${offer.featured ? 'wallet-card-pulse' : ''}`}
      style={{
        background: offer.cardBg,
        borderRadius: 22,
        padding: '22px 18px 18px',
        boxShadow: `${offer.cardShadow}, 0 2px 0 0 rgba(255,255,255,0.10) inset, 0 -2px 0 0 rgba(0,0,0,0.40) inset`,
        overflow: 'hidden',
        transformStyle: 'preserve-3d',
        willChange: 'transform',
      }}
      data-testid={`offer-${offer.key}`}
    >
      {/* Sabril DA decorative frame — mimosa + awning + palm inside each card */}
      <SabrilDecorFrame awningTL awningBR mimosa palm={false} awningSize={70} mimosaSize={80} />

      {/* AMEX-style top highlight — glass sheen (subtle on ivory) */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 pointer-events-none"
        style={{
          height: '38%',
          background: 'linear-gradient(180deg, rgba(216,148,64,0.06) 0%, rgba(216,148,64,0.02) 50%, transparent 100%)',
        }}
      />
      {/* HOLOGRAPHIC FOIL — replaced by gentle gold shimmer on ivory */}
      <div
        aria-hidden
        data-holo
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            linear-gradient(115deg,
              transparent 0%,
              rgba(216,148,64,0.06) 30%,
              rgba(216,148,64,0.10) 50%,
              rgba(216,148,64,0.06) 70%,
              transparent 100%
            )
          `,
          backgroundSize: '220% 220%',
          backgroundPosition: '0% 50%',
          mixBlendMode: 'overlay',
          opacity: 0.4,
          animation: `wallet-holo-shift 7s ease-in-out infinite`,
        }}
      />
      {/* Watermark — embossed "S" signature behind content — GOLD on ivory */}
      <div
        aria-hidden
        className="absolute pointer-events-none select-none"
        style={{
          right: -10,
          bottom: -28,
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontStyle: 'italic',
          fontWeight: 900,
          fontSize: 220,
          lineHeight: 1,
          color: 'rgba(216,148,64,0.08)',
          letterSpacing: '-0.06em',
          textShadow: `0 1px 0 rgba(216,148,64,0.04), 0 0 30px rgba(216,148,64,0.15)`,
        }}
      >S</div>
      {/* Diagonal sheen — moves with cursor */}
      <div
        aria-hidden
        data-sheen
        className="absolute pointer-events-none transition-opacity duration-300"
        style={{
          inset: 0,
          background: `radial-gradient(circle at 50% 50%, ${offer.accent}28 0%, transparent 55%)`,
          backgroundSize: '180% 180%',
          backgroundPosition: '50% 50%',
          opacity: 0.55,
          mixBlendMode: 'screen',
        }}
      />
      {/* Subtle grain texture */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.65) 0.6px, transparent 0.6px)',
          backgroundSize: '4px 4px',
          mixBlendMode: 'soft-light',
        }}
      />
      {/* Premium halo glow — featured card only */}
      {offer.featured && (
        <div
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            inset: -2,
            borderRadius: 24,
            background: `conic-gradient(from 0deg, ${offer.accent}AA, ${offer.accent}33, ${offer.accent}AA, ${offer.accent}33, ${offer.accent}AA)`,
            filter: 'blur(14px)',
            opacity: 0.55,
            zIndex: 0,
            animation: 'wallet-halo-spin 6s linear infinite',
          }}
        />
      )}

      <div className="relative z-10 flex flex-col h-full">
        {/* ÉTAPE ribbon — big editorial marker on top of the card */}
        {offer.step && (
          <div
            className="absolute"
            style={{
              top: -18, left: 12,
              background: offer.accent,
              color: '#FFFFFF',
              padding: '5px 12px 5px 10px',
              borderRadius: 999,
              fontFamily: theme.fontMono,
              fontSize: 9.5,
              fontWeight: 900,
              letterSpacing: '0.20em',
              textTransform: 'uppercase',
              boxShadow: `0 8px 20px -6px ${offer.accent}AA, 0 0 0 2px rgba(255,255,255,0.85)`,
              zIndex: 6,
              display: 'inline-flex', alignItems: 'center', gap: 6,
              whiteSpace: 'nowrap',
            }}
            data-testid={`offer-step-ribbon-${offer.key}`}
          >
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 18, height: 18, borderRadius: 999,
              background: '#FFFFFF', color: offer.accent,
              fontFamily: theme.fontDisplay, fontSize: 11, fontWeight: 900,
              letterSpacing: '-0.02em',
            }}>{offer.step}</span>
            {offer.stepLabel.replace(/^ÉTAPE \d\s*·\s*/, '')}
          </div>
        )}
        {/* Top row : pill format + "card chip" mark */}
        <div className="flex items-center justify-between mb-3">
          <span style={{
            background: 'rgba(255,255,255,0.10)',
            color: offer.accent,
            border: `0.5px solid ${offer.accent}55`,
            borderRadius: 999,
            padding: '4px 10px',
            fontFamily: theme.fontMono, fontSize: 9, letterSpacing: '0.14em',
            textTransform: 'uppercase', fontWeight: 800, whiteSpace: 'nowrap',
            backdropFilter: 'blur(10px)',
          }}>
            {offer.pill}
          </span>
          {/* Verrouillé chip (Étape 2 locked) — replaces the AMEX chip when locked */}
          {isLocked ? (
            <div
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                background: `linear-gradient(135deg, ${offer.accent}22 0%, ${offer.accent}66 100%)`,
                color: offer.accent,
                border: `0.5px solid ${offer.accent}88`,
                borderRadius: 999,
                padding: '5px 9px 5px 8px',
                fontFamily: theme.fontMono, fontSize: 8.5, letterSpacing: '0.20em',
                textTransform: 'uppercase', fontWeight: 900, whiteSpace: 'nowrap',
                boxShadow: `0 6px 16px -6px ${offer.accent}77, inset 0 1px 0 rgba(255,255,255,0.18)`,
              }}
              data-testid="offer-lock-chip"
            >
              <Lock size={11} strokeWidth={2.6} />
              <span>Verrouillé</span>
            </div>
          ) : (
            <div
              style={{
                width: 32, height: 24, borderRadius: 5,
                background: `linear-gradient(135deg, ${offer.accent}55 0%, ${offer.accent}22 50%, ${offer.accent}88 100%)`,
                border: `0.5px solid ${offer.accent}55`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.18), inset 0 -1px 0 0 rgba(0,0,0,0.25)',
              }}
            >
              <div style={{
                width: 22, height: 16, borderRadius: 3,
                background: `linear-gradient(135deg, ${offer.accent}AA 0%, ${offer.accent}40 100%)`,
                border: `0.5px solid ${offer.accent}33`,
                display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridTemplateRows: 'repeat(2, 1fr)',
                gap: 1.5, padding: 2,
              }}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} style={{ background: `${offer.accent}66`, borderRadius: 0.5 }} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Featured ribbon (centered) */}
        {offer.featured && (
          <div className="mb-2 flex justify-center">
            <span style={{
              fontFamily: theme.fontMono, fontSize: 8.5, fontWeight: 800,
              letterSpacing: '0.20em', color: offer.accent,
              padding: '3px 10px',
              background: `linear-gradient(135deg, ${offer.accent}25 0%, ${offer.accent}10 100%)`,
              border: `0.5px solid ${offer.accent}55`,
              borderRadius: 999,
              boxShadow: `0 0 14px ${offer.accent}40`,
            }}>
              ★ OFFRE SIGNATURE
            </span>
          </div>
        )}

        {/* Title — embossed */}
        <h3 style={{
          fontFamily: theme.fontDisplay,
          fontSize: offer.short.length > 26 ? 15 : offer.short.length > 14 ? 19 : 22,
          fontWeight: 900,
          lineHeight: 1.05,
          letterSpacing: '-0.03em',
          color: '#0B2B24',
          textAlign: 'center',
          marginBottom: 4,
          textShadow: '0 1px 0 rgba(0,0,0,0.55), 0 0 18px rgba(0,0,0,0.30)',
        }}>
          {offer.short}
        </h3>
        {/* Cover image — book preview with 3D tilt on hover */}
        {offer.coverImage && (
          <div
            className="mx-auto mb-3 ebook-cover-wrap"
            style={{
              width: '76%', maxWidth: 220,
              perspective: '1200px',
            }}
          >
            <div
              className="ebook-cover-tilt"
              style={{
                aspectRatio: '1489 / 2106',
                borderRadius: 8,
                overflow: 'hidden',
                position: 'relative',
                backgroundImage: `url(${offer.coverImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundColor: DA.paper,
                boxShadow: '0 18px 32px -14px rgba(11,43,36,0.35), 0 0 0 0.5px rgba(216,148,64,0.35), 0 2px 4px rgba(0,0,0,0.10)',
                transform: 'rotateY(0deg) rotateX(0deg)',
                transformStyle: 'preserve-3d',
                transition: 'transform 480ms cubic-bezier(0.32, 0.72, 0, 1), box-shadow 480ms ease-out',
                willChange: 'transform',
              }}
              role="img"
              aria-label={`Couverture · ${offer.short}`}
              data-testid={`ebook-cover-${offer.key}`}
            >
              {/* Spine highlight — vertical gradient on the left edge to simulate book depth */}
              <span
                aria-hidden
                style={{
                  position: 'absolute', inset: '0 auto 0 0', width: 12,
                  background: 'linear-gradient(90deg, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0.05) 60%, transparent 100%)',
                  pointerEvents: 'none',
                }}
              />
              {/* Page-edge gold reflection on the right */}
              <span
                aria-hidden
                style={{
                  position: 'absolute', inset: '0 0 0 auto', width: 6,
                  background: 'linear-gradient(270deg, rgba(255,255,255,0.55) 0%, rgba(216,148,64,0.30) 60%, transparent 100%)',
                  pointerEvents: 'none',
                }}
              />
              {/* Glossy top-light shine */}
              <span
                aria-hidden
                className="ebook-cover-shine"
                style={{
                  position: 'absolute', top: 0, left: '-30%', width: '60%', height: '100%',
                  background: 'linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.35) 50%, transparent 70%)',
                  pointerEvents: 'none',
                  transform: 'translateX(-100%)',
                  transition: 'transform 900ms cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              />
            </div>
            {/* Feuilleter — Apple Books "Look Inside" preview button */}
            {offer.key === 'ebook' && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShowPreview(true); }}
                data-testid="ebook-feuilleter-btn"
                className="mx-auto mt-3 flex items-center justify-center gap-1.5"
                style={{
                  display: 'flex',
                  background: 'rgba(11,43,36,0.06)',
                  border: '0.5px solid rgba(216,148,64,0.45)',
                  color: '#0B2B24',
                  padding: '7px 14px',
                  borderRadius: 999,
                  fontFamily: theme.fontMono,
                  fontSize: 9.5,
                  fontWeight: 800,
                  letterSpacing: '0.20em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  transition: 'background 220ms ease, transform 220ms ease',
                  backdropFilter: 'blur(6px)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(216,148,64,0.14)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(11,43,36,0.06)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                </svg>
                Feuilleter
              </button>
            )}
          </div>
        )}
        {/* Tagline */}
        <p style={{
          fontFamily: theme.fontMono, fontSize: 9, letterSpacing: '0.22em',
          textTransform: 'uppercase', color: offer.accent, fontWeight: 800,
          textAlign: 'center', marginBottom: 14,
          opacity: 0.85,
        }}>
          {offer.tagline}
        </p>

        {/* PRICE — clean iOS hero (no gradient clip, tabular nums) — hidden when no priceFrom */}
        {offer.priceFrom ? (
        <div className="flex flex-col items-center mb-4 pb-4" style={{ borderBottom: `0.5px solid ${offer.accent}25` }}>
          <p style={{
            fontFamily: theme.fontMono, fontSize: 8.5, letterSpacing: '0.24em',
            textTransform: 'uppercase', color: 'rgba(216,148,64,0.75)', fontWeight: 700,
            marginBottom: 6,
          }}>
            Investissement
          </p>
          {(() => {
            // Split "à partir de 14 900€" → label "à partir de" + number "14 900€"
            const match = offer.priceFrom.match(/^(à partir de)\s+(.+)$/i);
            const prefix = match ? 'À partir de' : null;
            const number = match ? match[2] : offer.priceFrom;
            return (
              <>
                {prefix && (
                  <p style={{
                    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
                    fontSize: 11,
                    fontWeight: 500,
                    letterSpacing: '0.01em',
                    color: 'rgba(11,43,36,0.62)',
                    marginBottom: 2,
                  }}>
                    {prefix}
                  </p>
                )}
                <p style={{
                  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
                  fontSize: 36,
                  fontWeight: 700,
                  letterSpacing: '-0.038em',
                  color: '#0B2B24',
                  lineHeight: 1,
                  fontVariantNumeric: 'tabular-nums',
                  textShadow: `0 0 28px ${offer.accent}50`,
                  marginBottom: 4,
                }}>
                  {number}
                </p>
              </>
            );
          })()}
          <p style={{
            fontFamily: theme.fontMono, fontSize: 8.5, letterSpacing: '0.16em',
            textTransform: 'uppercase', color: offer.accent, fontWeight: 700,
            opacity: 0.85, marginTop: 4,
          }}>
            {offer.priceSub}
          </p>
        </div>
        ) : (
          <div className="mb-4 pb-4" style={{ borderBottom: `0.5px solid ${offer.accent}25` }}>
            {offer.priceSub && (
              <p style={{
                fontFamily: theme.fontMono, fontSize: 9.5, letterSpacing: '0.16em',
                textTransform: 'uppercase', color: offer.accent, fontWeight: 700,
                opacity: 0.85, textAlign: 'center',
              }}>
                {offer.priceSub}
              </p>
            )}
          </div>
        )}

        {/* Rows */}
        <div className="flex-1 space-y-3">
          {/* Pour qui */}
          <div>
            <p style={{
              fontFamily: theme.fontMono, fontSize: 9.5, letterSpacing: '0.20em',
              textTransform: 'uppercase', color: DA.greenDeep, fontWeight: 800, marginBottom: 6,
              opacity: 0.85,
            }}>
              👤 Pour qui
            </p>
            {Array.isArray(offer.forWho) ? (
              <ul className="space-y-1.5" style={{ marginTop: 2 }}>
                {offer.forWho.map((line, i) => (
                  <li key={line} className="flex items-start gap-2">
                    <span
                      className="wallet-check inline-flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{
                        width: 16, height: 16, borderRadius: 999,
                        background: '#0095F6',
                        boxShadow: '0 1px 3px rgba(0,149,246,0.45), 0 0 0 1px rgba(255,255,255,0.20)',
                        animationDelay: `${i * 60}ms`,
                      }}
                      aria-hidden
                    >
                      <Check className="w-2.5 h-2.5" strokeWidth={4} style={{ color: '#FFFFFF' }} />
                    </span>
                    <span style={{
                      fontFamily: theme.fontSans, fontSize: 12, lineHeight: 1.35,
                      color: 'rgba(11,43,36,0.92)', fontWeight: 600,
                    }}>
                      {line}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{
                fontFamily: theme.fontSans, fontSize: 12.5, lineHeight: 1.35,
                color: 'rgba(11,43,36,0.92)', fontWeight: 600,
              }}>
                {offer.forWho}
              </p>
            )}
          </div>

          {/* Format */}
          <div style={{ borderTop: '0.5px solid rgba(255,255,255,0.10)', paddingTop: 10 }}>
            <p style={{
              fontFamily: theme.fontMono, fontSize: 8.5, letterSpacing: '0.18em',
              textTransform: 'uppercase', color: 'rgba(216,148,64,0.72)', fontWeight: 700, marginBottom: 3,
            }}>
              Format
            </p>
            <p style={{
              fontFamily: theme.fontSans, fontSize: 12, lineHeight: 1.35,
              color: 'rgba(11,43,36,0.80)',
            }}>
              {FORMAT_LABEL}
            </p>
          </div>

          {/* Disponible — only when offer has an `available` list (Configurateur) */}
          {Array.isArray(offer.available) && offer.available.length > 0 && (
            <div style={{ borderTop: '0.5px solid rgba(255,255,255,0.10)', paddingTop: 10 }}>
              <p style={{
                fontFamily: theme.fontMono, fontSize: 9.5, letterSpacing: '0.20em',
                textTransform: 'uppercase', color: DA.greenDeep, fontWeight: 800, marginBottom: 6,
                opacity: 0.85,
              }}>
                📌 Disponible
              </p>
              <ul className="space-y-1.5">
                {offer.available.map((it, i) => (
                  <li key={it.label} className="flex items-center gap-2">
                    <span
                      className="wallet-check inline-flex items-center justify-center flex-shrink-0"
                      style={{
                        width: 16, height: 16, borderRadius: 999,
                        background: '#0095F6',
                        boxShadow: '0 1px 3px rgba(0,149,246,0.45), 0 0 0 1px rgba(255,255,255,0.20)',
                        animationDelay: `${300 + i * 60}ms`,
                      }}
                      aria-hidden
                    >
                      <Check className="w-2.5 h-2.5" strokeWidth={4} style={{ color: '#FFFFFF' }} />
                    </span>
                    <span style={{ fontSize: 14, lineHeight: 1, flexShrink: 0 }}>{it.emoji}</span>
                    <span style={{
                      fontFamily: theme.fontSans, fontSize: 12.5, fontWeight: 600,
                      color: 'rgba(11,43,36,0.92)', lineHeight: 1.35,
                    }}>
                      {it.label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Inclus */}
          <div style={{ borderTop: '0.5px solid rgba(255,255,255,0.10)', paddingTop: 10 }}>
            <p style={{
              fontFamily: theme.fontMono, fontSize: 9.5, letterSpacing: '0.20em',
              textTransform: 'uppercase', color: DA.greenDeep, fontWeight: 800, marginBottom: 6,
              opacity: 0.85,
            }}>
              ✔ Ce que vous allez faire
            </p>
            <ul className="space-y-1">
              {(isOpen ? offer.bullets : offer.bullets.slice(0, 3)).map((b, i) => (
                <li key={b} className="flex items-start gap-2">
                  <span
                    className="wallet-check inline-flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{
                      width: 16, height: 16, borderRadius: 999,
                      background: '#0095F6',
                      boxShadow: '0 1px 3px rgba(0,149,246,0.45), 0 0 0 1px rgba(255,255,255,0.20)',
                      animationDelay: `${480 + i * 60}ms`,
                    }}
                    aria-hidden
                  >
                    <Check className="w-2.5 h-2.5" strokeWidth={4} style={{ color: '#FFFFFF' }} />
                  </span>
                  <span style={{
                    fontFamily: theme.fontSans, fontSize: 12, fontWeight: 600,
                    color: 'rgba(11,43,36,0.92)', lineHeight: 1.35,
                  }}>
                    {b}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* CTA — embossed button */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); handleAction(); }}
          disabled={loading}
          data-cta-btn="true"
          className={`w-full mt-4 active:scale-[0.98] transition-all disabled:opacity-60 flex items-center justify-center gap-1.5${offer.ctaAction === 'configurer-open' ? ' wallet-cta-gold-pulse' : ''}`}
          style={{
            background: offer.ctaAction === 'configurer-open'
              ? 'linear-gradient(135deg, #D89440 0%, #B8792F 100%)'
              : DA.greenDeep,
            color: '#FFFFFF', borderRadius: 12, padding: '13px',
            fontFamily: theme.fontSans, fontSize: 12, fontWeight: 700,
            letterSpacing: '0.14em', textTransform: 'uppercase',
            boxShadow: offer.ctaAction === 'configurer-open'
              ? '0 10px 24px -8px rgba(11,43,36,0.55), 0 0 0 0.5px rgba(216,148,64,0.55), inset 0 1px 0 0 rgba(255,214,120,0.35)'
              : `0 10px 24px -8px ${DA.greenDeep}88, inset 0 1px 0 0 rgba(216,148,64,0.20)`,
            border: 'none',
          }}
          data-testid={`offer-cta-${offer.key}`}
        >
          {loading ? (
            'Redirection Stripe…'
          ) : offer.ctaAction === 'ebook-checkout' ? (
            <>🚀 Accéder au Guide · {offer.priceFrom}</>
          ) : offer.ctaAction === 'economie-checkout' ? (
            <>✈️ Réserver dès 2 000€</>
          ) : offer.ctaAction === 'affaires-quote' ? (
            <>📩 Demander mon devis</>
          ) : offer.ctaAction === 'premiere-quote' ? (
            <>📞 Café de calibrage</>
          ) : offer.ctaAction === 'configurer-open' ? (
            isLocked ? (
              <><Lock className="w-4 h-4" strokeWidth={2.4} /> Débloquer avec mon code</>
            ) : (
              <>🚀 Commencer mon accompagnement</>
            )
          ) : (
            <><MessageCircle className="w-4 h-4" strokeWidth={2.4} /> Discuter</>
          )}
        </button>

        {/* Secondary CTA — Signer mon contrat (economie + affaires only) */}
        {(offer.key === 'economie' || offer.key === 'affaires') && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              window.dispatchEvent(new CustomEvent('contract:open', { detail: { offer: offer.key } }));
            }}
            data-cta-btn="true"
            className="w-full mt-2 active:scale-[0.98] transition flex items-center justify-center gap-1.5"
            style={{
              background: 'rgba(216,148,64,0.08)',
              color: '#0B2B24',
              borderRadius: 12,
              padding: '10px',
              border: `0.5px solid ${offer.accent}55`,
              fontFamily: theme.fontDisplay,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '-0.005em',
              backdropFilter: 'blur(8px)',
            }}
            data-testid={`offer-sign-${offer.key}`}
          >
            📝 Signer mon contrat
          </button>
        )}
      </div>
      {offer.key === 'ebook' && (
        <EbookPreviewLightbox
          open={showPreview}
          onClose={() => setShowPreview(false)}
          onBuy={onAction}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────────
// COMMENT ÇA FONCTIONNE — Section principale du parcours (Étape 1 → Étape 2)
// Fidèle à l'infographie fournie par Sabril.
// Structure : Big title → Étape 1 (790€ · 3 livrables) → Pivot "On choisit
// ensemble" → Étape 2 (3 formats) → Double sortie garantie → Signature.
// ─────────────────────────────────────────────────────────────────────
function CommentCaFonctionne() {
  return (
    <section
      id="section-parcours"
      className="pt-8 pb-8 px-4"
      style={{
        background: `linear-gradient(180deg, ${DA.ivoryLight} 0%, ${DA.paper} 60%, ${DA.ivory} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
      data-testid="section-parcours"
    >
      {/* Discreet mimosa ornament — top-right */}
      <div style={{ position: 'absolute', top: -20, right: -30, opacity: 0.18, pointerEvents: 'none' }}>
        <MimosaBranch size={140} color={DA.goldMain} />
      </div>

      {/* Section header */}
      <div className="relative z-10 text-center mb-8" data-testid="parcours-header">
        <p style={{
          fontFamily: DA.mono, fontSize: 10, fontWeight: 800,
          letterSpacing: '0.36em', textTransform: 'uppercase',
          color: DA.goldDeep, marginBottom: 12,
        }}>
          Le parcours
        </p>
        <h2 style={{
          fontFamily: DA.serif, fontSize: 'clamp(30px, 8vw, 44px)', fontWeight: 400,
          fontStyle: 'italic', letterSpacing: '-0.03em', color: DA.greenDeep,
          lineHeight: 1, marginBottom: 10,
        }}>
          Comment ça fonctionne ?
        </h2>
        <p style={{
          fontFamily: DA.serif, fontSize: 14, fontWeight: 500,
          fontStyle: 'italic', color: DA.inkBody, lineHeight: 1.5,
          maxWidth: 340, margin: '0 auto',
        }}>
          Un process simple. Des décisions rentables.
        </p>
        <div className="flex justify-center mt-5">
          <span style={{ width: 32, height: 1, background: DA.goldMain, opacity: 0.6 }} />
        </div>
      </div>

      {/* ═══════════════ ÉTAPE 1 ═══════════════ */}
      <div
        className="relative z-10 mb-4"
        style={{
          background: DA.paper,
          border: `0.5px solid ${DA.goldMain}`,
          borderRadius: 18,
          padding: '22px 18px 20px',
          boxShadow: '0 22px 48px -20px rgba(11,43,36,0.22), inset 0 0 0 3px rgba(216,148,64,0.06)',
        }}
        data-testid="parcours-etape1"
      >
        <div className="flex items-center gap-3 mb-4">
          <span
            style={{
              width: 46, height: 46, borderRadius: 999,
              background: `linear-gradient(135deg, ${DA.goldMain} 0%, ${DA.goldDeep} 100%)`,
              color: DA.ivory,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: DA.serif, fontSize: 24, fontWeight: 700,
              letterSpacing: '-0.02em',
              boxShadow: `0 10px 22px -6px ${DA.goldMain}88, inset 0 1px 0 rgba(255,255,255,0.35)`,
            }}
          >1</span>
          <div className="flex-1">
            <p style={{
              fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
              letterSpacing: '0.28em', textTransform: 'uppercase',
              color: DA.goldDeep, marginBottom: 2,
            }}>Étape · Obligatoire</p>
            <p style={{
              fontFamily: DA.serif, fontSize: 18, fontWeight: 700, fontStyle: 'italic',
              color: DA.greenDeep, letterSpacing: '-0.01em', lineHeight: 1.15,
            }}>Sabril Express</p>
          </div>
        </div>

        <div className="flex items-baseline justify-between mb-4" style={{
          borderTop: `0.5px solid ${DA.goldMain}55`,
          borderBottom: `0.5px solid ${DA.goldMain}55`,
          padding: '14px 0',
        }}>
          <div>
            <p style={{
              fontFamily: DA.sans, fontSize: 34, fontWeight: 800,
              color: DA.greenDeep, letterSpacing: '-0.03em', lineHeight: 1,
            }}>790€</p>
            <p style={{
              fontFamily: DA.serif, fontSize: 11.5, fontStyle: 'italic',
              color: DA.inkMuted, marginTop: 4,
            }}>Pour le pack stratégique complet</p>
          </div>
          <span
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              background: `linear-gradient(135deg, ${DA.goldMain} 0%, ${DA.goldDeep} 100%)`,
              color: DA.ivory,
              padding: '6px 11px',
              borderRadius: 999,
              fontFamily: DA.mono, fontSize: 8.5, fontWeight: 900,
              letterSpacing: '0.22em', textTransform: 'uppercase',
              boxShadow: `0 8px 20px -6px ${DA.goldMain}AA`,
              whiteSpace: 'nowrap',
            }}
            data-testid="parcours-deductible-badge"
          >
            <Sparkles size={11} strokeWidth={2.6} />
            Déductible
          </span>
        </div>

        <p style={{
          fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
          letterSpacing: '0.28em', textTransform: 'uppercase',
          color: DA.goldDeep, marginBottom: 10,
        }}>Le pack contient</p>

        <div className="flex flex-col gap-3">
          {[
            { icon: '🎥', title: 'Visio #1 · Diagnostic', time: '45 min',
              bullets: ['État des lieux complet de votre restaurant','Analyse concept & chiffres','Positionnement','3 priorités identifiées'] },
            { icon: '🎯', title: 'Visio #2 · Stratégie & Plan d\u2019action', time: '45 min',
              bullets: ['Plan d\u2019action sur 30 jours','Recommandations stratégiques & opérationnelles','Organisation, menu, rentabilité','Réponses à toutes vos questions'] },
            { icon: '📖', title: 'Le Guide Ultime · offert', time: '70+ pages',
              bullets: ['Sa méthode complète','Toutes les clés pour construire un resto rentable','À lire, relire et appliquer'] },
          ].map((deliv, i) => (
            <div key={i} style={{
              background: `linear-gradient(180deg, ${DA.ivory} 0%, ${DA.ivoryLight} 100%)`,
              border: `0.5px solid ${DA.goldMain}55`,
              borderRadius: 12,
              padding: '13px 14px 11px',
            }} data-testid={`parcours-etape1-deliverable-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span aria-hidden style={{ fontSize: 18 }}>{deliv.icon}</span>
                  <p style={{
                    fontFamily: DA.serif, fontSize: 14, fontWeight: 700, fontStyle: 'italic',
                    color: DA.greenDeep, letterSpacing: '-0.005em',
                  }}>{deliv.title}</p>
                </div>
                <span style={{
                  fontFamily: DA.mono, fontSize: 8.5, fontWeight: 800,
                  letterSpacing: '0.22em', textTransform: 'uppercase',
                  color: DA.goldDeep, background: `${DA.goldMain}18`,
                  padding: '3px 7px', borderRadius: 999, whiteSpace: 'nowrap',
                }}>{deliv.time}</span>
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {deliv.bullets.map((b, bi) => (
                  <li key={bi} className="flex items-start gap-2" style={{ marginBottom: 4 }}>
                    <span aria-hidden style={{
                      display: 'inline-block', marginTop: 6, width: 4, height: 4,
                      borderRadius: 999, background: DA.goldMain, flexShrink: 0,
                    }} />
                    <span style={{
                      fontFamily: DA.sans, fontSize: 12.5, color: DA.inkBody,
                      lineHeight: 1.45, letterSpacing: '-0.005em',
                    }}>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* ═══════════ CTA principal · Étape 1 ═══════════ */}
        <div className="mt-5 flex flex-col gap-2">
          <button
            type="button"
            onClick={() => { window.location.href = '/ebook'; }}
            className="w-full active:scale-[0.98] transition-all wallet-cta-gold-pulse"
            style={{
              background: 'linear-gradient(135deg, #D89440 0%, #B8792F 100%)',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: 14,
              padding: '16px 18px',
              fontFamily: DA.sans, fontSize: 13, fontWeight: 800,
              letterSpacing: '0.14em', textTransform: 'uppercase',
              boxShadow: '0 14px 32px -10px rgba(184,121,47,0.55), inset 0 1px 0 rgba(255,214,120,0.35)',
              cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
            data-testid="parcours-etape1-cta-payer"
          >
            🚀 Payer 790€ · Réserver mes 2 visios
          </button>
          <p style={{
            fontFamily: DA.serif, fontSize: 11.5, fontStyle: 'italic',
            color: DA.inkMuted, textAlign: 'center', lineHeight: 1.45, letterSpacing: '-0.005em',
          }}>
            Paiement sécurisé Stripe · Vos 2 créneaux visio réservés dans la foulée.
          </p>
          {/* Secondary — WhatsApp for questions */}
          <a
            href="https://wa.me/33745522450?text=Bonjour%20Sabril%2C%20j%27h%C3%A9site%20avant%20le%20Sabril%20Express%2C%20j%27ai%20une%20question."
            target="_blank"
            rel="noopener noreferrer"
            className="w-full active:scale-[0.98] transition-all inline-flex items-center justify-center gap-2 mt-1"
            style={{
              background: 'transparent',
              color: DA.greenDeep,
              border: `0.5px solid ${DA.goldMain}55`,
              borderRadius: 12,
              padding: '11px 14px',
              fontFamily: DA.sans, fontSize: 11.5, fontWeight: 700,
              letterSpacing: '0.14em', textTransform: 'uppercase',
              textDecoration: 'none',
            }}
            data-testid="parcours-etape1-cta-question"
          >
            <MessageCircle size={13} strokeWidth={2.4} />
            Une question ? Écrire à Sabril
          </a>
        </div>
      </div>

      {/* PIVOT */}
      <div className="relative z-10 my-5 text-center" data-testid="parcours-pivot">
        <div className="flex justify-center mb-3">
          <span style={{ width: 0.5, height: 22, background: `linear-gradient(180deg, transparent 0%, ${DA.goldMain} 100%)` }} />
        </div>
        <p style={{
          fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
          letterSpacing: '0.34em', textTransform: 'uppercase',
          color: DA.goldDeep, marginBottom: 6,
        }}>À la fin des 2 visios</p>
        <p style={{
          fontFamily: DA.serif, fontSize: 18, fontWeight: 500, fontStyle: 'italic',
          color: DA.greenDeep, letterSpacing: '-0.01em', lineHeight: 1.35,
          maxWidth: 300, margin: '0 auto',
        }}>
          On choisit <strong style={{ color: DA.goldDeep, fontWeight: 700 }}>ensemble</strong> la formule d&apos;accompagnement la plus adaptée.
        </p>
        <div className="flex justify-center mt-3">
          <span style={{ width: 0.5, height: 22, background: `linear-gradient(180deg, ${DA.goldMain} 0%, transparent 100%)` }} />
        </div>
      </div>

      {/* ÉTAPE 2 */}
      <div className="relative z-10 mb-6" style={{
        background: `linear-gradient(180deg, ${DA.greenDeep} 0%, #0F3A31 100%)`,
        color: DA.ivory,
        border: `0.5px solid ${DA.goldMain}`,
        borderRadius: 18,
        padding: '22px 18px 20px',
        boxShadow: '0 22px 48px -18px rgba(11,43,36,0.55), inset 0 0 0 3px rgba(216,148,64,0.10)',
      }} data-testid="parcours-etape2">
        <div className="flex items-center gap-3 mb-4">
          <span style={{
            width: 46, height: 46, borderRadius: 999,
            background: `linear-gradient(135deg, ${DA.ivory} 0%, ${DA.ivoryLight} 100%)`,
            color: DA.greenDeep,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: DA.serif, fontSize: 24, fontWeight: 700,
            letterSpacing: '-0.02em',
            boxShadow: '0 10px 22px -6px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.60)',
          }}>2</span>
          <div className="flex-1">
            <p style={{
              fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
              letterSpacing: '0.28em', textTransform: 'uppercase',
              color: DA.goldMain, marginBottom: 2,
            }}>Étape · Accompagnement</p>
            <p style={{
              fontFamily: DA.serif, fontSize: 18, fontWeight: 700, fontStyle: 'italic',
              color: DA.ivory, letterSpacing: '-0.01em', lineHeight: 1.15,
            }}>Le Salon Privé</p>
          </div>
        </div>

        <p style={{
          fontFamily: DA.serif, fontSize: 13.5, fontStyle: 'italic',
          color: 'rgba(255,255,255,0.85)', lineHeight: 1.55, marginBottom: 14,
          borderBottom: `0.5px solid ${DA.goldMain}44`, paddingBottom: 14,
        }}>
          Choisissez la formule qui vous ressemble.<br/>
          3 formats — même exigence, même méthode.
        </p>

        <div className="flex flex-col gap-3">
          {[
            { tag: '48H EXPRESS', subtitle: '2 jours intensifs sur place',
              details: 'Immersion terrain · Plan d\u2019action livré au départ · Suivi WhatsApp 30j' },
            { tag: '90 JOURS · DISTANCE', subtitle: 'Accompagnement 100% à distance',
              details: 'Visios bi-mensuelles · WhatsApp direct · Feuille de route 90j' },
            { tag: '90 JOURS · HYBRIDE', subtitle: 'Distance + 15 jours sur place',
              details: 'Terrain + suivi rapproché · Les 12 chantiers inclus · Vol + hôtel offerts' },
          ].map((fmt, i) => (
            <div key={i} style={{
              background: 'rgba(255,255,255,0.06)',
              border: `0.5px solid ${DA.goldMain}55`,
              borderRadius: 12, padding: '12px 14px 11px',
              backdropFilter: 'blur(6px)',
            }} data-testid={`parcours-etape2-format-${i}`}>
              <div className="mb-1">
                <span style={{
                  fontFamily: DA.mono, fontSize: 8.5, fontWeight: 900,
                  letterSpacing: '0.24em', textTransform: 'uppercase', color: DA.goldMain,
                }}>{fmt.tag}</span>
              </div>
              <p style={{
                fontFamily: DA.serif, fontSize: 13.5, fontStyle: 'italic',
                color: DA.ivory, marginBottom: 4, lineHeight: 1.35,
              }}>{fmt.subtitle}</p>
              <p style={{
                fontFamily: DA.sans, fontSize: 11.5,
                color: 'rgba(255,255,255,0.72)', lineHeight: 1.45, letterSpacing: '-0.005em',
              }}>{fmt.details}</p>
            </div>
          ))}
        </div>

        {/* ═══════════ CTA · Étape 2 · Verrouillé (accès sur code) ═══════════ */}
        <div className="mt-5">
          <button
            type="button"
            onClick={() => { window.location.href = '/configurer'; }}
            className="w-full active:scale-[0.98] transition-all"
            style={{
              background: `linear-gradient(135deg, ${DA.goldMain} 0%, ${DA.goldDeep} 100%)`,
              color: DA.greenDeep,
              border: `0.5px solid ${DA.goldSoft}`,
              borderRadius: 14,
              padding: '16px 18px',
              fontFamily: DA.sans, fontSize: 12.5, fontWeight: 800,
              letterSpacing: '0.14em', textTransform: 'uppercase',
              boxShadow: '0 14px 32px -10px rgba(216,148,64,0.65), inset 0 1px 0 rgba(255,235,180,0.55)',
              cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
            data-testid="parcours-etape2-cta-unlock"
          >
            <Lock size={15} strokeWidth={2.6} />
            Débloquer avec mon code
          </button>
          <p style={{
            fontFamily: DA.serif, fontSize: 11.5, fontStyle: 'italic',
            color: 'rgba(255,255,255,0.72)', textAlign: 'center',
            marginTop: 10, lineHeight: 1.5, letterSpacing: '-0.005em',
          }}>
            Le code vous est transmis à la fin de votre 2<sup>ᵉ</sup> visio Sabril Express.
          </p>
        </div>
      </div>

      {/* DOUBLE EXIT */}
      <div className="relative z-10 mb-6" data-testid="parcours-double-exit">
        <p style={{
          fontFamily: DA.mono, fontSize: 9.5, fontWeight: 800,
          letterSpacing: '0.30em', textTransform: 'uppercase',
          color: DA.goldDeep, marginBottom: 12, textAlign: 'center',
        }}>Deux issues · zéro perdant</p>

        <div className="flex flex-col gap-3">
          <div style={{
            background: `linear-gradient(135deg, ${DA.greenDeep} 0%, #0F3A31 100%)`,
            color: DA.ivory,
            border: `0.5px solid ${DA.goldMain}`,
            borderRadius: 14, padding: '16px 18px',
            boxShadow: '0 18px 40px -18px rgba(11,43,36,0.55)',
          }} data-testid="parcours-outcome-continue">
            <div className="flex items-center gap-2 mb-2">
              <Check size={14} strokeWidth={3} style={{ color: DA.goldMain }} />
              <p style={{
                fontFamily: DA.mono, fontSize: 9, fontWeight: 900,
                letterSpacing: '0.24em', textTransform: 'uppercase', color: DA.goldMain,
              }}>Vous continuez avec moi</p>
            </div>
            <p style={{
              fontFamily: DA.serif, fontSize: 16, fontWeight: 500, fontStyle: 'italic',
              color: DA.ivory, lineHeight: 1.4, letterSpacing: '-0.005em',
            }}>
              Les <strong style={{ color: DA.goldMain, fontWeight: 800, fontStyle: 'normal' }}>790€</strong> sont
              intégralement déduits du prix de l&apos;accompagnement choisi.
            </p>
          </div>

          <div style={{
            background: DA.paper, color: DA.inkBody,
            border: `0.5px solid ${DA.goldMain}`,
            borderRadius: 14, padding: '16px 18px',
            boxShadow: '0 18px 40px -20px rgba(11,43,36,0.20)',
          }} data-testid="parcours-outcome-quit">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={14} strokeWidth={2.4} style={{ color: DA.goldDeep }} />
              <p style={{
                fontFamily: DA.mono, fontSize: 9, fontWeight: 900,
                letterSpacing: '0.24em', textTransform: 'uppercase', color: DA.goldDeep,
              }}>Vous ne continuez pas</p>
            </div>
            <p style={{
              fontFamily: DA.serif, fontSize: 14, fontStyle: 'italic',
              color: DA.inkBody, lineHeight: 1.5, marginBottom: 10,
            }}>Vous repartez quand même avec :</p>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {[
                'Les 2 visios stratégiques (diagnostic + plan d\u2019action)',
                'Un plan d\u2019action concret sur 30 jours',
                'Le Guide Ultime (70+ pages)',
                'Une vision claire des priorités de votre restaurant',
              ].map((b, bi) => (
                <li key={bi} className="flex items-start gap-2" style={{ marginBottom: 5 }}>
                  <Check size={12} strokeWidth={2.8} style={{ color: DA.goldDeep, marginTop: 4, flexShrink: 0 }} />
                  <span style={{
                    fontFamily: DA.sans, fontSize: 12.5, color: DA.inkBody,
                    lineHeight: 1.45, letterSpacing: '-0.005em',
                  }}>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* SIGNATURE */}
      <div className="relative z-10 text-center mb-3" data-testid="parcours-signature">
        <div className="flex justify-center mb-4">
          <span style={{ width: 32, height: 1, background: DA.goldMain, opacity: 0.6 }} />
        </div>
        <p style={{
          fontFamily: DA.mono, fontSize: 10, fontWeight: 900,
          letterSpacing: '0.32em', textTransform: 'uppercase',
          color: DA.greenDeep, lineHeight: 1.7, marginBottom: 4,
        }}>
          Prenez les bonnes décisions,<br/>
          dès aujourd&apos;hui.
        </p>
        <p style={{
          fontFamily: DA.serif, fontSize: 13, fontStyle: 'italic',
          color: DA.inkMuted, letterSpacing: '-0.005em',
        }}>— Sabril</p>
      </div>
    </section>
  );
}


// Wallet Section — stacked cards in native style
// ─────────────────────────────────────────────────────────────────────
function WalletOffersSection() {
  const [openKey, setOpenKey] = useState('premiere');
  const scrollerRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);

  // Track horizontal scroll to update active dot
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      const cardW = 316; // 300px card + 16px gap
      const idx = Math.round(el.scrollLeft / cardW);
      setActiveIdx(Math.max(0, Math.min(OFFERS.length - 1, idx)));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const goToCard = (idx) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: idx * 316, behavior: 'smooth' });
  };

  return (
    <div id="offres" data-testid="wallet-section">
      <SectionHeader
        overline="② Choisir votre porte d'entrée"
        title="Comment on commence."
        subtitle="Deux façons simples : Sabril Express pour tester ma méthode en 90 minutes, ou l'accompagnement sur-mesure pour passer à l'action ensemble."
      />
      {/* Vertical stack — 2 cartes empilées : Ebook + Commencer mon accompagnement */}
      <div
        className="pb-3"
        data-testid="wallet-section-cards"
      >
        <style>{`
          [data-testid="wallet-section-cards"]::-webkit-scrollbar{display:none}
          @keyframes walletCardGlow {
            0%, 100% { box-shadow: 0 18px 48px -10px rgba(255,196,46,0.55), 0 0 0 0.5px rgba(255,226,117,0.40), 0 0 50px -4px rgba(255,226,117,0.32), 0 2px 0 0 rgba(255,255,255,0.10) inset, 0 -2px 0 0 rgba(0,0,0,0.40) inset; }
            50%      { box-shadow: 0 18px 56px -8px rgba(255,196,46,0.78), 0 0 0 1px rgba(255,226,117,0.65), 0 0 72px 6px rgba(255,226,117,0.55), 0 2px 0 0 rgba(255,255,255,0.10) inset, 0 -2px 0 0 rgba(0,0,0,0.40) inset; }
          }
          .wallet-card-pulse { animation: walletCardGlow 2.6s ease-in-out infinite; }
          @keyframes wallet-holo-shift {
            0%, 100% { background-position:   0% 50%; opacity: 0.45; }
            50%      { background-position: 100% 50%; opacity: 0.75; }
          }
          @keyframes wallet-halo-spin {
            0%   { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes wallet-check-stagger {
            0%   { opacity: 0; transform: scale(0.4) translateY(4px); }
            60%  { opacity: 1; transform: scale(1.12) translateY(0); }
            100% { opacity: 1; transform: scale(1) translateY(0); }
          }
          .wallet-check {
            animation: wallet-check-stagger 520ms cubic-bezier(0.34, 1.56, 0.64, 1) both;
          }
          .ebook-cover-wrap:hover .ebook-cover-tilt,
          .ebook-cover-wrap:focus-within .ebook-cover-tilt,
          .ebook-cover-wrap:active .ebook-cover-tilt,
          .wallet-card-pulse:hover .ebook-cover-tilt {
            transform: rotateY(-9deg) rotateX(3deg) translateZ(14px) scale(1.02);
            box-shadow:
              -12px 22px 38px -14px rgba(11,43,36,0.42),
              0 0 0 0.5px rgba(216,148,64,0.45),
              0 2px 6px rgba(0,0,0,0.15);
          }
          .wallet-card-pulse:hover .ebook-cover-shine,
          .ebook-cover-wrap:hover .ebook-cover-shine,
          .ebook-cover-wrap:active .ebook-cover-shine {
            transform: translateX(280%);
          }
          @media (prefers-reduced-motion: reduce) {
            .wallet-card-pulse { animation: none; }
            [data-holo] { animation: none !important; }
            .wallet-check { animation: none; opacity: 1; transform: none; }
            .ebook-cover-wrap:hover .ebook-cover-tilt { transform: none; }
            .ebook-cover-shine { display: none; }
            .wallet-cta-gold-pulse { animation: none !important; }
          }
          @keyframes walletCtaGoldPulse {
            0%, 100% {
              box-shadow:
                0 10px 24px -8px rgba(11,43,36,0.55),
                0 0 0 0px rgba(216,148,64,0.55),
                0 0 22px -2px rgba(216,148,64,0.35),
                inset 0 1px 0 0 rgba(255,214,120,0.35);
            }
            50% {
              box-shadow:
                0 14px 32px -6px rgba(11,43,36,0.60),
                0 0 0 3px rgba(216,148,64,0.28),
                0 0 42px 2px rgba(255,196,46,0.65),
                inset 0 1px 0 0 rgba(255,214,120,0.55);
            }
          }
          @keyframes walletCtaGoldShine {
            0%   { transform: translateX(-110%); }
            60%  { transform: translateX(140%); }
            100% { transform: translateX(140%); }
          }
          .wallet-cta-gold-pulse {
            position: relative;
            overflow: hidden;
            animation: walletCtaGoldPulse 2.4s ease-in-out infinite;
          }
          .wallet-cta-gold-pulse::after {
            content: '';
            position: absolute;
            top: 0; left: 0; height: 100%; width: 40%;
            background: linear-gradient(115deg, transparent 20%, rgba(255,214,120,0.55) 50%, transparent 80%);
            transform: translateX(-110%);
            animation: walletCtaGoldShine 3.6s cubic-bezier(0.4,0,0.2,1) infinite;
            pointer-events: none;
          }
        `}</style>
        <div
          ref={scrollerRef}
          className="flex overflow-x-auto snap-x snap-mandatory gap-4 pl-5 pr-2 pb-4 scroll-smooth"
          style={{ scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch' }}
          data-testid="wallet-scroller"
        >
          {OFFERS.filter((o) => o.key === 'ebook' || o.key === 'configurer').map((o) => (
            <div
              key={o.key}
              className="snap-start flex-shrink-0"
              style={{ scrollSnapAlign: 'start', width: '85vw', maxWidth: 360 }}
            >
              <WalletCard
                offer={o}
                isOpen={openKey === o.key}
                onToggle={() => setOpenKey((k) => (k === o.key ? null : o.key))}
                onAction={() => openSabril(o.key)}
              />
            </div>
          ))}
          {/* Trailing spacer so last card snaps flush with room to breathe */}
          <div aria-hidden className="flex-shrink-0 w-4" />
        </div>
        {/* Swipe hint mobile — animated arrow */}
        <div
          className="md:hidden flex items-center justify-center gap-1.5 mt-1 mb-2 px-4"
          style={{
            fontFamily: theme.fontMono,
            fontSize: 9.5,
            letterSpacing: '0.24em',
            textTransform: 'uppercase',
            color: '#A87C4F',
            fontWeight: 700,
          }}
          data-testid="wallet-swipe-hint"
        >
          <span aria-hidden style={{ animation: 'wallet-swipe-nudge 1.8s ease-in-out infinite' }}>←</span>
          <span>Faites glisser</span>
          <span aria-hidden style={{ animation: 'wallet-swipe-nudge 1.8s ease-in-out infinite' }}>→</span>
        </div>
        <style>{`
          @keyframes wallet-swipe-nudge {
            0%, 100% { transform: translateX(0); opacity: 0.5; }
            50% { transform: translateX(-3px); opacity: 1; }
          }
        `}</style>
      </div>

      {/* Dots + swipe hint retirés — layout est maintenant en pile verticale, plus besoin */}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Ce qu'on fait en 90 jours — 5 outcome cards (Apple-style icons)
// Restaurant owner reads in 2 seconds. No jargon.
// ─────────────────────────────────────────────────────────────────────
function KpiStrip() {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <div data-testid="kpi-strip">
      <SectionHeader
        overline="90 jours · ce qu'on fait pour vous"
        title="6 résultats concrets."
        subtitle="Tappe une app pour voir le détail."
      />
      {/* iOS Home-screen grid : 3 cols × 2 rows */}
      <div className="px-5 mb-4">
        <div className="grid grid-cols-3 gap-x-3 gap-y-5" data-testid="kpi-grid">
          {KPIS.map((k, idx) => (
            <button
              key={k.key}
              type="button"
              onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
              className="flex flex-col items-center active:scale-[0.94] transition-transform"
              style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
              data-testid={`kpi-${k.key}`}
            >
              <span
                className="relative inline-flex items-center justify-center mb-1.5"
                style={{
                  width: 60, height: 60, borderRadius: 16,
                  background: `linear-gradient(135deg, ${k.color} 0%, ${k.color}DD 100%)`,
                  boxShadow: `0 8px 20px -6px ${k.color}AA, inset 0 1px 0 rgba(255,255,255,0.30), inset 0 -2px 0 rgba(0,0,0,0.10)`,
                }}
              >
                <k.icon className="w-7 h-7" style={{ color: '#FFFFFF' }} strokeWidth={2.2} />
                {/* iOS notification badge */}
                {k.badge && (
                  <span
                    className="absolute"
                    style={{
                      top: -7, right: -10,
                      background: '#FF3B30',
                      color: '#FFFFFF',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
                      fontSize: 10, fontWeight: 800, letterSpacing: '-0.005em',
                      padding: '2px 6px',
                      borderRadius: 999,
                      minWidth: 22, textAlign: 'center',
                      border: '1.5px solid #FFFFFF',
                      boxShadow: '0 2px 6px rgba(255,59,48,0.45)',
                      lineHeight: 1.2,
                    }}
                    data-testid={`kpi-badge-${k.key}`}
                  >
                    {k.badge}
                  </span>
                )}
              </span>
              <p style={{
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
                fontSize: 11.5, fontWeight: 600, color: theme.inkDeep, letterSpacing: '-0.01em',
                lineHeight: 1.15, textAlign: 'center', minHeight: 28,
              }}>
                {k.title}
              </p>
            </button>
          ))}
        </div>

        {/* Detail card — opens like an iOS app preview when an icon is tapped */}
        {openIdx !== null && KPIS[openIdx] && (
          <div
            className="mt-5 relative"
            style={{
              background: theme.bgPaper,
              borderRadius: 18,
              padding: '14px 14px 16px',
              boxShadow: '0 12px 28px -10px rgba(0,0,0,0.12), 0 0 0 0.5px rgba(60,60,67,0.10)',
            }}
            data-testid={`kpi-detail-${KPIS[openIdx].key}`}
          >
            <div className="flex items-center gap-3 mb-2">
              <span
                className="inline-flex items-center justify-center flex-shrink-0"
                style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: `linear-gradient(135deg, ${KPIS[openIdx].color} 0%, ${KPIS[openIdx].color}DD 100%)`,
                  boxShadow: `0 4px 10px -2px ${KPIS[openIdx].color}AA`,
                }}
              >
                {(() => { const I = KPIS[openIdx].icon; return <I className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={2.4} />; })()}
              </span>
              <p style={{
                fontFamily: theme.fontDisplay, fontSize: 15, fontWeight: 800,
                color: theme.inkDeep, letterSpacing: '-0.015em', lineHeight: 1.2,
              }}>
                {KPIS[openIdx].title}
              </p>
              <button
                type="button"
                onClick={() => setOpenIdx(null)}
                className="ml-auto flex-shrink-0 active:opacity-60"
                style={{
                  width: 24, height: 24, borderRadius: 999,
                  background: 'rgba(60,60,67,0.08)', border: 'none', padding: 0,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}
                aria-label="Fermer"
                data-testid="kpi-detail-close"
              >
                <X className="w-3.5 h-3.5" style={{ color: 'rgba(60,60,67,0.65)' }} strokeWidth={2.4} />
              </button>
            </div>
            <p style={{
              fontFamily: theme.fontSans, fontSize: 12.5, color: theme.inkBody,
              lineHeight: 1.45, marginBottom: 8,
            }}>
              {KPIS[openIdx].sub}
            </p>
            <span style={{
              display: 'inline-block',
              fontFamily: theme.fontMono, fontSize: 9.5, fontWeight: 800, letterSpacing: '0.10em', textTransform: 'uppercase',
              color: KPIS[openIdx].color, padding: '4px 9px',
              background: `${KPIS[openIdx].color}14`,
              borderRadius: 999,
            }}>
              ✓ {KPIS[openIdx].proof}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Pour qui — pill chips
// ─────────────────────────────────────────────────────────────────────
function PourQui() {
  return (
    <div data-testid="pour-qui">
      <SectionHeader overline="C'est pour qui" title="Pour qui on travaille." subtitle="Brunch, street food, hôtel, restaurant — peu importe le concept, ce qui compte c'est l'envie de durer." />
      <div className="overflow-x-auto px-5 pb-3 no-scrollbar">
        <div className="flex gap-2 flex-wrap" style={{ maxWidth: '100%' }}>
          {TARGETS.map((t, i) => (
            <div
              key={t.label + i}
              className="inline-flex items-center gap-1.5"
              style={{
                background: theme.bgPaper,
                borderRadius: 999,
                padding: '6px 12px 6px 6px',
                boxShadow: '0 0 0 0.5px rgba(60,60,67,0.14), 0 1px 2px rgba(0,0,0,0.04)',
              }}
              data-testid={`target-${i}`}
            >
              <span
                className="inline-flex items-center justify-center"
                style={{ width: 22, height: 22, borderRadius: 999, background: theme.inkDeep }}
              >
                <t.icon className="w-3 h-3" style={{ color: '#FFFFFF' }} strokeWidth={2.4} />
              </span>
              <span style={{ fontFamily: theme.fontDisplay, fontSize: 12, fontWeight: 600, color: theme.inkDeep, letterSpacing: '-0.01em' }}>
                {t.label}
              </span>
              <span style={{ fontFamily: theme.fontSans, fontSize: 11, color: theme.inkMuted }}>
                · {t.sub}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Compétences — iPhone Home Screen style (4×2 app grid) — clickable
// ─────────────────────────────────────────────────────────────────────
function Competences() {
  const COLORS = ['#FF3B30', '#FF9500', '#FFCC00', '#34C759', '#5AC8FA', '#007AFF', '#AF52DE', '#FF2D55'];
  const [opened, setOpened] = useState(null); // { step, color } | null
  const close = () => setOpened(null);

  useEffect(() => {
    if (!opened) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [opened]);

  return (
    <div data-testid="competences">
      <SectionHeader overline="Nos 8 expertises" title="On maîtrise tout." subtitle="Un resto qui marche, c'est jamais une seule compétence — c'est 8 apps qui tournent ensemble. Tap pour ouvrir." />
      {/* iOS Home Screen — 4×2 grid */}
      <div
        className="mx-4 mb-3 px-3 pt-5 pb-4"
        style={{
          background: 'linear-gradient(160deg, rgba(255,255,255,0.65) 0%, rgba(242,242,247,0.55) 100%)',
          borderRadius: 22,
          border: '0.5px solid rgba(60,60,67,0.10)',
          backdropFilter: 'blur(18px) saturate(180%)',
          WebkitBackdropFilter: 'blur(18px) saturate(180%)',
          boxShadow: '0 0 0 0.5px rgba(60,60,67,0.06), 0 8px 22px -10px rgba(0,0,0,0.08)',
        }}
        data-testid="competences-homescreen"
      >
        <div className="grid grid-cols-4 gap-y-4 gap-x-2">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <button
                type="button"
                key={p.num}
                onClick={() => setOpened({ step: p, color: COLORS[i] })}
                className="flex flex-col items-center active:scale-[0.92] transition-transform"
                data-testid={`pillar-app-${p.num}`}
              >
                <span
                  className="flex items-center justify-center"
                  style={{
                    width: 56, height: 56,
                    borderRadius: 14,
                    background: `linear-gradient(160deg, ${COLORS[i]} 0%, ${COLORS[i]}DD 100%)`,
                    boxShadow: `0 6px 14px -4px ${COLORS[i]}66, inset 0 1px 0 0 rgba(255,255,255,0.30)`,
                  }}
                >
                  <Icon className="w-[24px] h-[24px]" style={{ color: '#FFFFFF' }} strokeWidth={2.2} />
                </span>
                <p
                  className="mt-1.5 text-center"
                  style={{
                    fontFamily: theme.fontDisplay,
                    fontSize: 10.5,
                    fontWeight: 600,
                    color: theme.inkDeep,
                    letterSpacing: '-0.01em',
                    lineHeight: 1.15,
                    maxWidth: 72,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {p.label}
                </p>
              </button>
            );
          })}
        </div>
        {/* Page indicator dots */}
        <div className="flex items-center justify-center gap-1.5 mt-4">
          <span style={{ width: 6, height: 6, borderRadius: 999, background: 'rgba(60,60,67,0.75)' }} />
          <span style={{ width: 6, height: 6, borderRadius: 999, background: 'rgba(60,60,67,0.25)' }} />
          <span style={{ width: 6, height: 6, borderRadius: 999, background: 'rgba(60,60,67,0.25)' }} />
        </div>
      </div>
      <p className="px-5 mb-2" style={{ fontFamily: theme.fontSans, fontSize: 11, color: theme.inkMuted, lineHeight: 1.4, textAlign: 'center' }}>
        8 apps · 8 expertises · activées en moins de 30 jours.
      </p>

      {opened && <MethodAppOverlay step={opened.step} color={opened.color} onClose={close} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// CareerSwipe — "Mon CV" en mode Stories swipe horizontal
// ─────────────────────────────────────────────────────────────────────
const SABRIL_CAREER = [
  {
    brand: 'bein SPORTS',
    role: 'Responsable Publicitaire',
    period: 'Régie publicitaire TV',
    image: 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/k3vy2jz8_IMG_0754.jpeg',
    tint: '#7C3AED',
    bullets: ['Vente d\'espaces TV, digital, événements', 'Portefeuille clients agences & annonceurs', 'Négociation, closing & suivi de campagnes', 'Atteinte & dépassement objectifs CA'],
  },
  {
    brand: 'BFM · Bonsoir Lille',
    role: 'Direction Commerciale Publicitaire',
    period: 'Altice Media · Lille',
    image: 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/1khzrgqe_IMG_0755.jpeg',
    tint: '#0066CC',
    bullets: ['Management de l\'équipe commerciale', 'Stratégie de développement des revenus pub', 'Pilotage performance & reporting', 'Gestion grands comptes & partenariats stratégiques'],
  },
  {
    brand: 'HTBC · Formatek',
    role: 'Consultant Senior · Recrutement',
    period: 'Cabinet C-level',
    image: 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/o5mmhfxe_IMG_0410.jpeg',
    tint: '#C9A87A',
    bullets: ['Cabinet spécialisé grands groupes', 'Identification & approche de talents clés', 'Conduite d\'entretiens & évaluation candidats', 'Accompagnement besoins recrutement stratégiques'],
  },
  {
    brand: 'LOSC · Boulanger',
    role: 'Activation Partenariat Ligue 1',
    period: 'Sponsoring Uber Eats',
    image: 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/hckkljs5_5B8F7C00-BC63-4110-A67C-7975FE0A4D1A.jpeg',
    tint: '#E63946',
    bullets: ['Activation sponsoring match-day Ligue 1', 'Coordination événementiel terrain', 'Relation Boulanger × LOSC', 'Visibilité naming, PLV stade, conf de presse'],
  },
  {
    brand: 'Paris Saint-Germain',
    role: 'Communication & Marketing · PSG Académie Monde',
    period: 'Pôle communication & marketing groupe',
    image: 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/imqrh3vs_IMG_0611.jpeg',
    tint: '#1B3A6B',
    bullets: ['Communication & marketing PSG groupe', 'PSG Académie Monde — dispositifs internationaux', 'Activations & événements grands comptes', 'Pilotage marque · partenariats stratégiques'],
  },
  {
    brand: 'Maison Avocado + Bring Café',
    role: 'Concepts vendus · Aujourd\'hui : accompagnement intl.',
    period: 'Lille · franchise en cours',
    image: 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/s71b6af4_7A644930-5B8A-44D0-8BF9-D55732DCE79D.png',
    tint: '#FF6B35',
    bullets: ['Fondateur Maison Avocado (Lille) + Bring Café', 'Marque en cours de franchise · Paris/Dubai/Miami', 'Concepts revendus après mise sur orbite', 'Aujourd\'hui : accompagnement hôtels & restos à l\'international'],
  },
];

function CareerSwipe() {
  const trackRef = useRef(null);
  const [idx, setIdx] = useState(0);

  const onScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    const w = el.clientWidth;
    const cur = Math.round(el.scrollLeft / w);
    if (cur !== idx) setIdx(cur);
  };

  return (
    <div className="mb-2" data-testid="career-swipe">
      <SectionHeader
        overline="Mon parcours"
        title="Avant de bosser pour vous, j'ai bossé là."
        subtitle="bein, BFM, LOSC, PSG. J'ai monté Maison Avocado + Bring, revendu les concepts. Aujourd'hui : hôtels & restos partout dans le monde. Swipe →"
      />

      {/* Horizontal snap scroller */}
      <div
        ref={trackRef}
        onScroll={onScroll}
        className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar"
        style={{
          gap: 12,
          paddingLeft: 16,
          paddingRight: 16,
          scrollPadding: '0 16px',
          WebkitOverflowScrolling: 'touch',
        }}
        data-testid="career-swipe-track"
      >
        {SABRIL_CAREER.map((c, i) => (
          <div
            key={c.brand}
            className="snap-center shrink-0 relative overflow-hidden"
            style={{
              width: 280,
              height: 380,
              borderRadius: 22,
              boxShadow: '0 10px 30px -10px rgba(0,0,0,0.30), 0 0 0 0.5px rgba(60,60,67,0.10)',
              background: theme.bgPaper,
            }}
            data-testid={`career-card-${i}`}
          >
            {/* Image fills card */}
            <div
              style={{
                position: 'absolute', inset: 0,
                backgroundImage: `url(${c.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            {/* Top color tag */}
            <div className="absolute top-3 left-3 z-[2]">
              <span
                style={{
                  background: 'rgba(0,0,0,0.55)',
                  backdropFilter: 'blur(14px)',
                  WebkitBackdropFilter: 'blur(14px)',
                  color: '#FFFFFF',
                  borderRadius: 999,
                  padding: '4px 10px',
                  fontFamily: theme.fontMono,
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  border: '0.5px solid rgba(255,255,255,0.20)',
                }}
              >
                {c.period}
              </span>
            </div>
            {/* Bottom gradient + content */}
            <div
              className="absolute inset-x-0 bottom-0 z-[2] px-4 pb-4 pt-16"
              style={{
                background: `linear-gradient(180deg, rgba(0,0,0,0) 0%, ${c.tint}DD 60%, ${c.tint} 100%)`,
                color: '#FFFFFF',
              }}
            >
              <p style={{ fontFamily: theme.fontDisplay, fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                {c.brand}
              </p>
              <p style={{ fontFamily: theme.fontMono, fontSize: 10, letterSpacing: '0.10em', textTransform: 'uppercase', opacity: 0.92, fontWeight: 700, marginTop: 4 }}>
                {c.role}
              </p>
              <ul className="mt-2 flex flex-col gap-1">
                {c.bullets.slice(0, 4).map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-1.5"
                    style={{ fontFamily: theme.fontSans, fontSize: 11, color: 'rgba(255,255,255,0.92)', lineHeight: 1.35 }}
                  >
                    <span style={{ color: '#FFFFFF', marginTop: 4, width: 3, height: 3, borderRadius: 999, background: theme.bgPaper, display: 'inline-block', flexShrink: 0 }} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {/* Page dots */}
      <div className="flex items-center justify-center gap-1.5 mt-3">
        {SABRIL_CAREER.map((c, i) => (
          <span
            key={c.brand}
            style={{
              width: i === idx ? 18 : 6,
              height: 6,
              borderRadius: 999,
              background: i === idx ? c.tint : 'rgba(60,60,67,0.20)',
              transition: 'width 220ms ease, background 220ms ease',
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// LossCalculator — "Combien vous perdez chaque mois"
// ─────────────────────────────────────────────────────────────────────
function LossCalculator() {
  const TARGET = 27; // healthy food cost %
  const [ca, setCa] = useState(60); // K€ / month
  const [fc, setFc] = useState(38); // %

  const fmtEur = (n) => {
    const v = Math.max(0, Math.round(n));
    return v.toLocaleString('fr-FR').replace(/,/g, ' ') + ' €';
  };

  const monthlyLossEur = Math.max(0, (ca * 1000) * Math.max(0, (fc - TARGET) / 100));
  const annualLossEur = monthlyLossEur * 12;
  const recovered = monthlyLossEur; // same amount recovered with program
  const verdict =
    monthlyLossEur >= 8000 ? { txt: 'Critique — chaque mois perdu coûte cher.', tone: '#FF3B30' } :
    monthlyLossEur >= 3000 ? { txt: 'Important — il y a un vrai levier à activer.', tone: '#FF9500' } :
    monthlyLossEur > 0     ? { txt: 'Optimisable — quelques points à gratter.', tone: '#FFCC00' } :
                             { txt: 'Niveau benchmark. Continue.', tone: '#34C759' };

  // iOS-style range slider styling
  const slider = (label, suffix, value, setValue, min, max, step, tint) => (
    <div className="mb-3" data-testid={`calc-slider-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-baseline justify-between mb-1.5">
        <span style={{ fontFamily: theme.fontMono, fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: theme.inkMuted, fontWeight: 700 }}>
          {label}
        </span>
        <span style={{ fontFamily: theme.fontDisplay, fontSize: 18, fontWeight: 800, color: tint, letterSpacing: '-0.02em' }}>
          {value}{suffix}
        </span>
      </div>
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        className="w-full"
        style={{
          accentColor: tint,
          height: 4,
        }}
      />
    </div>
  );

  return (
    <div className="px-4 mb-6" data-testid="loss-calculator">
      <SectionHeader
        overline="Réveil brutal"
        title="Combien vous perdez chaque mois ?"
        subtitle="2 chiffres, 5 secondes. Le verdict honnête, en direct."
      />
      <div
        className="overflow-hidden"
        style={{
          background: theme.bgPaper,
          borderRadius: 22,
          border: '0.5px solid rgba(60,60,67,0.10)',
          boxShadow: '0 12px 28px -14px rgba(26,23,20,0.14)',
        }}
      >
        {/* Inputs panel */}
        <div className="px-5 pt-5 pb-3">
          {slider("CA mensuel", " K€", ca, setCa, 10, 300, 1, '#007AFF')}
          {slider("Food cost actuel", " %", fc, setFc, 22, 50, 0.5, '#FF6B35')}
          <p style={{ fontFamily: theme.fontMono, fontSize: 9.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: theme.inkMuted, fontWeight: 600, textAlign: 'center', marginTop: 4 }}>
            Cible saine du métier · {TARGET}%
          </p>
        </div>

        {/* Verdict */}
        <div
          className="px-5 py-4"
          style={{
            background: '#FAFAF7',
            borderTop: '0.5px solid rgba(60,60,67,0.08)',
          }}
        >
          {/* Big monthly loss */}
          <p style={{ fontFamily: theme.fontMono, fontSize: 9.5, letterSpacing: '0.20em', textTransform: 'uppercase', color: theme.inkMuted, fontWeight: 700 }}>
            Vous laissez chaque mois
          </p>
          <p
            data-testid="calc-monthly-loss"
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 44, fontWeight: 900,
              color: monthlyLossEur > 0 ? '#FF3B30' : '#34C759',
              letterSpacing: '-0.035em',
              lineHeight: 1.02,
              marginTop: 4,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {fmtEur(monthlyLossEur)}
          </p>
          <p style={{ fontFamily: theme.fontSans, fontSize: 12, color: theme.inkBody, marginTop: 4, lineHeight: 1.4 }}>
            Soit <b style={{ color: theme.inkDeep }}>{fmtEur(annualLossEur)}</b> par an qui partent en fumée — sans rien faire d'autre.
          </p>

          {/* Verdict pill */}
          <div className="mt-3 flex items-center gap-2">
            <span style={{ width: 6, height: 6, borderRadius: 999, background: verdict.tone, boxShadow: `0 0 8px ${verdict.tone}66` }} />
            <span style={{ fontFamily: theme.fontDisplay, fontSize: 12.5, color: verdict.tone, fontWeight: 700, letterSpacing: '-0.005em' }}>
              {verdict.txt}
            </span>
          </div>

          {/* Recoverable amount */}
          {monthlyLossEur > 0 && (
            <div
              className="mt-3 px-3 py-3"
              style={{
                background: 'rgba(52,199,89,0.08)',
                borderRadius: 12,
                border: '0.5px solid rgba(52,199,89,0.25)',
              }}
            >
              <p style={{ fontFamily: theme.fontMono, fontSize: 9.5, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#1FA644', fontWeight: 800 }}>
                Récupérable avec le programme
              </p>
              <p style={{ fontFamily: theme.fontDisplay, fontSize: 22, fontWeight: 800, color: '#1FA644', letterSpacing: '-0.025em', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                +{fmtEur(recovered)} <span style={{ fontSize: 12, fontWeight: 600, opacity: 0.75 }}>/ mois</span>
              </p>
            </div>
          )}

          {/* CTA */}
          <button
            type="button"
            onClick={() => openSabril('question')}
            className="mt-3 w-full active:scale-[0.97] transition-transform inline-flex items-center justify-center gap-2"
            style={{
              background: 'linear-gradient(180deg, #0A0A0F 0%, #1A1A1F 100%)',
              color: '#FFFFFF',
              padding: '13px 18px',
              borderRadius: 999,
              fontFamily: theme.fontDisplay, fontSize: 14, fontWeight: 700,
              letterSpacing: '-0.005em',
              boxShadow: '0 12px 24px -8px rgba(10,10,15,0.30)',
            }}
            data-testid="calc-cta"
          >
            <MessageCircle className="w-4 h-4" strokeWidth={2.4} />
            En parler à Sabril maintenant
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// CapitaineDuNavire — coaching dirigeant 12h INCLUS dans l'accompagnement
// ─────────────────────────────────────────────────────────────────────
const CAPITAINE_PILLARS = [
  {
    num: '01', label: 'Vision', headline: 'Fixer un cap clair.',
    color: '#1B3A2F', accent: '#C9A87A',
    apps: [
      { label: 'Cap clair',  desc: 'Définir la destination', icon: Compass },
      { label: 'Objectifs',  desc: 'Ambitieux & mesurables',  icon: Trophy },
      { label: 'Plan 90j',   desc: 'Feuille de route trimestrielle',   icon: CalendarRange },
      { label: 'Décisions',  desc: 'Trancher les clés',       icon: GitBranch },
    ],
  },
  {
    num: '02', label: 'Organisation', headline: 'Reprendre le contrôle.',
    color: '#152843', accent: '#C9A87A',
    apps: [
      { label: 'Agenda',    desc: 'Structurer son temps',     icon: Clock },
      { label: 'Process',   desc: 'Procédures & modes d\'emploi',      icon: Settings },
      { label: 'Sortir op', desc: 'Quitter l\'opérationnel',  icon: LogOut },
      { label: 'Priorités', desc: 'Suivi & arbitrage',        icon: ListChecks },
    ],
  },
  {
    num: '03', label: 'Leadership', headline: 'Construire une équipe.',
    color: '#5B1A1F', accent: '#C9A87A',
    apps: [
      { label: 'Recruter',  desc: 'Casting des bons',         icon: UserPlus },
      { label: 'Manager',   desc: 'Impact & cadence',         icon: Megaphone },
      { label: 'Responsab.',desc: 'Délégation cadrée',        icon: ShieldCheck },
      { label: 'Culture',   desc: 'Engagement durable',       icon: Heart },
    ],
  },
  {
    num: '04', label: 'Croissance', headline: 'Développer & scaler.',
    color: '#0F2A47', accent: '#C9A87A',
    apps: [
      { label: 'Rentab.',   desc: 'Piloter la marge',         icon: BarChart3 },
      { label: 'CA+',       desc: 'Augmenter le revenu',      icon: TrendingUp },
      { label: 'Acquisit.', desc: 'Attirer + convertir',      icon: Magnet },
      { label: 'Valeur',    desc: 'Construire l\'actif',      icon: Gem },
    ],
  },
];

function CapitaineDuNavire() {
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    if (!opened) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setOpened(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [opened]);

  const capitaineStep = {
    num: '12h',
    label: 'Le Capitaine du navire',
    icon: Crown,
    accent: 'Coaching dirigeant · INCLUS DANS L\'ACCOMPAGNEMENT',
    longDesc: 'Sur 90 jours, on travaille VOUS — pas seulement votre resto. 12h de coaching individuel pour transformer un opérateur en dirigeant capable de faire grandir une marque. Vous sortez avec un cap clair, des décisions tranchées, une équipe alignée.',
    deliverables: [
      '12h de coaching individuel (1h × 12 sessions ou 2h × 6) — adaptable',
      'Plan stratégique 90j personnalisé (Vision · Organisation · Leadership · Croissance)',
      'Journal de bord du dirigeant (rituels quotidiens, hebdo, mensuels)',
      'Cartographie de tes rôles : opérateur vs dirigeant vs investisseur',
      'Outils concrets : priorités visuelles, points hebdos 1:1, tableau de bord',
      'Accès WhatsApp prioritaire entre les sessions (réponse < 4h)',
      'Bilan + plan d\'action post-programme pour les 90j suivants',
    ],
  };
  return (
    <div data-testid="capitaine-navire">
      <SectionHeader
        overline="Coaching dirigeant · INCLUS"
        title="Le Capitaine du navire."
        subtitle="On forme le dirigeant qui transforme son restaurant en une marque qui performe."
      />

      {/* iOS app-style header — Compass app + label "12h Coaching" */}
      <div className="px-4 mb-4">
        <button
          type="button"
          onClick={() => setOpened(true)}
          className="relative w-full flex items-center gap-4 active:scale-[0.98] transition-transform"
          data-testid="capitaine-card-tappable"
        >
          {/* App icon — black mat, gold compass */}
          <div className="relative shrink-0">
            <span
              className="flex items-center justify-center"
              style={{
                width: 76, height: 76,
                borderRadius: 18,
                background: 'linear-gradient(160deg, #0E1424 0%, #050811 100%)',
                border: '0.5px solid rgba(201,168,122,0.35)',
                boxShadow: '0 10px 22px -8px rgba(0,0,0,0.55), inset 0 1px 0 0 rgba(255,255,255,0.05), inset 0 0 0 0.5px rgba(201,168,122,0.12)',
              }}
            >
              <svg width="42" height="42" viewBox="0 0 32 32" aria-hidden>
                <g stroke="#C9A87A" strokeWidth="0.9" fill="none">
                  <circle cx="16" cy="16" r="12" />
                  <circle cx="16" cy="16" r="6.5" />
                </g>
                <g fill="#C9A87A">
                  <polygon points="16,3 19,16 16,13 13,16" />
                  <polygon points="16,29 19,16 16,19 13,16" opacity="0.55" />
                  <polygon points="3,16 16,13 13,16 16,19" opacity="0.55" />
                  <polygon points="29,16 16,13 19,16 16,19" opacity="0.55" />
                </g>
              </svg>
            </span>
            {/* iOS notification badge */}
            <span
              className="absolute -top-1.5 -right-1.5 flex items-center justify-center"
              style={{
                minWidth: 22, height: 22, borderRadius: 999,
                background: '#34C759',
                color: '#FFFFFF',
                fontFamily: theme.fontMono,
                fontSize: 8.5, fontWeight: 800, letterSpacing: '0.12em',
                padding: '0 6px',
                border: '1.5px solid #FFFFFF',
                boxShadow: '0 2px 5px rgba(0,0,0,0.18)',
                textTransform: 'uppercase',
              }}
              data-testid="capitaine-included-badge"
            >
              INCLUS
            </span>
          </div>

          {/* Label (right side) */}
          <div className="flex-1 min-w-0 text-left">
            <p
              style={{
                fontFamily: theme.fontDisplay,
                fontSize: 11,
                fontWeight: 700,
                color: theme.inkMuted,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                marginBottom: 4,
              }}
            >
              Coaching dirigeant
            </p>
            <p style={{ fontFamily: theme.fontDisplay, fontSize: 26, fontWeight: 800, color: theme.inkDeep, letterSpacing: '-0.025em', lineHeight: 1.0 }}>
              12<span style={{ fontSize: 16, fontWeight: 600, opacity: 0.7 }}>h</span>
              <span style={{ color: '#A87C4F', marginLeft: 6 }}>· Capitaine</span>
            </p>
            <p style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 12, color: theme.inkBody, marginTop: 5, lineHeight: 1.35 }}>
              90 jours pour transformer votre vision en une marque qui performe.
            </p>
          </div>
        </button>
      </div>

      {/* iOS Home Screen — light bg, only apps in black mat · 16 apps groupées par pilier */}
      <div className="px-4 mb-3">
        <div
          className="relative"
          data-testid="capitaine-homescreen"
        >
          <div className="relative flex flex-col gap-4">
            {CAPITAINE_PILLARS.map((p) => (
              <div key={p.num} data-testid={`capitaine-pillar-${p.num}`}>
                {/* Pillar header (overline) */}
                <div className="flex items-center gap-2 mb-2 px-1">
                  <span
                    style={{
                      fontFamily: theme.fontMono,
                      fontSize: 8.5,
                      fontWeight: 800,
                      letterSpacing: '0.18em',
                      color: '#A87C4F',
                      textTransform: 'uppercase',
                    }}
                  >
                    {p.num}
                  </span>
                  <span
                    style={{
                      fontFamily: theme.fontDisplay,
                      fontSize: 11,
                      fontWeight: 700,
                      color: theme.inkDeep,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {p.label}
                  </span>
                  <span style={{ flex: 1, height: 0.5, background: 'rgba(60,60,67,0.12)' }} />
                  <span
                    style={{
                      fontFamily: 'Georgia, serif',
                      fontStyle: 'italic',
                      fontSize: 9.5,
                      color: theme.inkMuted,
                    }}
                  >
                    {p.headline}
                  </span>
                </div>

                {/* 4 black-mat app icons */}
                <div className="grid grid-cols-4 gap-x-2 gap-y-1">
                  {p.apps.map((a) => {
                    const Icon = a.icon;
                    return (
                      <div
                        key={a.label}
                        className="flex flex-col items-center"
                        data-testid={`capitaine-app-${p.num}-${a.label.toLowerCase().replace(/\W+/g, '-')}`}
                      >
                        <div className="relative">
                          <span
                            className="flex items-center justify-center"
                            style={{
                              width: 54, height: 54,
                              borderRadius: 14,
                              background: 'linear-gradient(160deg, #0E1424 0%, #050811 100%)',
                              border: '0.5px solid rgba(201,168,122,0.30)',
                              boxShadow: '0 6px 14px -4px rgba(0,0,0,0.45), inset 0 1px 0 0 rgba(255,255,255,0.04), inset 0 0 0 0.5px rgba(201,168,122,0.10)',
                            }}
                          >
                            <Icon className="w-[22px] h-[22px]" style={{ color: p.accent }} strokeWidth={1.8} />
                          </span>
                        </div>
                        <p
                          className="mt-1.5 text-center"
                          style={{
                            fontFamily: theme.fontDisplay,
                            fontSize: 9.5,
                            fontWeight: 600,
                            color: theme.inkDeep,
                            letterSpacing: '-0.01em',
                            lineHeight: 1.1,
                            maxWidth: 72,
                          }}
                        >
                          {a.label}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Page indicator dots */}
          <div className="relative flex items-center justify-center gap-1.5 mt-4">
            <span style={{ width: 5, height: 5, borderRadius: 999, background: 'rgba(168,124,79,0.25)' }} />
            <span style={{ width: 5, height: 5, borderRadius: 999, background: 'rgba(168,124,79,0.85)' }} />
            <span style={{ width: 5, height: 5, borderRadius: 999, background: 'rgba(168,124,79,0.25)' }} />
          </div>
        </div>
      </div>

      {/* Quote */}
      <div className="px-5 mb-3">
        <div
          style={{
            borderTop: '0.5px solid rgba(201, 168, 122, 0.40)',
            borderBottom: '0.5px solid rgba(201, 168, 122, 0.40)',
            padding: '12px 6px',
          }}
        >
          <p
            className="text-center"
            style={{
              fontFamily: 'Georgia, serif',
              fontStyle: 'italic',
              fontSize: 12.5,
              color: theme.inkBody,
              lineHeight: 1.5,
            }}
          >
            « Je ne viens pas gérer votre restaurant.
            <br />
            Je vous forme à devenir <span style={{ color: '#A87C4F', fontWeight: 600 }}>le dirigeant capable de le faire grandir.</span> »
          </p>
        </div>
      </div>

      {/* Tap hint */}
      <p
        className="px-5 mb-2 text-center"
        style={{ fontFamily: theme.fontMono, fontSize: 9.5, letterSpacing: '0.18em', textTransform: 'uppercase', color: theme.inkMuted, fontWeight: 700 }}
      >
        Tap la carte pour voir le détail des 12h
      </p>

      {opened && <MethodAppOverlay step={capitaineStep} color="#C9A87A" onClose={() => setOpened(false)} />}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Méthode — iPhone Home Screen style (4×3 = 12 apps), 90 jours
// ─────────────────────────────────────────────────────────────────────
function Methode() {
  // iOS-system palette extended to 12 distinct hues
  // Step 01 (Capitaine) uses premium gold to distinguish the coaching-dirigeant moment
  const COLORS = [
    '#D4A74C', '#FF9500', '#FFCC00', '#34C759',
    '#5AC8FA', '#007AFF', '#5856D6', '#AF52DE',
    '#FF2D55', '#A2845E', '#1FB6A0', '#FF6B35',
  ];
  const [opened, setOpened] = useState(null); // { step, color } | null
  const close = () => setOpened(null);

  // Lock body scroll when overlay open + ESC to close
  useEffect(() => {
    if (!opened) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [opened]);

  return (
    <div data-testid="methode">
      {/* Inline keyframes — pulse glow for the Capitaine (step 01) tile */}
      <style>{`
        @keyframes capitaine-pulse {
          0%, 100% {
            box-shadow:
              0 6px 14px -4px rgba(212,167,76,0.55),
              inset 0 1px 0 0 rgba(255,255,255,0.30),
              0 0 0 0 rgba(212,167,76,0.55);
          }
          50% {
            box-shadow:
              0 6px 18px -4px rgba(212,167,76,0.80),
              inset 0 1px 0 0 rgba(255,255,255,0.30),
              0 0 0 10px rgba(212,167,76,0.00);
          }
        }
        @keyframes capitaine-badge-bounce {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.08); }
        }
      `}</style>
      <SectionHeader overline="La méthode" title="12 étapes, 90 jours." subtitle="Une feuille de route claire. Chaque étape = une app. Tap pour voir le détail." />
      {/* iOS Home Screen — 4×3 grid */}
      <div
        className="mx-4 mb-3 px-3 pt-5 pb-4"
        style={{
          background: 'linear-gradient(160deg, rgba(255,255,255,0.65) 0%, rgba(242,242,247,0.55) 100%)',
          borderRadius: 22,
          border: '0.5px solid rgba(60,60,67,0.10)',
          backdropFilter: 'blur(18px) saturate(180%)',
          WebkitBackdropFilter: 'blur(18px) saturate(180%)',
          boxShadow: '0 0 0 0.5px rgba(60,60,67,0.06), 0 8px 22px -10px rgba(0,0,0,0.08)',
        }}
        data-testid="methode-homescreen"
      >
        <div className="grid grid-cols-4 gap-y-4 gap-x-2">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isCapitaine = i === 0;
            return (
              <button
                type="button"
                key={s.num}
                onClick={() => setOpened({ step: s, color: COLORS[i] })}
                className="flex flex-col items-center active:scale-[0.92] transition-transform"
                data-testid={`step-app-${s.num}`}
              >
                <div className="relative">
                  <span
                    className="flex items-center justify-center"
                    style={{
                      width: 56, height: 56,
                      borderRadius: 14,
                      background: `linear-gradient(160deg, ${COLORS[i]} 0%, ${COLORS[i]}DD 100%)`,
                      boxShadow: `0 6px 14px -4px ${COLORS[i]}66, inset 0 1px 0 0 rgba(255,255,255,0.30)`,
                      animation: isCapitaine ? 'capitaine-pulse 2.6s ease-in-out infinite' : undefined,
                    }}
                  >
                    <Icon className="w-[24px] h-[24px]" style={{ color: '#FFFFFF' }} strokeWidth={2.2} />
                  </span>
                  {/* iOS badge — step number */}
                  <span
                    className="absolute -top-1 -right-1 flex items-center justify-center"
                    style={{
                      minWidth: 18, height: 18, borderRadius: 999,
                      background: '#FF3B30',
                      color: '#FFFFFF',
                      fontFamily: theme.fontDisplay,
                      fontSize: 9.5, fontWeight: 800, letterSpacing: '-0.01em',
                      padding: '0 4px',
                      border: '1.5px solid #FFFFFF',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                    }}
                  >
                    {s.num}
                  </span>
                  {/* "INCLUS" pulsing chip — only on Capitaine step 01 */}
                  {isCapitaine && (
                    <span
                      className="absolute -bottom-1.5 left-1/2 flex items-center justify-center"
                      style={{
                        transform: 'translateX(-50%)',
                        padding: '2px 6px 2px 5px',
                        borderRadius: 999,
                        background: 'linear-gradient(135deg, #FCD34D 0%, #D4A74C 100%)',
                        color: '#2A1A05',
                        fontFamily: theme.fontMono,
                        fontSize: 8,
                        fontWeight: 900,
                        letterSpacing: '0.10em',
                        textTransform: 'uppercase',
                        border: '1.5px solid #FFFFFF',
                        boxShadow: '0 2px 6px rgba(212,167,76,0.55), inset 0 1px 0 rgba(255,255,255,0.45)',
                        whiteSpace: 'nowrap',
                        animation: 'capitaine-badge-bounce 2.2s ease-in-out infinite',
                      }}
                      data-testid="step-capitaine-inclus-badge"
                    >
                      ★ Inclus
                    </span>
                  )}
                </div>
                <p
                  className="mt-1.5 text-center"
                  style={{
                    fontFamily: theme.fontDisplay,
                    fontSize: 10.5,
                    fontWeight: isCapitaine ? 800 : 600,
                    color: theme.inkDeep,
                    letterSpacing: '-0.01em',
                    lineHeight: 1.15,
                    maxWidth: 78,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {s.label}
                </p>
              </button>
            );
          })}
        </div>
        {/* Page indicator dots */}
        <div className="flex items-center justify-center gap-1.5 mt-4">
          <span style={{ width: 6, height: 6, borderRadius: 999, background: 'rgba(60,60,67,0.25)' }} />
          <span style={{ width: 6, height: 6, borderRadius: 999, background: 'rgba(60,60,67,0.75)' }} />
          <span style={{ width: 6, height: 6, borderRadius: 999, background: 'rgba(60,60,67,0.25)' }} />
        </div>
      </div>
      <p className="px-5 mb-2" style={{ fontFamily: theme.fontSans, fontSize: 11, color: theme.inkMuted, lineHeight: 1.4, textAlign: 'center' }}>
        12 apps · 12 jalons · 90 jours pour les installer.
      </p>

      {opened && <MethodAppOverlay step={opened.step} color={opened.color} onClose={close} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// MethodAppOverlay — iOS-style app expand modal with spring animation
// ─────────────────────────────────────────────────────────────────────
function MethodAppOverlay({ step, color, onClose }) {
  const Icon = step.icon;
  return (
    <div
      className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center"
      style={{
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        animation: 'methodOverlayFade 220ms ease-out',
      }}
      onClick={onClose}
      data-testid="method-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-[400px] sm:rounded-3xl rounded-t-3xl bg-white overflow-hidden"
        style={{
          maxHeight: '90vh',
          boxShadow: '0 -30px 70px -10px rgba(0,0,0,0.45)',
          animation: 'methodSheetIn 360ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}
        data-testid="method-overlay-sheet"
      >
        {/* Hero header with the big app icon */}
        <div
          className="relative px-5 pt-7 pb-5"
          style={{
            background: `linear-gradient(180deg, ${color} 0%, ${color}DD 100%)`,
            color: '#FFFFFF',
          }}
        >
          {/* Handle bar */}
          <div className="absolute left-1/2 top-2 -translate-x-1/2">
            <span style={{ width: 38, height: 4, borderRadius: 999, background: 'rgba(255,255,255,0.40)', display: 'block' }} />
          </div>
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 flex items-center justify-center active:scale-[0.92] transition-transform"
            style={{
              width: 28, height: 28, borderRadius: 999,
              background: 'rgba(255,255,255,0.20)',
              border: '0.5px solid rgba(255,255,255,0.30)',
            }}
            data-testid="method-overlay-close"
            aria-label="Fermer"
          >
            <span style={{ fontFamily: theme.fontDisplay, fontSize: 16, fontWeight: 700, color: '#FFFFFF', lineHeight: 1, marginTop: -1 }}>×</span>
          </button>

          <div className="flex items-center gap-3">
            <div className="relative shrink-0">
              <span
                className="flex items-center justify-center"
                style={{
                  width: 64, height: 64, borderRadius: 16,
                  background: 'rgba(255,255,255,0.18)',
                  border: '0.5px solid rgba(255,255,255,0.30)',
                  boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.20)',
                }}
              >
                <Icon className="w-7 h-7" style={{ color: '#FFFFFF' }} strokeWidth={2.2} />
              </span>
              <span
                className="absolute -top-1 -right-1 flex items-center justify-center"
                style={{
                  minWidth: 20, height: 20, borderRadius: 999,
                  background: theme.bgPaper,
                  color,
                  fontFamily: theme.fontDisplay,
                  fontSize: 10.5, fontWeight: 800, letterSpacing: '-0.01em',
                  padding: '0 5px',
                  border: '1.5px solid rgba(255,255,255,0.80)',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.20)',
                }}
              >
                {step.num}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p style={{ fontFamily: theme.fontMono, fontSize: 9.5, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.80)', fontWeight: 700 }}>
                {step.accent || `Étape ${step.num} · ${step.when || ''}`}
              </p>
              <p style={{ fontFamily: theme.fontDisplay, fontSize: 24, fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.025em', lineHeight: 1.05, marginTop: 4 }}>
                {step.label}
              </p>
            </div>
          </div>
        </div>

        {/* Body — scrollable */}
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 220px)' }}>
          <div className="px-5 py-4">
            <p style={{ fontFamily: theme.fontSans, fontSize: 13.5, color: theme.inkBody, lineHeight: 1.55 }}>
              {step.longDesc}
            </p>
          </div>

          {/* Deliverables list */}
          <div className="px-4">
            <p className="px-1 mb-2" style={{ fontFamily: theme.fontMono, fontSize: 9.5, letterSpacing: '0.20em', textTransform: 'uppercase', color: theme.inkMuted, fontWeight: 700 }}>
              Ce qu'on livre concrètement
            </p>
            <div
              style={{
                background: theme.bgPaper,
                borderRadius: 14,
                border: '0.5px solid rgba(60,60,67,0.08)',
                overflow: 'hidden',
              }}
            >
              {step.deliverables.map((d, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 px-4 py-3"
                  style={{ borderTop: idx === 0 ? 'none' : '0.5px solid rgba(60,60,67,0.08)' }}
                >
                  <span
                    className="flex items-center justify-center shrink-0 mt-0.5"
                    style={{
                      width: 20, height: 20, borderRadius: 999,
                      background: `${color}18`,
                      border: `0.5px solid ${color}40`,
                    }}
                  >
                    <Check className="w-3 h-3" strokeWidth={3} style={{ color }} />
                  </span>
                  <p style={{ fontFamily: theme.fontDisplay, fontSize: 13.5, color: theme.inkDeep, lineHeight: 1.4, letterSpacing: '-0.005em' }}>
                    {d}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="px-5 py-4 pb-2" />
        </div>

        {/* Sticky CTA */}
        <div
          className="px-4 py-3"
          style={{
            borderTop: '0.5px solid rgba(60,60,67,0.10)',
            background: 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
          }}
        >
          <button
            type="button"
            onClick={() => { onClose(); openSabril(); }}
            className="w-full active:scale-[0.97] transition-transform inline-flex items-center justify-center gap-2"
            style={{
              background: `linear-gradient(180deg, ${color} 0%, ${color}CC 100%)`,
              color: '#FFFFFF',
              padding: '13px 18px',
              borderRadius: 14,
              fontFamily: theme.fontDisplay, fontSize: 14, fontWeight: 700, letterSpacing: '-0.005em',
              boxShadow: `0 8px 18px -6px ${color}80`,
            }}
            data-testid="method-overlay-cta"
          >
            <MessageCircle className="w-3.5 h-3.5" strokeWidth={2.4} />
            En parler à Sabril
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Témoignages — small horizontal cards
// ─────────────────────────────────────────────────────────────────────
function Temoignages() {
  return (
    <div data-testid="temoignages">
      <SectionHeader overline="Ils nous font confiance" title="Quelques marques qu'on a construites." subtitle="Cannes, Marrakech, Naples — partout où une marque mérite d'exister." />
      <div className="overflow-x-auto px-5 pb-3 no-scrollbar">
        <div className="flex gap-2.5" style={{ minWidth: 'min-content' }}>
          {TESTIMONIALS.map((t, i) => (
            <div
              key={t.name}
              className="flex-shrink-0"
              style={{
                width: 220,
                background: theme.bgPaper,
                borderRadius: 14,
                padding: 14,
                boxShadow: '0 0 0 0.5px rgba(60,60,67,0.12), 0 4px 12px rgba(0,0,0,0.04)',
              }}
              data-testid={`testimonial-${i}`}
            >
              <div className="flex items-center justify-between mb-2">
                <p style={{ fontFamily: theme.fontMono, fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: theme.inkMuted, fontWeight: 600 }}>
                  {t.loc}
                </p>
                <span
                  style={{
                    fontFamily: theme.fontMono, fontSize: 8.5, fontWeight: 700, letterSpacing: '0.12em',
                    color: theme.inkDeep, padding: '2px 7px', background: theme.bgSecondary, borderRadius: 999,
                  }}
                >
                  {t.metric}
                </span>
              </div>
              <p style={{ fontFamily: theme.fontDisplay, fontSize: 17, fontWeight: 700, color: theme.inkDeep, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                {t.name}
              </p>
              <p style={{ fontFamily: theme.fontSans, fontSize: 12, fontStyle: 'italic', color: theme.inkBody, marginTop: 6, lineHeight: 1.4 }}>
                « {t.quote} »
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Sabril Card — director credentials, native iOS contact card style
// ─────────────────────────────────────────────────────────────────────
function SabrilCard() {
  return (
    <div className="px-4 mb-5" data-testid="sabril-card">
      <div
        className="relative overflow-hidden"
        style={{
          background: `linear-gradient(160deg, ${theme.wallet.graphite.from} 0%, ${theme.wallet.graphite.to} 100%)`,
          borderRadius: 20,
          padding: 18,
          border: '1px solid rgba(255,255,255,0.16)',
          boxShadow: '0 1px 0 0 rgba(255,255,255,0.28) inset, 0 -2px 6px rgba(0,0,0,0.30) inset, 0 16px 36px -10px rgba(0,0,0,0.50)',
        }}
      >
        <WalletCardArt palette="graphite" radius={20} />
        <div className="relative flex items-start justify-between">
          <div>
            <p style={{ fontFamily: theme.fontMono, fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.65)', fontWeight: 700 }}>
              Director · Salon Privé
            </p>
            <p style={{ fontFamily: theme.fontDisplay, fontSize: 24, fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.030em', lineHeight: 1.05, marginTop: 6 }}>
              Sabril Jurczeszyn
            </p>
            <p style={{ fontFamily: theme.fontSans, fontSize: 12, color: 'rgba(255,255,255,0.78)', marginTop: 4 }}>
              Directeur de marque · 40+ marques
            </p>
          </div>
          <Crown className="w-4 h-4" style={{ color: 'rgba(255,255,255,0.85)' }} />
        </div>
        <div
          className="relative grid grid-cols-2 gap-3 mt-4 pt-3"
          style={{ borderTop: '1px solid rgba(255,255,255,0.18)' }}
        >
          <div>
            <p style={{ fontFamily: theme.fontMono, fontSize: 8.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', fontWeight: 700 }}>
              Member since
            </p>
            <p style={{ fontFamily: theme.fontMono, fontSize: 12, color: '#FFFFFF', letterSpacing: '0.10em', marginTop: 2, fontWeight: 600 }}>2018 — ∞</p>
          </div>
          <div>
            <p style={{ fontFamily: theme.fontMono, fontSize: 8.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', fontWeight: 700 }}>
              Status
            </p>
            <p style={{ fontFamily: theme.fontMono, fontSize: 12, color: '#30D158', letterSpacing: '0.10em', marginTop: 2, fontWeight: 700 }}>● ACTIVE</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Application Form — iOS-style grouped inputs
// ─────────────────────────────────────────────────────────────────────
const SITUATIONS = [
  { value: '',        label: '— Choisir —' },
  { value: 'open',    label: 'Restaurant en activité' },
  { value: 'project', label: "Projet d'ouverture" },
  { value: 'multi',   label: 'Multi-sites / Groupe' },
];

const REVENUE_BANDS = [
  { value: '',        label: '— Choisir —' },
  { value: '<15k',    label: '< 15 000 € / mois' },
  { value: '15-30k',  label: '15 — 30 000 € / mois' },
  { value: '30-60k',  label: '30 — 60 000 € / mois' },
  { value: '60-120k', label: '60 — 120 000 € / mois' },
  { value: '120k+',   label: '> 120 000 € / mois' },
];

const DESIRED_OFFERS = [
  { value: '',         label: 'À déterminer ensemble' },
  { value: 'economie', label: 'Classe Économie' },
  { value: 'affaires', label: 'Classe Affaires' },
  { value: 'premiere', label: 'Première Classe' },
  { value: 'salon',    label: 'Salon Privé (mensuel)' },
];

function ApplicationForm() {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    restaurant_name: '', city: '', situation: '', revenue_band: '',
    desired_offer: '', motivation: '',
  });
  const [state, setState] = useState({ loading: false, error: '', success: false });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email || !form.restaurant_name) {
      setState({ loading: false, error: 'Prénom, email et nom du restaurant sont requis.', success: false });
      return;
    }
    setState({ loading: true, error: '', success: false });
    try {
      const res = await fetch(`${API_URL}/api/salon-prive/application`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, source: 'prsnl-salon-prive' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Erreur lors de l'envoi.");
      setState({ loading: false, error: '', success: true });
    } catch (err) {
      setState({ loading: false, error: err.message || 'Erreur réseau.', success: false });
    }
  };

  const inputStyle = {
    fontFamily: theme.fontDisplay, fontSize: 14, color: theme.inkDeep,
    background: 'transparent', border: 'none', outline: 'none', width: '100%',
    padding: '8px 0', letterSpacing: '-0.005em',
  };
  const labelStyle = {
    fontFamily: theme.fontDisplay, fontSize: 13, color: theme.inkDeep,
    fontWeight: 500, width: 96, flexShrink: 0, letterSpacing: '-0.005em',
  };
  const fieldRow = (label, child, last) => (
    <div
      className="flex items-center gap-2 px-4 py-1"
      style={{ borderBottom: last ? 'none' : `0.5px solid ${theme.hairline}` }}
    >
      <span style={labelStyle}>{label}</span>
      <div className="flex-1 min-w-0">{child}</div>
    </div>
  );

  if (state.success) {
    return (
      <div id="candidature" className="px-4 pb-3" data-testid="application-success">
        <div
          className="relative overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, #34C759 0%, #1FA644 100%)',
            borderRadius: 16,
            padding: 18,
            color: '#FFFFFF',
          }}
        >
          <div
            className="inline-flex items-center justify-center"
            style={{ width: 36, height: 36, borderRadius: 999, background: 'rgba(255,255,255,0.20)', border: '1px solid rgba(255,255,255,0.30)' }}
          >
            <Check className="w-5 h-5" strokeWidth={3} />
          </div>
          <p style={{ fontFamily: theme.fontDisplay, fontSize: 18, fontWeight: 700, marginTop: 10, letterSpacing: '-0.02em' }}>
            Merci{form.first_name ? `, ${form.first_name}` : ''}.
          </p>
          <p style={{ fontFamily: theme.fontSans, fontSize: 12.5, opacity: 0.92, marginTop: 4, lineHeight: 1.4 }}>
            Sabril étudie votre dossier personnellement. Réponse par message sous 48h.
          </p>
          <button
            type="button"
            onClick={openSabril}
            className="mt-3 inline-flex items-center gap-1.5 active:scale-[0.97] transition-transform"
            style={{
              background: theme.bgPaper, color: '#1FA644',
              padding: '8px 14px', borderRadius: 999,
              fontFamily: theme.fontDisplay, fontSize: 12.5, fontWeight: 700,
            }}
            data-testid="success-cta-sabril"
          >
            <MessageCircle className="w-3.5 h-3.5" strokeWidth={2.4} /> Discuter en attendant
          </button>
        </div>
      </div>
    );
  }

  return (
    <div id="candidature" data-testid="application-form">
      <SectionHeader overline="Candidater" title="Envoyer votre dossier." subtitle="Présentez votre projet. Sabril vous répond personnellement sous 48 heures." />
      <form onSubmit={submit} data-testid="salon-prive-form">
        <ListGroup>
          {fieldRow('Prénom', <input type="text" required placeholder="Sabril" value={form.first_name} onChange={set('first_name')} style={inputStyle} data-testid="form-first-name" />)}
          {fieldRow('Nom',    <input type="text" placeholder="Jurczeszyn" value={form.last_name} onChange={set('last_name')} style={inputStyle} data-testid="form-last-name" />)}
          {fieldRow('Email',  <input type="email" required placeholder="s@brand.com" value={form.email} onChange={set('email')} style={inputStyle} data-testid="form-email" />)}
          {fieldRow('Tél.',   <input type="tel" placeholder="WhatsApp · pour caler un café" value={form.phone} onChange={set('phone')} style={inputStyle} data-testid="form-phone" />, true)}
        </ListGroup>
        <ListGroup>
          {fieldRow('Resto.',  <input type="text" required placeholder="Maison Avocado" value={form.restaurant_name} onChange={set('restaurant_name')} style={inputStyle} data-testid="form-restaurant" />)}
          {fieldRow('Ville',   <input type="text" placeholder="Marrakech" value={form.city} onChange={set('city')} style={inputStyle} data-testid="form-city" />)}
          {fieldRow('Situat.', <select value={form.situation} onChange={set('situation')} style={inputStyle} data-testid="form-situation">{SITUATIONS.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}</select>)}
          {fieldRow('CA/mois', <select value={form.revenue_band} onChange={set('revenue_band')} style={inputStyle} data-testid="form-revenue">{REVENUE_BANDS.map((r) => (<option key={r.value} value={r.value}>{r.label}</option>))}</select>)}
          {fieldRow('Offre',   <select value={form.desired_offer} onChange={set('desired_offer')} style={inputStyle} data-testid="form-offer">{DESIRED_OFFERS.map((o) => (<option key={o.value || 'none'} value={o.value}>{o.label}</option>))}</select>, true)}
        </ListGroup>
        <div className="px-4 mb-3">
          <div style={{ background: theme.bgPaper, borderRadius: 14, padding: '10px 12px', boxShadow: '0 0 0 0.5px rgba(60,60,67,0.10), 0 1px 2px rgba(0,0,0,0.03)' }}>
            <p style={{ fontFamily: theme.fontMono, fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: theme.inkMuted, fontWeight: 600, marginBottom: 6 }}>
              Votre projet
            </p>
            <textarea
              value={form.motivation}
              onChange={set('motivation')}
              rows={3}
              placeholder="Où vous en êtes, ce que vous cherchez, l'obstacle actuel…"
              style={{ ...inputStyle, resize: 'none', lineHeight: 1.4, fontSize: 13, padding: 0 }}
              data-testid="form-motivation"
            />
          </div>
        </div>
        {state.error && (
          <p className="px-5" style={{ fontFamily: theme.fontMono, fontSize: 11, color: '#FF3B30', letterSpacing: '0.02em' }} data-testid="form-error">
            {state.error}
          </p>
        )}
        <div className="px-4 mb-3">
          <button
            type="submit"
            disabled={state.loading}
            className="w-full inline-flex items-center justify-center gap-2 active:scale-[0.97] transition-transform disabled:opacity-60"
            style={{
              background: 'linear-gradient(180deg, #007AFF 0%, #0051D5 100%)',
              color: '#FFFFFF',
              padding: '13px 18px',
              borderRadius: 14,
              fontFamily: theme.fontDisplay, fontSize: 14, fontWeight: 600, letterSpacing: '-0.005em',
              boxShadow: '0 1px 0 0 rgba(255,255,255,0.22) inset, 0 8px 20px -6px rgba(0,122,255,0.45)',
            }}
            data-testid="form-submit"
          >
            {state.loading
              ? <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.4} /> Envoi…</>
              : <><Send className="w-3.5 h-3.5" strokeWidth={2.4} /> Envoyer le dossier</>
            }
          </button>
          <p className="text-center" style={{ fontFamily: theme.fontMono, fontSize: 10, color: theme.inkMuted, letterSpacing: '0.14em', textTransform: 'uppercase', marginTop: 6 }}>
            Réponse sous 48 heures
          </p>
        </div>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// MeetingFlow — 2 cards d'action en fin de page (café ou chat)
// Plus de "3 étapes" : le visiteur est déjà convaincu, il a juste à choisir.
// ─────────────────────────────────────────────────────────────────────
const MEETING_FLOW_HERO = 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/1kjseyqc_Capture%20d%E2%80%99e%CC%81cran%202026-07-01%20a%CC%80%2000.04.53.png';

function MeetingFlow() {
  const scrollToId = (id) => {
    try { navigator.vibrate?.(12); } catch (_) { /* noop */ }
    const target = document.getElementById(id);
    if (target) {
      const top = target.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <div className="px-4" data-testid="meeting-flow">
      <style>{`
        @keyframes meeting-shine {
          0%   { transform: translateX(-120%) skewX(-18deg); opacity: 0; }
          40%  { opacity: 0.45; }
          100% { transform: translateX(220%)  skewX(-18deg); opacity: 0; }
        }
        @keyframes meeting-pulse-dot {
          0%, 100% { opacity: 1;   transform: scale(1); }
          50%      { opacity: 0.5; transform: scale(0.88); }
        }
      `}</style>

      <SectionHeader
        overline="Prochaine étape"
        title="Prenons un café ou discutons avant."
        subtitle="Vous choisissez le format qui vous convient — un café en présentiel ou un message direct."
      />

      {/* Hero photo banner — voiture jaune MOTEK · esprit voyage / signature */}
      <div
        className="relative overflow-hidden mb-4"
        style={{
          borderRadius: 18,
          aspectRatio: '16 / 11',
          background: 'linear-gradient(180deg, #FFFBEF 0%, #FFF0BF 100%)',
          border: '0.5px solid rgba(60,60,67,0.10)',
          boxShadow: '0 0 0 0.5px rgba(60,60,67,0.06), 0 14px 32px -14px rgba(254,140,0,0.30)',
        }}
        data-testid="meeting-flow-hero-photo"
      >
        <img
          src={MEETING_FLOW_HERO}
          alt="Direction signature — la table sous le capot jaune"
          loading="lazy"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: '50% 40%',
            display: 'block',
          }}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0"
          style={{
            height: '50%',
            background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.45) 70%, rgba(0,0,0,0.70) 100%)',
          }}
        />
        <div
          className="absolute top-3 left-3 inline-flex items-center gap-1.5"
          style={{
            background: 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(12px) saturate(180%)',
            WebkitBackdropFilter: 'blur(12px) saturate(180%)',
            borderRadius: 999,
            padding: '4px 9px 4px 7px',
            border: '0.5px solid rgba(60,60,67,0.10)',
            boxShadow: '0 4px 10px -3px rgba(0,0,0,0.15)',
          }}
        >
          <Sparkles className="w-3 h-3" strokeWidth={2.4} style={{ color: '#FE8C00' }} />
          <span style={{
            fontFamily: theme.fontMono,
            fontSize: 9,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: theme.inkDeep,
            fontWeight: 700,
          }}>
            Direction signature
          </span>
        </div>
        <p
          className="absolute inset-x-0 bottom-0 px-4 pb-3"
          style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontStyle: 'italic',
            fontSize: 12,
            color: '#FFFFFF',
            lineHeight: 1.35,
            letterSpacing: '-0.005em',
            textShadow: '0 1px 4px rgba(0,0,0,0.40)',
          }}
        >
          « On ne livre pas un service. On livre une scène — un univers, un détail, une émotion. »
        </p>
      </div>

      {/* 2 action cards — café & chat */}
      <div className="flex flex-col gap-3">
        {/* ───── Café IRL — premium amber/coffee ───── */}
        <article
          className="relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #FE8C00 0%, #A87C4F 100%)',
            borderRadius: 20,
            padding: '16px 16px 14px',
            color: '#FFFFFF',
            boxShadow: '0 0 0 0.5px rgba(168,124,79,0.18), 0 18px 36px -16px rgba(168,124,79,0.55)',
          }}
          data-testid="meeting-action-cafe"
        >
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background: 'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.22) 50%, transparent 70%)',
              animation: 'meeting-shine 5.5s ease-in-out infinite',
              mixBlendMode: 'overlay',
            }}
          />
          <div className="relative flex items-start gap-3">
            <span
              className="inline-flex items-center justify-center flex-shrink-0"
              style={{
                width: 44, height: 44, borderRadius: 14,
                background: 'rgba(255,255,255,0.18)',
                border: '0.5px solid rgba(255,255,255,0.30)',
                boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.25)',
              }}
            >
              <Coffee className="w-5 h-5" strokeWidth={2.2} style={{ color: '#FFFFFF' }} />
            </span>
            <div className="flex-1 min-w-0">
              <p style={{
                fontFamily: theme.fontMono,
                fontSize: 9.5,
                letterSpacing: '0.24em',
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.82)',
                fontWeight: 700,
              }}>
                Le format que je préfère
              </p>
              <p style={{
                fontFamily: theme.fontDisplay,
                fontSize: 18,
                fontWeight: 800,
                color: '#FFFFFF',
                letterSpacing: '-0.025em',
                lineHeight: 1.1,
                marginTop: 3,
              }}>
                Prenons un café ensemble.
              </p>
              <p style={{
                fontFamily: theme.fontSans,
                fontSize: 12.5,
                color: 'rgba(255,255,255,0.92)',
                lineHeight: 1.45,
                marginTop: 5,
              }}>
                On se voit en vrai · IRL dans une de mes villes (Marrakech · Marbella · Cannes · Paris · Miami).
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => scrollToId('section-cafe-booking')}
            className="w-full mt-3.5 inline-flex items-center justify-center gap-2 active:scale-[0.97] transition-transform"
            style={{
              background: theme.bgPaper,
              color: '#A87C4F',
              borderRadius: 999,
              padding: '11px 16px',
              fontFamily: theme.fontDisplay,
              fontSize: 13.5,
              fontWeight: 800,
              letterSpacing: '-0.005em',
              boxShadow: '0 6px 14px -4px rgba(0,0,0,0.25)',
            }}
            data-testid="meeting-action-cafe-cta"
          >
            <Coffee className="w-4 h-4" strokeWidth={2.4} />
            Choisir ma date · IRL
          </button>
        </article>

        {/* ───── Discuter — chat iOS blue ───── */}
        <article
          className="relative overflow-hidden"
          style={{
            background: theme.bgPaper,
            borderRadius: 20,
            padding: '16px 16px 14px',
            border: '0.5px solid rgba(0,122,255,0.15)',
            boxShadow: '0 0 0 0.5px rgba(0,122,255,0.18), 0 12px 26px -14px rgba(0,122,255,0.30)',
          }}
          data-testid="meeting-action-chat"
        >
          <div className="flex items-start gap-3">
            <span
              className="inline-flex items-center justify-center flex-shrink-0"
              style={{
                width: 44, height: 44, borderRadius: 14,
                background: 'linear-gradient(180deg, #007AFF 0%, #0A84FF 100%)',
                boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.40), 0 4px 10px -2px rgba(0,122,255,0.45)',
              }}
            >
              <MessageCircle className="w-5 h-5" strokeWidth={2.2} style={{ color: '#FFFFFF' }} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span
                  aria-hidden
                  className="block"
                  style={{
                    width: 7, height: 7, borderRadius: 999,
                    background: '#34C759',
                    boxShadow: '0 0 0 2.5px rgba(52,199,89,0.18)',
                    animation: 'meeting-pulse-dot 1.6s ease-in-out infinite',
                  }}
                />
                <p style={{
                  fontFamily: theme.fontMono,
                  fontSize: 9.5,
                  letterSpacing: '0.24em',
                  textTransform: 'uppercase',
                  color: '#007AFF',
                  fontWeight: 700,
                }}>
                  En ligne · réponse perso
                </p>
              </div>
              <p style={{
                fontFamily: theme.fontDisplay,
                fontSize: 18,
                fontWeight: 800,
                color: theme.inkDeep,
                letterSpacing: '-0.025em',
                lineHeight: 1.1,
                marginTop: 3,
              }}>
                Discuter avant.
              </p>
              <p style={{
                fontFamily: theme.fontSans,
                fontSize: 12.5,
                color: theme.inkMuted,
                lineHeight: 1.45,
                marginTop: 5,
              }}>
                Une question avant de vous engager ? Ouvre le chat — réponse perso, à votre rythme.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => openSabril()}
            className="w-full mt-3.5 inline-flex items-center justify-center gap-2 active:scale-[0.97] transition-transform"
            style={{
              background: 'linear-gradient(180deg, #007AFF 0%, #0A84FF 100%)',
              color: '#FFFFFF',
              borderRadius: 999,
              padding: '11px 16px',
              fontFamily: theme.fontDisplay,
              fontSize: 13.5,
              fontWeight: 800,
              letterSpacing: '-0.005em',
              boxShadow: '0 6px 14px -4px rgba(0,122,255,0.50)',
            }}
            data-testid="meeting-action-chat-cta"
          >
            <MessageCircle className="w-4 h-4" strokeWidth={2.4} />
            Ouvrir le chat
          </button>
        </article>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// TourAgenda — Sabril sur la route, calendrier des cafés IRL
// ─────────────────────────────────────────────────────────────────────
const SABRIL_TOUR = [
  { range: '1 — 15 juil.', city: 'Marrakech', country: 'MA', tint: '#FE8C00', emoji: '🇲🇦', slots: 5, booked: 4 },
  { range: '15 — 22 juil.', city: 'Marbella', country: 'ES', tint: '#34C759', emoji: '🇪🇸', slots: 5, booked: 3 },
  { range: '22 — 30 juil.', city: 'Cannes', country: 'FR', tint: '#007AFF', emoji: '🇫🇷', slots: 5, booked: 3 },
  { range: '1 — 15 août',  city: 'Marrakech', country: 'MA', tint: '#FE8C00', emoji: '🇲🇦', slots: 5, booked: 2 },
  { range: '15 — 30 août', city: 'Paris', country: 'FR', tint: '#AF52DE', emoji: '🇫🇷', slots: 5, booked: 4 },
  { range: '1 — 15 sept.', city: 'Miami', country: 'US', tint: '#FF2D55', emoji: '🇺🇸', slots: 5, booked: 2 },
];

function TourAgenda() {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [open, setOpen] = useState(null); // { city, date_range, tint }
  const [form, setForm] = useState({ first_name: '', phone: '', note: '' });
  const [state, setState] = useState({ loading: false, error: '', success: false });

  const reset = () => {
    setForm({ first_name: '', phone: '', note: '' });
    setState({ loading: false, error: '', success: false });
  };
  const close = () => { setOpen(null); reset(); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.phone) {
      setState({ loading: false, error: 'Prénom et WhatsApp requis.', success: false });
      return;
    }
    setState({ loading: true, error: '', success: false });
    try {
      const sessionId = (typeof window !== 'undefined' && window.localStorage.getItem('sabril_session_id')) || `tour-${Date.now()}`;
      const res = await fetch(`${API_URL}/api/sabril/tour/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          city: open.city,
          date_range: open.date_range,
          session_id: sessionId,
          first_name: form.first_name,
          phone: form.phone,
          note: form.note,
          page: 'prsnl',
        }),
      });
      if (!res.ok) throw new Error('Envoi impossible. Réessaye dans une minute.');
      setState({ loading: false, error: '', success: true });
    } catch (err) {
      setState({ loading: false, error: err.message || 'Erreur réseau.', success: false });
    }
  };
  return (
    <div className="mt-4" data-testid="tour-agenda">
      {/* iOS Calendar app style header */}
      <div
        className="relative overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFAFB 100%)',
          borderRadius: 18,
          border: '0.5px solid rgba(60,60,67,0.10)',
          boxShadow: '0 0 0 0.5px rgba(60,60,67,0.06), 0 10px 26px -10px rgba(0,0,0,0.10)',
        }}
      >
        {/* Header — iOS Calendar style */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2" style={{ borderBottom: '0.5px solid rgba(60,60,67,0.12)' }}>
          <div className="flex items-center gap-2">
            <span
              className="flex items-center justify-center"
              style={{
                width: 26, height: 26, borderRadius: 7,
                background: 'linear-gradient(180deg, #FF3B30 0%, #D70015 100%)',
                boxShadow: '0 3px 8px -2px rgba(255,59,48,0.45)',
              }}
            >
              <span style={{ fontFamily: theme.fontDisplay, fontSize: 11, color: '#FFFFFF', fontWeight: 800, letterSpacing: '-0.02em' }}>
                26
              </span>
            </span>
            <div>
              <p style={{ fontFamily: theme.fontDisplay, fontSize: 13, fontWeight: 700, color: theme.inkDeep, letterSpacing: '-0.015em', lineHeight: 1 }}>
                Tournée Sabril
              </p>
              <p style={{ fontFamily: theme.fontMono, fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: theme.inkMuted, fontWeight: 700, marginTop: 2 }}>
                Été 2026 · Café IRL
              </p>
            </div>
          </div>
          <span
            className="inline-flex items-center gap-1"
            style={{
              background: 'rgba(52,199,89,0.12)',
              color: '#1FA644',
              borderRadius: 999,
              padding: '3px 8px',
              fontFamily: theme.fontMono,
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
            }}
          >
            <span style={{ width: 5, height: 5, borderRadius: 999, background: '#34C759' }} />
            Live
          </span>
        </div>

        {/* Events list */}
        <div className="px-2 py-1">
          {SABRIL_TOUR.map((t, i) => {
            const isFull = false; // Rule: no destination is ever marked "complet"
            const remaining = Math.max(1, t.slots - t.booked);
            const pct = Math.min(85, Math.round((t.booked / t.slots) * 100));
            return (
              <div
                key={`${t.range}-${i}`}
                className="flex items-start gap-3 px-2 py-2.5"
                style={{ borderBottom: i === SABRIL_TOUR.length - 1 ? 'none' : '0.5px solid rgba(60,60,67,0.08)' }}
                data-testid={`tour-${t.city.toLowerCase()}-${i}`}
              >
                {/* Color bar — iOS Calendar event indicator */}
                <span
                  className="flex-shrink-0 mt-0.5"
                  style={{
                    width: 3, height: 38, borderRadius: 999, background: t.tint,
                    opacity: isFull ? 0.4 : 1,
                  }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <p style={{ fontFamily: theme.fontDisplay, fontSize: 13, fontWeight: 700, color: theme.inkDeep, letterSpacing: '-0.015em', lineHeight: 1.15 }}>
                      {t.city} <span style={{ fontSize: 12, marginLeft: 2 }}>{t.emoji}</span>
                    </p>
                    <p style={{ fontFamily: theme.fontMono, fontSize: 9.5, color: isFull ? '#FF3B30' : t.tint, fontWeight: 700, letterSpacing: '0.04em', flexShrink: 0 }}>
                      {t.booked}/{t.slots}
                    </p>
                  </div>
                  <p style={{ fontFamily: theme.fontMono, fontSize: 10, color: theme.inkMuted, fontWeight: 600, letterSpacing: '0.04em', marginTop: 2 }}>
                    {t.range}
                  </p>
                  {/* Progress bar */}
                  <div
                    className="mt-1.5"
                    style={{
                      width: '100%', height: 3, borderRadius: 999,
                      background: 'rgba(60,60,67,0.08)',
                      overflow: 'hidden',
                    }}
                    aria-label={`${t.booked} cafés sur ${t.slots} déjà calés`}
                  >
                    <div
                      style={{
                        width: `${pct}%`,
                        height: '100%',
                        borderRadius: 999,
                        background: isFull
                          ? 'linear-gradient(90deg, #FF3B30 0%, #D70015 100%)'
                          : `linear-gradient(90deg, ${t.tint} 0%, ${t.tint}CC 100%)`,
                        transition: 'width 600ms ease-out',
                      }}
                    />
                  </div>
                </div>
                {isFull ? (
                  <span
                    className="flex-shrink-0 mt-0.5"
                    style={{
                      background: 'rgba(255,59,48,0.10)',
                      color: '#FF3B30',
                      borderRadius: 999,
                      padding: '5px 10px',
                      fontFamily: theme.fontDisplay,
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: '0.02em',
                      border: '0.5px solid rgba(255,59,48,0.25)',
                      textTransform: 'uppercase',
                    }}
                    data-testid={`tour-full-${t.city.toLowerCase()}-${i}`}
                  >
                    Complet
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setOpen({ city: t.city, date_range: t.range, tint: t.tint })}
                    className="flex-shrink-0 mt-0.5 active:scale-[0.94] transition-transform"
                    style={{
                      background: `${t.tint}14`,
                      color: t.tint,
                      borderRadius: 999,
                      padding: '5px 10px',
                      fontFamily: theme.fontDisplay,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '-0.005em',
                      border: `0.5px solid ${t.tint}30`,
                    }}
                    data-testid={`tour-cta-${t.city.toLowerCase()}-${i}`}
                  >
                    {remaining === 1 ? 'Dernière place' : 'Caler un café'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <p
        className="text-center px-5 mt-2"
        style={{ fontFamily: theme.fontSans, fontSize: 11, color: theme.inkMuted, lineHeight: 1.4, fontStyle: 'italic' }}
      >
        Sabril choisit ses projets. Si on accroche, il se déplace.
      </p>

      {/* iOS Action Sheet modal — collect WhatsApp before sending notif */}
      {open && (
        <div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center"
          style={{
            background: 'rgba(0,0,0,0.45)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
          onClick={close}
          data-testid="tour-modal-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-[380px] sm:rounded-2xl rounded-t-2xl bg-white"
            style={{
              padding: 18,
              boxShadow: '0 -20px 50px -10px rgba(0,0,0,0.30)',
            }}
            data-testid="tour-modal"
          >
            {state.success ? (
              <div className="text-center py-4">
                <div
                  className="inline-flex items-center justify-center mx-auto"
                  style={{ width: 48, height: 48, borderRadius: 999, background: 'rgba(52,199,89,0.12)', border: '1px solid rgba(52,199,89,0.30)' }}
                >
                  <Check className="w-6 h-6" strokeWidth={2.8} style={{ color: '#1FA644' }} />
                </div>
                <p style={{ fontFamily: theme.fontDisplay, fontSize: 18, fontWeight: 800, color: theme.inkDeep, letterSpacing: '-0.02em', marginTop: 10 }}>
                  Demande envoyée à Sabril.
                </p>
                <p style={{ fontFamily: theme.fontSans, fontSize: 12.5, color: theme.inkBody, marginTop: 6, lineHeight: 1.45 }}>
                  Il vous confirme un créneau café à <b>{open.city}</b> ({open.date_range}) par WhatsApp dans la journée.
                </p>
                <button
                  type="button"
                  onClick={close}
                  className="mt-4 w-full active:scale-[0.97] transition-transform"
                  style={{
                    background: 'linear-gradient(180deg, #1C1C1E 0%, #000000 100%)',
                    color: '#FFFFFF',
                    padding: '12px 18px',
                    borderRadius: 14,
                    fontFamily: theme.fontDisplay, fontSize: 14, fontWeight: 700,
                  }}
                  data-testid="tour-modal-close-success"
                >
                  Parfait.
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="tour-modal-form">
                {/* Handle bar */}
                <div className="flex items-center justify-center mb-2">
                  <span style={{ width: 36, height: 4, borderRadius: 999, background: 'rgba(60,60,67,0.20)' }} />
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className="flex items-center justify-center"
                    style={{
                      width: 32, height: 32, borderRadius: 9,
                      background: `linear-gradient(160deg, ${open.tint} 0%, ${open.tint}CC 100%)`,
                      boxShadow: `0 6px 14px -4px ${open.tint}66`,
                    }}
                  >
                    <Coffee className="w-4 h-4" strokeWidth={2.4} style={{ color: '#FFFFFF' }} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p style={{ fontFamily: theme.fontDisplay, fontSize: 15, fontWeight: 800, color: theme.inkDeep, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                      Café à {open.city}
                    </p>
                    <p style={{ fontFamily: theme.fontMono, fontSize: 10, color: theme.inkMuted, fontWeight: 600, letterSpacing: '0.04em', marginTop: 2 }}>
                      {open.date_range}
                    </p>
                  </div>
                </div>
                <p style={{ fontFamily: theme.fontSans, fontSize: 12, color: theme.inkBody, lineHeight: 1.45, marginBottom: 12 }}>
                  Laisse-moi votre WhatsApp — Sabril vous confirme un créneau et un lieu sur place.
                </p>

                <div
                  style={{
                    background: '#F2F2F7',
                    borderRadius: 12,
                    padding: '10px 12px',
                    marginBottom: 8,
                  }}
                >
                  <label style={{ fontFamily: theme.fontMono, fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: theme.inkMuted, fontWeight: 700 }}>
                    Prénom
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Ton prénom"
                    value={form.first_name}
                    onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                    style={{
                      width: '100%', background: 'transparent', border: 'none', outline: 'none',
                      fontFamily: theme.fontDisplay, fontSize: 15, color: theme.inkDeep, padding: '4px 0',
                    }}
                    data-testid="tour-modal-first-name"
                  />
                </div>

                <div
                  style={{
                    background: '#F2F2F7',
                    borderRadius: 12,
                    padding: '10px 12px',
                    marginBottom: 8,
                  }}
                >
                  <label style={{ fontFamily: theme.fontMono, fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: theme.inkMuted, fontWeight: 700 }}>
                    WhatsApp
                  </label>
                  <input
                    type="tel"
                    required
                    placeholder="+33 6 …"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    style={{
                      width: '100%', background: 'transparent', border: 'none', outline: 'none',
                      fontFamily: theme.fontDisplay, fontSize: 15, color: theme.inkDeep, padding: '4px 0',
                    }}
                    data-testid="tour-modal-phone"
                  />
                </div>

                <div
                  style={{
                    background: '#F2F2F7',
                    borderRadius: 12,
                    padding: '10px 12px',
                    marginBottom: 12,
                  }}
                >
                  <label style={{ fontFamily: theme.fontMono, fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: theme.inkMuted, fontWeight: 700 }}>
                    Ton projet (optionnel)
                  </label>
                  <textarea
                    rows={2}
                    placeholder="Resto, ville, où vous en êtes…"
                    value={form.note}
                    onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                    style={{
                      width: '100%', background: 'transparent', border: 'none', outline: 'none', resize: 'none',
                      fontFamily: theme.fontDisplay, fontSize: 13.5, color: theme.inkDeep, padding: '4px 0',
                      lineHeight: 1.4,
                    }}
                    data-testid="tour-modal-note"
                  />
                </div>

                {state.error && (
                  <p style={{ fontFamily: theme.fontMono, fontSize: 11, color: '#FF3B30', marginBottom: 8 }} data-testid="tour-modal-error">
                    {state.error}
                  </p>
                )}

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={close}
                    className="flex-1 active:scale-[0.97] transition-transform"
                    style={{
                      background: '#F2F2F7',
                      color: theme.inkDeep,
                      padding: '12px 14px',
                      borderRadius: 14,
                      fontFamily: theme.fontDisplay, fontSize: 14, fontWeight: 600,
                    }}
                    data-testid="tour-modal-cancel"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={state.loading}
                    className="flex-1 active:scale-[0.97] transition-transform disabled:opacity-60"
                    style={{
                      background: `linear-gradient(180deg, ${open.tint} 0%, ${open.tint}CC 100%)`,
                      color: '#FFFFFF',
                      padding: '12px 14px',
                      borderRadius: 14,
                      fontFamily: theme.fontDisplay, fontSize: 14, fontWeight: 700,
                      boxShadow: `0 6px 14px -4px ${open.tint}66`,
                    }}
                    data-testid="tour-modal-submit"
                  >
                    {state.loading ? 'Envoi…' : 'Envoyer à Sabril'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Final dark CTA card
// ─────────────────────────────────────────────────────────────────────
function FinalCTA() {
  return (
    <div id="contact" className="px-4 mt-4 mb-2" data-testid="final-cta">
      <div
        className="relative overflow-hidden"
        style={{
          background: 'linear-gradient(160deg, #1C1C1E 0%, #000000 100%)',
          borderRadius: 20,
          padding: 18,
          boxShadow: '0 16px 38px -10px rgba(0,0,0,0.55)',
        }}
      >
        <WalletCardArt palette="graphite" radius={20} />
        <div className="relative">
          <p style={{ fontFamily: theme.fontMono, fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', fontWeight: 700 }}>
            Embarquement immédiat
          </p>
          <h3 style={{ fontFamily: theme.fontDisplay, fontSize: 22, fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.025em', lineHeight: 1.05, marginTop: 4 }}>
            Votre marque.<br />Votre fierté.<br /><span style={{ color: 'rgba(255,255,255,0.65)' }}>Notre mission.</span>
          </h3>
          <p style={{ fontFamily: theme.fontSans, fontSize: 12.5, color: 'rgba(255,255,255,0.78)', marginTop: 10, lineHeight: 1.5 }}>
            On discute en messages. Si le projet a une âme, on se voit autour d'un café — pas d'appel, juste du vrai.
          </p>
          <div className="flex items-center gap-2 mt-3">
            <button
              type="button"
              onClick={() => openSabril('question')}
              className="flex-1 inline-flex items-center justify-center gap-2 active:scale-[0.97] transition-transform"
              style={{
                background: theme.bgPaper, color: theme.inkDeep,
                padding: '12px 18px', borderRadius: 999,
                fontFamily: theme.fontDisplay, fontSize: 13.5, fontWeight: 700, letterSpacing: '-0.005em',
                boxShadow: '0 1px 0 0 rgba(255,255,255,0.90) inset, 0 8px 22px -6px rgba(0,0,0,0.40)',
              }}
              data-testid="cta-final-sabril"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={2.4} />
              Discuter avec Sabril
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// OnboardingChatCard — fake iMessage conversation that POSES THE RULES
// of the funnel (steps · price in private · coffee at the end)
// ─────────────────────────────────────────────────────────────────────
function OnboardingChatCard() {
  const BUBBLES = [
    { side: 'them', text: 'Salut Sabril 👋 Je viens d\'arriver sur votre page.' },
    { side: 'me',   text: 'Bienvenue 🙌 **Lis bien la page, étape par étape.**' },
    { side: 'me',   text: 'Vous allez découvrir mes **12 étapes sur 90 jours** pour transformer votre resto en marque 🚀' },
    { side: 'them', text: 'Ok cool, et après ? 🤔' },
    { side: 'me',   text: 'Vous choisissez **l\'offre qui vous correspond** 👇' },
    { side: 'me',   text: '🥉  **48h Express** — audit terrain commando, plan d\'action écrit sous 48h.\n🥈  **90 jours Distanciel** — pilotage stratégique à distance, 2 RDV/mois.\n🥇  **90 jours · 17 jours sur place** — immersion physique, production + mise en place des actions avec votre équipe.\n👑  **Salon Privé** — direction de marque externalisée, illimitée.' },
    { side: 'me',   text: 'Vous **candidatez** (lol) 📝 On discute en privé, je vous pose 3-4 questions ciblées, puis **je vous file le tarif net — uniquement dans le chat 🔒**' },
    { side: 'them', text: 'Et si je dis oui au tarif ? 🤝' },
    { side: 'me',   text: 'Là c\'est l\'étape qui change tout 😉' },
    { side: 'me',   text: 'Faut surtout que **votre projet me plaise** 🎯 Si oui, je prends un billet d\'avion ✈️ et **on se rencontre sur place autour d\'un café** ☕' },
    { side: 'me',   text: 'Je sélectionne sévère — je suis **limité dans le nombre de clients** que je prends par an, et je joue **résultats + performance** sur chacun. C\'est pour ça que je peux pas tout prendre 🙏' },
    { side: 'them', text: 'Et un call rapide en attendant ? 📞' },
    { side: 'me',   text: '**Pas de call téléphonique.** Jamais 🚫' },
    { side: 'me',   text: 'Un call ne dit rien. Je veux **voir** votre resto, sentir l\'odeur de votre cuisine 🍳, croiser votre équipe 👥, regarder votre carte en vrai. C\'est sur place qu\'on sait si ça matche ✨' },
    { side: 'me',   text: 'Et un dernier truc : **j\'accompagne 13 clients par an. Max** 👑 C\'est pour ça que je sélectionne autant.' },
  ];

  return (
    <div className="px-4 my-4" data-testid="onboarding-chat-card">
      <div
        className="relative"
        style={{
          background: '#EFE9DC', // WhatsApp-style warm sand background
          borderRadius: 22,
          padding: '14px 14px 12px',
          border: '0.5px solid rgba(60,60,67,0.10)',
          boxShadow: '0 12px 28px -14px rgba(26,23,20,0.14)',
        }}
      >
        {/* Top label */}
        <div className="flex items-center gap-2 mb-3 px-1">
          <span
            className="inline-flex items-center justify-center"
            style={{
              width: 22, height: 22, borderRadius: 999,
              background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
              boxShadow: '0 4px 10px -4px rgba(37,211,102,0.45)',
            }}
          >
            <MessageCircle className="w-3 h-3 text-white" strokeWidth={2.6} />
          </span>
          <div className="flex-1 min-w-0">
            <p style={{ fontFamily: theme.fontDisplay, fontSize: 13, fontWeight: 700, color: theme.inkDeep, lineHeight: 1, letterSpacing: '-0.005em' }}>
              Sabril
            </p>
            <p style={{ fontFamily: theme.fontMono, fontSize: 8.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#1FA644', fontWeight: 700, marginTop: 2 }}>
              ● En ligne · répond en 4 min
            </p>
          </div>
          <span
            style={{
              fontFamily: theme.fontMono,
              fontSize: 8.5,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: '#A87C4F',
              fontWeight: 800,
              background: 'rgba(168,124,79,0.10)',
              border: '0.5px solid rgba(168,124,79,0.35)',
              padding: '3px 7px',
              borderRadius: 999,
            }}
          >
            13 clients/an
          </span>
        </div>

        {/* Conversation */}
        <div className="flex flex-col gap-1.5">
          {BUBBLES.map((b, i) => {
            const isMe = b.side === 'me';
            return (
              <div
                key={i}
                className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                style={{ animation: `heroFadeUp 0.5s ${i * 0.08}s both ease-out` }}
              >
                <div
                  style={{
                    maxWidth: '80%',
                    background: isMe ? '#DCF8C6' : '#FFFFFF',
                    color: '#0B141A',
                    padding: '8px 11px 9px',
                    borderRadius: 14,
                    borderTopRightRadius: isMe ? 4 : 14,
                    borderTopLeftRadius: isMe ? 14 : 4,
                    fontFamily: theme.fontSans,
                    fontSize: 13,
                    lineHeight: 1.42,
                    letterSpacing: '-0.005em',
                    boxShadow: '0 1px 1.5px rgba(0,0,0,0.08)',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: b.text
                      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                      .replace(/\n/g, '<br/>'),
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* Footer rule */}
        <div
          className="mt-3 pt-3 flex items-center gap-2"
          style={{ borderTop: '0.5px solid rgba(60,60,67,0.08)' }}
        >
          <span style={{ fontSize: 14 }}>🔒</span>
          <p style={{ fontFamily: theme.fontMono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: theme.inkMuted, fontWeight: 700, lineHeight: 1.35 }}>
            Tarif en privé · billet d'avion uniquement si accord
          </p>
        </div>

        {/* CTA */}
        <button
          type="button"
          onClick={() => openSabril('question')}
          className="w-full mt-3 inline-flex items-center justify-center gap-2 active:scale-[0.97] transition-transform"
          style={{
            background: 'linear-gradient(180deg, #25D366 0%, #128C7E 100%)',
            color: '#FFFFFF',
            padding: '12px 16px',
            borderRadius: 999,
            fontFamily: theme.fontDisplay,
            fontSize: 13.5,
            fontWeight: 700,
            letterSpacing: '-0.005em',
            boxShadow: '0 10px 22px -8px rgba(37,211,102,0.45)',
          }}
          data-testid="onboarding-chat-cta"
        >
          <MessageCircle className="w-4 h-4" strokeWidth={2.4} />
          Démarrer la discussion
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// HeroCover — couverture style livre/magazine, intro premium
// Hero : journal Maison Avocado · bulle avatar Sabril · bio intégrée
// ─────────────────────────────────────────────────────────────────────
const MAISON_AVOCADO_JOURNAL = 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/m8408ew1_Capture%20d%E2%80%99e%CC%81cran%202026-07-01%20a%CC%80%2000.04.38.png';

function HeroCover() {
  const navigate = useNavigate();
  const scrollToNext = () => {
    try { navigator.vibrate?.(12); } catch (_) { /* noop */ }
    const t = document.getElementById('section-career') || document.getElementById('section-meetingflow');
    if (t) {
      const top = t.getBoundingClientRect().top + window.scrollY - 60;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };
  const goToAudit = () => {
    try { navigator.vibrate?.(14); } catch (_) { /* noop */ }
    navigate('/diagnostic');
  };

  return (
    <div className="px-4 pt-3 pb-2" data-testid="hero-cover">
      <style>{`
        @keyframes hero-cover-shine {
          0%   { transform: translateX(-120%) skewX(-18deg); opacity: 0; }
          40%  { opacity: 0.55; }
          100% { transform: translateX(220%)  skewX(-18deg); opacity: 0; }
        }
        @keyframes hero-cover-arrow {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(3px); }
        }
        @keyframes hero-cover-pulse-dot {
          0%, 100% { opacity: 1;   transform: scale(1); }
          50%      { opacity: 0.4; transform: scale(0.85); }
        }
        @keyframes hero-cover-avatar-pop {
          0%   { transform: scale(0.92) translateY(4px); opacity: 0; }
          100% { transform: scale(1) translateY(0);    opacity: 1; }
        }
      `}</style>

      <article
        className="relative overflow-hidden"
        style={{
          background: theme.bgPaper,
          borderRadius: 22,
          border: `0.5px solid ${DA.goldSoft}`,
          boxShadow: `0 0 0 0.5px ${DA.goldSoft}, 0 24px 48px -18px rgba(11,43,36,0.28)`,
        }}
        data-testid="hero-cover-card"
      >
        {/* Sabril brand mark — with sun above the "i" */}
        <div className="relative pt-8 pb-5 flex justify-center" style={{ borderBottom: `0.5px solid ${DA.goldSoft}` }}>
          <BrandMark size="md" center />
        </div>

        {/* Top ribbon — Édition + numéro */}
        <div
          className="flex items-center justify-between px-4 pt-3 pb-2.5"
          style={{ borderBottom: '0.5px solid rgba(60,60,67,0.10)' }}
        >
          <div className="flex items-center gap-1.5">
            <span
              aria-hidden
              className="block"
              style={{
                width: 6, height: 6, borderRadius: 999,
                background: '#A87C4F',
                animation: 'hero-cover-pulse-dot 1.6s ease-in-out infinite',
              }}
            />
            <span style={{
              fontFamily: theme.fontMono,
              fontSize: 9,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              color: '#A87C4F',
              fontWeight: 700,
            }}>
              Édition 2026 · Guide privé
            </span>
          </div>
          <span style={{
            fontFamily: theme.fontMono,
            fontSize: 8.5,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: theme.inkMuted,
            fontWeight: 600,
          }}>
            N° 01
          </span>
        </div>

        {/* Hero photo — journal Maison Avocado */}
        <div
          className="relative"
          style={{
            width: '100%',
            aspectRatio: '4 / 5',
            overflow: 'hidden',
            background: 'linear-gradient(180deg, #F7EDD8 0%, #EFE0C0 100%)',
          }}
        >
          <img
            src={MAISON_AVOCADO_JOURNAL}
            alt="New Maison Avocado débarque à Lille"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: '50% 35%',
              display: 'block',
            }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          {/* Diagonal shine sweep */}
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background: 'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.28) 50%, transparent 70%)',
              animation: 'hero-cover-shine 5.5s ease-in-out 0.8s infinite',
              mixBlendMode: 'overlay',
            }}
          />

          {/* Soft bottom gradient for legibility */}
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0"
            style={{
              height: '55%',
              background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0.85) 100%)',
            }}
          />

          {/* Bottom block — bio integrated, magazine style */}
          <div className="absolute inset-x-0 bottom-0 px-4 pb-4">
            <div className="flex items-end gap-3">
              {/* Sabril avatar bubble — small floating */}
              <span
                className="flex-shrink-0 inline-flex items-center justify-center"
                style={{
                  width: 56, height: 56, borderRadius: 999,
                  padding: 2.5,
                  background: 'conic-gradient(from 45deg, #FE8C00, #F83600, #C71585, #833AB4, #515BD4, #F77737, #FE8C00)',
                  boxShadow: '0 8px 18px -6px rgba(0,0,0,0.40)',
                  animation: 'hero-cover-avatar-pop 0.6s ease-out',
                }}
              >
                <span
                  className="inline-flex items-center justify-center overflow-hidden"
                  style={{ width: '100%', height: '100%', borderRadius: 999, background: theme.bgPaper, padding: 2 }}
                >
                  <img
                    src={SABRIL_AVATAR}
                    alt="Sabril"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 999, objectPosition: '50% 22%' }}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </span>
              </span>

              {/* Bio text */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p style={{
                    fontFamily: theme.fontMono,
                    fontSize: 9,
                    letterSpacing: '0.28em',
                    textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.78)',
                    fontWeight: 700,
                  }}>
                    Le directeur de marque
                  </p>
                </div>
                <div className="flex items-center gap-1.5" style={{ marginTop: 4 }}>
                  <p style={{
                    fontFamily: theme.fontDisplay,
                    fontSize: 22,
                    fontWeight: 900,
                    letterSpacing: '-0.035em',
                    lineHeight: 0.96,
                    color: '#FFFFFF',
                    textShadow: '0 2px 8px rgba(0,0,0,0.30)',
                  }}>
                    SABRIL J.
                  </p>
                  {/* Instagram verified badge */}
                  <span
                    className="inline-flex items-center justify-center flex-shrink-0"
                    style={{
                      width: 17, height: 17, borderRadius: 999,
                      background: '#0095F6',
                      boxShadow: '0 2px 6px rgba(0,149,246,0.45), 0 0 0 1.5px rgba(255,255,255,0.95)',
                      marginTop: 2,
                    }}
                    aria-label="Verified"
                    data-testid="hero-cover-verified-badge"
                  >
                    <Check className="w-2.5 h-2.5" strokeWidth={4} style={{ color: '#FFFFFF' }} />
                  </span>
                </div>
                <p style={{
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  fontStyle: 'italic',
                  fontSize: 12.5,
                  color: '#FFFFFF',
                  opacity: 0.94,
                  marginTop: 5,
                  lineHeight: 1.32,
                  letterSpacing: '-0.005em',
                }}>
                  « Je construis des marques rentables, structurées et pérennes. »
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom narrative — what this page delivers */}
        <div className="px-4 pt-3 pb-4">
          <p style={{
            fontFamily: theme.fontMono,
            fontSize: 9,
            letterSpacing: '0.26em',
            textTransform: 'uppercase',
            color: '#A87C4F',
            fontWeight: 700,
          }}>
            Ce que vous allez trouver sur cette page
          </p>
          <p style={{
            fontFamily: theme.fontDisplay,
            fontSize: 16.5,
            fontWeight: 800,
            color: theme.inkDeep,
            letterSpacing: '-0.025em',
            lineHeight: 1.18,
            marginTop: 5,
          }}>
            Le 360° pour éviter tous les pièges<br />
            de la restauration.
          </p>

          {/* 3-point promise list */}
          <ul className="mt-3 flex flex-col gap-1.5">
            {[
              { k: 'a', v: '12 chantiers chronologiques · Jour 0 → 90.' },
              { k: 'b', v: 'Méthode · objectifs chiffrés · livrables clairs.' },
              { k: 'c', v: '2 offres claires · de 790€ à la direction privée.' },
            ].map((p) => (
              <li key={p.k} className="flex items-start gap-2">
                <span
                  className="inline-flex items-center justify-center shrink-0 mt-0.5"
                  style={{
                    width: 15, height: 15, borderRadius: 999,
                    background: '#0095F6',
                    color: '#FFFFFF',
                    boxShadow: '0 1px 3px rgba(0,149,246,0.35)',
                  }}
                  aria-hidden
                >
                  <Check className="w-2 h-2" strokeWidth={4} />
                </span>
                <span style={{
                  fontFamily: theme.fontSans,
                  fontSize: 12.5,
                  color: theme.inkBody,
                  lineHeight: 1.45,
                  letterSpacing: '-0.005em',
                }}>
                  {p.v}
                </span>
              </li>
            ))}
          </ul>

          {/* CTA — audit gratuit (primaire · gold) */}
          <button
            type="button"
            onClick={goToAudit}
            className="w-full mt-4 inline-flex items-center justify-center gap-2 active:scale-[0.97] transition-transform"
            style={{
              background: 'linear-gradient(180deg, #E5A661 0%, #C8862F 100%)',
              color: '#0B2B24',
              padding: '13px 16px',
              borderRadius: 999,
              border: '0.5px solid #B8792F',
              fontFamily: theme.fontDisplay,
              fontSize: 13.5,
              fontWeight: 800,
              letterSpacing: '-0.005em',
              boxShadow: '0 14px 28px -10px rgba(216,148,64,0.55), inset 0 1px 0 rgba(255,255,255,0.35)',
            }}
            data-testid="hero-cover-audit-gratuit"
          >
            Réaliser mon audit gratuit
            <ChevronRight className="w-3.5 h-3.5" strokeWidth={3} />
          </button>

          {/* CTA — start reading */}
          <button
            type="button"
            onClick={scrollToNext}
            className="w-full mt-2 inline-flex items-center justify-center gap-2 active:scale-[0.97] transition-transform"
            style={{
              background: 'linear-gradient(180deg, #1a1a1a 0%, #000000 100%)',
              color: '#FFFFFF',
              padding: '11px 16px',
              borderRadius: 999,
              fontFamily: theme.fontDisplay,
              fontSize: 13,
              fontWeight: 800,
              letterSpacing: '-0.005em',
              boxShadow: '0 10px 22px -10px rgba(0,0,0,0.40)',
            }}
            data-testid="hero-cover-start"
          >
            Commencer la lecture
            <ChevronDown className="w-3.5 h-3.5" strokeWidth={3} style={{ animation: 'hero-cover-arrow 1.6s ease-in-out infinite' }} />
          </button>

          {/* Tiny meta footer */}
          <div className="flex items-center justify-between mt-2.5">
            <span style={{
              fontFamily: theme.fontMono,
              fontSize: 8.5,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: theme.inkMuted,
              fontWeight: 600,
            }}>
              Lecture · ~6 min
            </span>
            <span style={{
              fontFamily: theme.fontMono,
              fontSize: 8.5,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: theme.inkMuted,
              fontWeight: 600,
            }}>
              @sabril.j
            </span>
          </div>
        </div>
      </article>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Footer mini
// ─────────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <div className="px-5 pt-4 pb-6 text-center" data-testid="footer">
      <p style={{ fontFamily: theme.fontMono, fontSize: 9.5, letterSpacing: '0.20em', textTransform: 'uppercase', color: theme.inkMuted, fontWeight: 600 }}>
        Salon Privé © 2026 · @sabril.j
      </p>
      <p style={{ fontFamily: theme.fontSans, fontSize: 11, fontStyle: 'italic', color: theme.inkMuted, marginTop: 4 }}>
        « Une vision claire, des actions justes, des résultats durables. »
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Section divider — MASSIVE breathing room between every section
// ─────────────────────────────────────────────────────────────────────
function SectionDivider() {
  return (
    <div className="flex items-center justify-center py-4" aria-hidden data-testid="section-divider">
      <span style={{ width: 28, height: 2, borderRadius: 999, background: theme.hairlineStrong, opacity: 0.4 }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ChapterHeader — numbered chapter intro with icon + tagline
// Used to give the page a clear narrative arc: 00 → 06
// ─────────────────────────────────────────────────────────────────────
function ChapterHeader({ num, title, tagline, accent = '#1a1a1a' }) {
  return (
    <div
      className="px-5 pt-8 pb-3"
      data-testid={`chapter-header-${num}`}
      id={`ch-${num}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span
          className="inline-flex items-center justify-center"
          style={{
            width: 28,
            height: 28,
            borderRadius: 999,
            background: accent,
            color: '#FFFFFF',
            fontFamily: theme.fontMono,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.05em',
          }}
        >
          {num}
        </span>
        <span
          style={{
            fontFamily: theme.fontMono,
            fontSize: 10.5,
            letterSpacing: '0.20em',
            textTransform: 'uppercase',
            color: theme.inkMuted,
            fontWeight: 700,
          }}
        >
          Chapitre · {num}/06
        </span>
      </div>
      <h2
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 26,
          fontWeight: 800,
          color: theme.inkDeep,
          letterSpacing: '-0.028em',
          lineHeight: 1.1,
          marginBottom: 6,
        }}
      >
        {title}
      </h2>
      {tagline && (
        <p
          style={{
            fontFamily: theme.fontSans,
            fontSize: 14,
            color: theme.inkMuted,
            lineHeight: 1.45,
            letterSpacing: '-0.005em',
          }}
        >
          {tagline}
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ContinueButton — clickable arrow at the end of each chapter,
// scrolls to the next chapter. Replaces the "natural scroll" with
// an intentional guided journey.
// ─────────────────────────────────────────────────────────────────────
function ContinueButton({ to, label }) {
  const onClick = () => {
    try { navigator.vibrate?.(15); } catch (_) { /* noop */ }
    const target = document.getElementById(`ch-${to}`);
    if (target) {
      const top = target.getBoundingClientRect().top + window.scrollY - 12;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };
  return (
    <div className="px-5 pt-2 pb-4 flex justify-center" data-testid={`chapter-continue-${to}`}>
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-2 active:scale-[0.96] transition-transform"
        style={{
          fontFamily: theme.fontSans,
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: '-0.005em',
          color: theme.inkDeep,
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          border: '0.5px solid rgba(60,60,67,0.18)',
          borderRadius: 999,
          padding: '9px 16px 9px 14px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.6)',
        }}
      >
        <span>{label}</span>
        <span aria-hidden style={{ fontSize: 14, lineHeight: 1 }}>↓</span>
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ChapterProgress — sticky pill at the top showing current chapter.
// Updates on scroll via IntersectionObserver. Style iOS dynamic island.
// ─────────────────────────────────────────────────────────────────────
const CHAPTER_LABELS = {
  '00': 'Qui je suis',
  '01': 'Ce que vous vivez',
  '02': 'Ce que je fais',
  '05': 'Les offres',
  '06': 'On se parle',
};
function ChapterProgress() {
  const [active, setActive] = useState('00');
  useEffect(() => {
    const ids = Object.keys(CHAPTER_LABELS);
    const els = ids.map((id) => document.getElementById(`ch-${id}`)).filter(Boolean);
    if (els.length === 0) return undefined;
    const obs = new IntersectionObserver((entries) => {
      // Pick the visible entry whose top is closest to viewport top
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => Math.abs(a.boundingClientRect.top) - Math.abs(b.boundingClientRect.top));
      if (visible[0]) {
        const id = visible[0].target.id.replace('ch-', '');
        setActive(id);
      }
    }, { rootMargin: '-15% 0px -70% 0px', threshold: [0, 0.5, 1] });
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
  const label = CHAPTER_LABELS[active] || '';
  return (
    <div
      className="sticky z-40 flex justify-center pt-1 pb-1 pointer-events-none"
      style={{ top: 0 }}
      data-testid="chapter-progress"
    >
      <div
        className="flex items-center gap-2 pointer-events-auto"
        style={{
          background: 'rgba(28,28,30,0.92)',
          backdropFilter: 'blur(24px) saturate(180%)',
          WebkitBackdropFilter: 'blur(24px) saturate(180%)',
          color: '#FFFFFF',
          borderRadius: 999,
          padding: '6px 12px',
          fontFamily: theme.fontMono,
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.06em',
          boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
        }}
      >
        <span style={{ color: '#A87C4F' }}>📍 {active}/06</span>
        <span style={{ opacity: 0.85, fontFamily: theme.fontSans, fontWeight: 600, letterSpacing: '-0.005em', fontSize: 11 }}>{label}</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Home Indicator (iOS gesture bar)
// ─────────────────────────────────────────────────────────────────────
function HomeIndicator() {
  return (
    <div className="flex justify-center pt-1 pb-2 sticky bottom-0 z-30" style={{ background: 'transparent', pointerEvents: 'none' }}>
      <div style={{ width: 134, height: 5, borderRadius: 999, background: 'rgba(0,0,0,0.85)' }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// StickyBottomBar — iOS tab-bar style, appears after hero scroll
// ─────────────────────────────────────────────────────────────────────
function StickyBottomBar() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      // Show after 280px scroll (past hero)
      setVisible(window.scrollY > 280);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // When the sticky bar is visible on mobile, push the floating Sabril bubble higher
  // so they never overlap. We toggle a body attribute and provide CSS overrides.
  useEffect(() => {
    document.body.setAttribute('data-sticky-cta', visible ? 'on' : 'off');
    return () => { document.body.removeAttribute('data-sticky-cta'); };
  }, [visible]);

  return (
    <div
      className="fixed left-1/2 z-[55] transition-all duration-400 ease-out"
      style={{
        bottom: visible ? 18 : -120,
        transform: 'translateX(-50%)',
        width: 'calc(100% - 24px)',
        maxWidth: 360,
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
      }}
      data-testid="sticky-bottom-bar"
    >
      <div
        className="flex items-center gap-2 px-2 py-2"
        style={{
          background: 'rgba(255, 255, 255, 0.78)',
          backdropFilter: 'blur(28px) saturate(180%)',
          WebkitBackdropFilter: 'blur(28px) saturate(180%)',
          borderRadius: 999,
          border: '0.5px solid rgba(60, 60, 67, 0.18)',
          boxShadow: '0 14px 38px -10px rgba(0, 0, 0, 0.25), 0 2px 6px rgba(0, 0, 0, 0.05)',
        }}
      >
        <button
          type="button"
          onClick={() => openSabril('question')}
          className="flex-1 inline-flex items-center justify-center gap-2 active:scale-[0.97] transition-transform"
          style={{
            background: 'linear-gradient(180deg, #1C1C1E 0%, #000000 100%)',
            color: '#FFFFFF',
            padding: '12px 16px',
            borderRadius: 999,
            fontFamily: theme.fontDisplay,
            fontSize: 13.5,
            fontWeight: 700,
            letterSpacing: '-0.005em',
            boxShadow: '0 1px 0 0 rgba(255,255,255,0.18) inset, 0 6px 14px -4px rgba(0,0,0,0.40)',
          }}
          data-testid="sticky-cta-discuter"
        >
          <MessageCircle className="w-4 h-4" strokeWidth={2.4} />
          Discuter avec Sabril
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// PAGE — iOS 27 Wallet App container
// ─────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────
// DesktopAmbient — habillage éditorial autour du frame iPhone sur ≥lg
// Watermark géant · header + footer flottants · info panels latéraux
// ─────────────────────────────────────────────────────────────────────
function DesktopAmbient() {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
  return (
    <div className="hidden lg:block fixed inset-0 pointer-events-none z-0" data-testid="desktop-ambient" aria-hidden>
      <style>{`
        @keyframes desktop-orbit { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        @keyframes desktop-pulse-dot {
          0%, 100% { opacity: 1;   transform: scale(1); }
          50%      { opacity: 0.5; transform: scale(0.85); }
        }
      `}</style>

      {/* Blurry blob decorations */}
      <span
        className="absolute rounded-full"
        style={{
          top: '10%', left: '-8%', width: 480, height: 480,
          background: 'radial-gradient(circle, rgba(168,124,79,0.25) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />
      <span
        className="absolute rounded-full"
        style={{
          bottom: '5%', right: '-6%', width: 520, height: 520,
          background: 'radial-gradient(circle, rgba(0,122,255,0.16) 0%, transparent 70%)',
          filter: 'blur(70px)',
        }}
      />

      {/* Huge italic "S" watermark — center behind phone */}
      <span
        className="absolute select-none"
        style={{
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontStyle: 'italic',
          fontWeight: 900,
          fontSize: 780,
          lineHeight: 1,
          color: 'rgba(60,60,67,0.045)',
          letterSpacing: '-0.06em',
          whiteSpace: 'nowrap',
        }}
      >
        S.
      </span>

      {/* Editorial vertical caps — LEFT rail */}
      <div
        className="absolute pointer-events-auto"
        style={{
          top: '50%', left: 40,
          transform: 'translateY(-50%) rotate(-90deg)',
          transformOrigin: 'left center',
          fontFamily: theme.fontMono,
          fontSize: 10.5,
          letterSpacing: '0.42em',
          textTransform: 'uppercase',
          color: '#8B6520',
          fontWeight: 700,
        }}
      >
        Édition 2026 · Salon Privé · N° 01
      </div>

      {/* Editorial vertical caps — RIGHT rail */}
      <div
        className="absolute"
        style={{
          top: '50%', right: 40,
          transform: 'translateY(-50%) rotate(90deg)',
          transformOrigin: 'right center',
          fontFamily: theme.fontMono,
          fontSize: 10.5,
          letterSpacing: '0.42em',
          textTransform: 'uppercase',
          color: 'rgba(60,60,67,0.55)',
          fontWeight: 700,
        }}
      >
        Marrakech · Marbella · Cannes · Paris · Miami
      </div>

      {/* Floating LEFT info panel — hero title */}
      <div
        className="absolute pointer-events-auto"
        style={{
          top: '50%',
          right: 'calc(50% + 240px)',
          transform: 'translateY(-50%)',
          width: 320,
          animation: 'desktop-orbit 8s ease-in-out infinite',
        }}
      >
        <div className="flex items-center gap-1.5 mb-3">
          <span
            className="block"
            style={{
              width: 8, height: 8, borderRadius: 999,
              background: '#A87C4F',
              animation: 'desktop-pulse-dot 1.6s ease-in-out infinite',
            }}
          />
          <span style={{
            fontFamily: theme.fontMono,
            fontSize: 10,
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            color: '#8B6520',
            fontWeight: 700,
          }}>
            Le directeur de marque
          </span>
        </div>
        <p style={{
          fontFamily: theme.fontDisplay,
          fontSize: 52,
          fontWeight: 900,
          letterSpacing: '-0.04em',
          lineHeight: 0.94,
          color: '#1a1a1a',
        }}>
          SABRIL<br />JURCZESZYN.
        </p>
        <p style={{
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontStyle: 'italic',
          fontSize: 17,
          lineHeight: 1.35,
          color: '#1a1a1a',
          marginTop: 18,
          letterSpacing: '-0.008em',
        }}>
          « Je construis des marques rentables, structurées et pérennes. »
        </p>
        <p style={{
          fontFamily: theme.fontSans,
          fontSize: 13,
          color: 'rgba(60,60,67,0.75)',
          marginTop: 12,
          lineHeight: 1.5,
        }}>
          Le 360° pour éviter tous les pièges de la restauration.
        </p>

        {/* Hairline gold divider */}
        <div className="mt-6 mb-5" style={{ width: 60, height: 1, background: 'linear-gradient(to right, #A87C4F, transparent)' }} />

        <div className="flex flex-col gap-2.5">
          {[
            { k: '18', v: 'chantiers · Jour 0 → 90' },
            { k: '2',  v: 'offres · de 790€ au Salon Privé' },
            { k: '9',  v: 'clients · 4 places restantes' },
          ].map((s) => (
            <div key={s.k} className="flex items-baseline gap-3">
              <span style={{
                fontFamily: theme.fontDisplay,
                fontSize: 28,
                fontWeight: 900,
                letterSpacing: '-0.035em',
                color: '#A87C4F',
                minWidth: 44,
              }}>{s.k}</span>
              <span style={{
                fontFamily: theme.fontSans,
                fontSize: 13,
                color: '#1a1a1a',
                fontWeight: 500,
              }}>{s.v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Floating RIGHT info panel — live status */}
      <div
        className="absolute pointer-events-auto"
        style={{
          top: '50%',
          left: 'calc(50% + 240px)',
          transform: 'translateY(-50%)',
          width: 300,
          animation: 'desktop-orbit 9.5s ease-in-out 0.5s infinite',
        }}
      >
        <div
          className="relative overflow-hidden"
          style={{
            background: 'rgba(255,255,255,0.75)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            borderRadius: 22,
            border: '0.5px solid rgba(255,255,255,0.90)',
            padding: 18,
            boxShadow: '0 20px 44px -20px rgba(0,0,0,0.18)',
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-1.5">
              <span
                className="block"
                style={{
                  width: 8, height: 8, borderRadius: 999,
                  background: '#34C759',
                  boxShadow: '0 0 0 3px rgba(52,199,89,0.20)',
                  animation: 'desktop-pulse-dot 1.4s ease-in-out infinite',
                }}
              />
              <span style={{
                fontFamily: theme.fontMono,
                fontSize: 9.5,
                letterSpacing: '0.24em',
                textTransform: 'uppercase',
                color: '#1a1a1a',
                fontWeight: 700,
              }}>
                Sabril · en ligne
              </span>
            </div>
            <span style={{
              fontFamily: theme.fontMono,
              fontSize: 9,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'rgba(60,60,67,0.65)',
              fontWeight: 700,
            }}>
              Live
            </span>
          </div>

          <p style={{
            fontFamily: theme.fontDisplay,
            fontSize: 15,
            fontWeight: 800,
            color: '#1a1a1a',
            letterSpacing: '-0.022em',
            lineHeight: 1.25,
          }}>
            L'expérience est optimisée pour mobile.
          </p>
          <p style={{
            fontFamily: theme.fontSans,
            fontSize: 12,
            color: 'rgba(60,60,67,0.75)',
            marginTop: 6,
            lineHeight: 1.5,
          }}>
            Scanne le QR code avec votre iPhone pour vivre la page comme un client — ou continue ici, ça marche aussi.
          </p>

          {/* Fake QR */}
          <div className="mt-4 flex items-center justify-center">
            <div
              style={{
                width: 96, height: 96,
                background: theme.bgPaper,
                borderRadius: 12,
                padding: 8,
                boxShadow: 'inset 0 0 0 0.5px rgba(60,60,67,0.15)',
              }}
            >
              <div
                style={{
                  width: '100%', height: '100%',
                  backgroundImage: `
                    linear-gradient(45deg, #1a1a1a 25%, transparent 25%),
                    linear-gradient(-45deg, #1a1a1a 25%, transparent 25%),
                    linear-gradient(45deg, transparent 75%, #1a1a1a 75%),
                    linear-gradient(-45deg, transparent 75%, #1a1a1a 75%)
                  `,
                  backgroundSize: '10px 10px',
                  backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0px',
                  borderRadius: 8,
                }}
                aria-hidden
              />
            </div>
          </div>

          <div className="mt-4 pt-3 border-t" style={{ borderColor: 'rgba(60,60,67,0.10)' }}>
            <p style={{
              fontFamily: theme.fontMono,
              fontSize: 9,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'rgba(60,60,67,0.55)',
              fontWeight: 700,
            }}>
              Territoire
            </p>
            <p style={{
              fontFamily: theme.fontSans,
              fontSize: 12,
              color: '#1a1a1a',
              marginTop: 3,
              fontWeight: 500,
            }}>
              Marrakech · Marbella · Cannes · Paris · Miami
            </p>
          </div>

          <button
            type="button"
            onClick={scrollToTop}
            className="w-full mt-3.5 inline-flex items-center justify-center gap-1.5 active:scale-[0.97] transition-transform pointer-events-auto"
            style={{
              background: 'linear-gradient(180deg, #1a1a1a 0%, #000000 100%)',
              color: '#FFFFFF',
              padding: '9px 14px',
              borderRadius: 999,
              fontFamily: theme.fontDisplay,
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: '-0.005em',
              boxShadow: '0 8px 18px -6px rgba(0,0,0,0.35)',
            }}
            data-testid="desktop-back-to-top"
          >
            Retour en haut ↑
          </button>
        </div>
      </div>

      {/* Bottom footer credits */}
      <div
        className="absolute inset-x-0 bottom-6 flex items-center justify-center gap-2"
        style={{
          fontFamily: theme.fontMono,
          fontSize: 9.5,
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          color: 'rgba(60,60,67,0.45)',
          fontWeight: 700,
        }}
      >
        <span>@sabril.j</span>
        <span style={{ opacity: 0.4 }}>·</span>
        <span>Salon Privé © 2026</span>
        <span style={{ opacity: 0.4 }}>·</span>
        <span>Made with ♥ à Lille</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ThreePillars — 3 promesses core (money · rentabilité · marque revendable)
// Version desktop
// ─────────────────────────────────────────────────────────────────────
function DesktopThreePillars() {
  const pillars = [
    {
      n: '01',
      icon: Calculator,
      tag: 'Cash Flow',
      title: 'Faire gagner de l\'argent.',
      body: 'Chaque décision est mesurée en euros. On récupère les fuites, on booste le panier moyen, on construit un flux de trésorerie stable.',
      kpis: ['+18% panier moyen', '−24% pertes', 'Cash 30j'],
    },
    {
      n: '02',
      icon: BarChart3,
      tag: 'Rentabilité',
      title: 'Rentabiliser durablement.',
      body: 'Marge brute maîtrisée, chiffres suivis chaque semaine, équipe formée. Votre rentabilité n\'est plus un accident — c\'est un système.',
      kpis: ['Food cost <28%', 'Marge nette 15%+', 'Équipe autonome'],
    },
    {
      n: '03',
      icon: Gem,
      tag: 'Valorisation',
      title: 'Créer une marque revendable.',
      body: 'Une marque construite pour valoir plusieurs fois son CA. Si vous voulez revendre ou franchiser — vous avez tous les actifs pour le faire.',
      kpis: ['Valo ×3 à ×7 CA', 'Actifs cessibles', 'Franchise-ready'],
    },
  ];

  const INK = DA.greenDeep;
  const INK_MUTED = DA.inkMuted;
  const HAIR = DA.goldSoft;

  return (
    <section
      id="desk-pillars"
      style={{
        background: DA.ivory,
        padding: '110px 0',
        borderTop: `0.5px solid ${DA.goldSoft}`,
      }}
    >
      <div className="max-w-4xl mx-auto px-8">
        {/* ─── UNIFIED EDITORIAL CARD ─── */}
        <article
          className="relative overflow-hidden mx-auto"
          style={{
            background: DA.paper,
            borderRadius: 28,
            padding: '72px 64px 80px',
            border: `0.5px solid ${HAIR}`,
            boxShadow: '0 30px 80px -30px rgba(11,43,36,0.18), 0 1px 3px rgba(11,43,36,0.05)',
          }}
          data-testid="desk-unified-pillar"
        >
          {/* Decorative frame — awning corners + mimosa bottom-left */}
          <AwningCorner position="top-left" size={200} />
          <AwningCorner position="bottom-right" size={200} />
          <MimosaBranch size={220} position="bottom-left" opacity={0.9} />

          {/* Header */}
          <div className="relative text-center" style={{ zIndex: 2 }}>
            <div className="flex justify-center mb-4">
              <SunOrnament size={34} color={DA.sunBright} />
            </div>
            <p style={{
              fontFamily: DA.mono,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '0.34em',
              textTransform: 'uppercase',
              color: DA.goldMain,
              marginBottom: 20,
            }}>
              Les 3 piliers du Salon Privé
            </p>
            <h2 style={{
              fontFamily: DA.serif,
              fontSize: 48,
              fontWeight: 900,
              letterSpacing: '-0.028em',
              color: INK,
              lineHeight: 1.02,
              marginBottom: 16,
            }}>
              Une mission. Trois promesses.
            </h2>
            <p style={{
              fontFamily: DA.sans,
              fontSize: 16,
              color: INK_MUTED,
              lineHeight: 1.6,
              maxWidth: 620,
              margin: '0 auto',
            }}>
              De l'argent qui rentre · une marge qui tient · une marque qui vaut.
            </p>
            <div className="mt-8">
              <SabrilDivider size={22} spacing={0} />
            </div>
          </div>

          {/* 3 stacked sections */}
          <div className="relative mt-10" style={{ zIndex: 2 }}>
            {pillars.map((p, i) => {
              const Icon = p.icon;
              const isLast = i === pillars.length - 1;
              return (
                <div
                  key={p.n}
                  className="relative"
                  style={{ paddingTop: i === 0 ? 0 : 36, paddingBottom: isLast ? 0 : 36 }}
                  data-testid={`desk-pillar-${p.n}`}
                >
                  <div className="flex items-start gap-8">
                    {/* Numeral column */}
                    <div className="flex-shrink-0" style={{ width: 88 }}>
                      <p style={{
                        fontFamily: DA.serif,
                        fontStyle: 'italic',
                        fontSize: 72,
                        fontWeight: 900,
                        color: DA.goldMain,
                        lineHeight: 0.9,
                        letterSpacing: '-0.04em',
                      }}>
                        N°{p.n.replace(/^0/, '')}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <span style={{ width: 24, height: 0.5, background: DA.goldMain }} />
                        <p style={{
                          fontFamily: DA.mono,
                          fontSize: 9.5,
                          letterSpacing: '0.28em',
                          textTransform: 'uppercase',
                          color: DA.goldDeep,
                          fontWeight: 800,
                        }}>
                          {p.tag}
                        </p>
                      </div>
                    </div>

                    {/* Content column */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-6">
                        <h3 style={{
                          fontFamily: DA.serif,
                          fontSize: 28,
                          fontWeight: 900,
                          letterSpacing: '-0.026em',
                          color: INK,
                          lineHeight: 1.08,
                          flex: 1,
                        }}>
                          {p.title}
                        </h3>
                        {/* Icon badge */}
                        <span
                          className="inline-flex items-center justify-center flex-shrink-0"
                          style={{
                            width: 48, height: 48,
                            borderRadius: 12,
                            background: DA.paper,
                            border: `0.5px solid ${HAIR}`,
                            boxShadow: '0 2px 6px rgba(11,43,36,0.06)',
                          }}
                        >
                          <Icon className="w-5 h-5" strokeWidth={1.75} style={{ color: INK }} />
                        </span>
                      </div>
                      <p style={{
                        fontFamily: DA.sans,
                        fontSize: 14.5,
                        color: INK_MUTED,
                        lineHeight: 1.6,
                        marginTop: 10,
                      }}>
                        {p.body}
                      </p>
                      {/* KPI captions */}
                      <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4">
                        {p.kpis.map((k, ki) => (
                          <span key={k} className="inline-flex items-baseline gap-1.5">
                            <span style={{
                              fontFamily: DA.mono,
                              fontSize: 9,
                              letterSpacing: '0.18em',
                              color: DA.goldMain,
                              fontWeight: 800,
                            }}>
                              {String(ki + 1).padStart(2, '0')}
                            </span>
                            <span style={{
                              fontFamily: DA.sans,
                              fontSize: 12.5,
                              color: INK,
                              fontWeight: 600,
                            }}>
                              {k}
                            </span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  {/* Divider between sections */}
                  {!isLast && (
                    <div className="flex items-center gap-4 mt-9">
                      <span style={{ flex: 1, height: 0.5, background: HAIR }} />
                      <SunOrnament size={14} color={DA.sunBright} opacity={0.7} />
                      <span style={{ flex: 1, height: 0.5, background: HAIR }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer stamp — page reference */}
          <div className="relative mt-12 flex justify-end" style={{ zIndex: 2 }}>
            <p style={{
              fontFamily: DA.mono,
              fontSize: 9,
              letterSpacing: '0.28em',
              color: DA.goldDeep,
              fontWeight: 800,
              textTransform: 'uppercase',
            }}>
              03 / 03 · Fin
            </p>
          </div>
        </article>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// MobileThreePillars — 3 promesses core version mobile compact
// ─────────────────────────────────────────────────────────────────────
function MobileThreePillars() {
  const pillars = [
    {
      n: '01', icon: Calculator,
      tag: 'Cash Flow',
      title: 'Faire gagner de l\'argent.',
      body: 'Chaque décision est mesurée en euros. Fuites récupérées, panier boosté, trésorerie stable.',
      kpis: ['+18% panier moyen', '−24% pertes', 'Cash 30j'],
    },
    {
      n: '02', icon: BarChart3,
      tag: 'Rentabilité',
      title: 'Rentabiliser durablement.',
      body: 'Marge maîtrisée, chiffres suivis, équipe formée. Votre rentabilité devient un système.',
      kpis: ['Food cost <28%', 'Marge nette 15%+', 'Équipe autonome'],
    },
    {
      n: '03', icon: Gem,
      tag: 'Valorisation',
      title: 'Créer une marque revendable.',
      body: 'Une marque qui vaut plusieurs fois son CA. Revente ou franchise — tous les actifs prêts.',
      kpis: ['Valo ×3 à ×7 CA', 'Actifs cessibles', 'Franchise-ready'],
    },
  ];

  const INK = DA.greenDeep;
  const INK_MUTED = DA.inkMuted;
  const HAIR = DA.goldSoft;

  return (
    <div className="px-4 py-6" data-testid="mobile-three-pillars">
      {/* ─── UNIFIED EDITORIAL CARD ─── */}
      <article
        className="relative overflow-hidden"
        style={{
          background: DA.paper,
          borderRadius: 22,
          padding: '44px 20px 48px',
          border: `0.5px solid ${HAIR}`,
          boxShadow: '0 20px 40px -18px rgba(11,43,36,0.16), 0 1px 2px rgba(11,43,36,0.04)',
        }}
        data-testid="mobile-unified-pillar"
      >
        {/* Decorative frame */}
        <AwningCorner position="top-left" size={110} />
        <AwningCorner position="bottom-right" size={110} />
        <MimosaBranch size={130} position="bottom-left" opacity={0.92} />

        {/* Header */}
        <div className="relative text-center" style={{ zIndex: 2 }}>
          <div className="flex justify-center mb-3">
            <SunOrnament size={26} color={DA.sunBright} />
          </div>
          <p style={{
            fontFamily: DA.mono,
            fontSize: 9,
            fontWeight: 800,
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            color: DA.goldMain,
            marginBottom: 12,
          }}>
            Les 3 piliers
          </p>
          <h2 style={{
            fontFamily: DA.serif,
            fontSize: 28,
            fontWeight: 900,
            letterSpacing: '-0.026em',
            color: INK,
            lineHeight: 1.05,
            marginBottom: 10,
          }}>
            Une mission.<br />Trois promesses.
          </h2>
          <p style={{
            fontFamily: DA.sans,
            fontSize: 12.5,
            color: INK_MUTED,
            lineHeight: 1.55,
            padding: '0 8px',
          }}>
            De l'argent qui rentre · une marge qui tient · une marque qui vaut.
          </p>
          <div className="mt-5">
            <SabrilDivider size={16} spacing={0} />
          </div>
        </div>

        {/* 3 stacked sections */}
        <div className="relative mt-6" style={{ zIndex: 2 }}>
          {pillars.map((p, i) => {
            const Icon = p.icon;
            const isLast = i === pillars.length - 1;
            return (
              <div
                key={p.n}
                className="relative"
                style={{ paddingTop: i === 0 ? 0 : 22, paddingBottom: isLast ? 0 : 22 }}
                data-testid={`mobile-pillar-${p.n}`}
              >
                {/* Top row: numeral + tag + icon */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span style={{
                        fontFamily: DA.serif,
                        fontStyle: 'italic',
                        fontSize: 36,
                        fontWeight: 900,
                        color: DA.goldMain,
                        lineHeight: 0.9,
                        letterSpacing: '-0.03em',
                      }}>
                        N°{p.n.replace(/^0/, '')}
                      </span>
                      <span style={{ width: 18, height: 0.5, background: DA.goldMain, opacity: 0.8 }} />
                      <span style={{
                        fontFamily: DA.mono,
                        fontSize: 8.5,
                        letterSpacing: '0.26em',
                        textTransform: 'uppercase',
                        color: DA.goldDeep,
                        fontWeight: 800,
                      }}>
                        {p.tag}
                      </span>
                    </div>
                  </div>
                  {/* Icon */}
                  <span
                    className="inline-flex items-center justify-center flex-shrink-0"
                    style={{
                      width: 36, height: 36,
                      borderRadius: 10,
                      background: DA.paper,
                      border: `0.5px solid ${HAIR}`,
                      boxShadow: '0 1px 3px rgba(11,43,36,0.05)',
                    }}
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.75} style={{ color: INK }} />
                  </span>
                </div>

                {/* Title */}
                <h3 style={{
                  fontFamily: DA.serif,
                  fontSize: 20,
                  fontWeight: 900,
                  letterSpacing: '-0.024em',
                  color: INK,
                  lineHeight: 1.08,
                  marginTop: 10,
                }}>
                  {p.title}
                </h3>

                {/* Body */}
                <p style={{
                  fontFamily: DA.sans,
                  fontSize: 12,
                  color: INK_MUTED,
                  lineHeight: 1.5,
                  marginTop: 6,
                }}>
                  {p.body}
                </p>

                {/* KPI captions */}
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3">
                  {p.kpis.map((k, ki) => (
                    <span key={k} className="inline-flex items-baseline gap-1">
                      <span style={{
                        fontFamily: DA.mono,
                        fontSize: 7.5,
                        letterSpacing: '0.14em',
                        color: DA.goldMain,
                        fontWeight: 800,
                      }}>
                        {String(ki + 1).padStart(2, '0')}
                      </span>
                      <span style={{
                        fontFamily: DA.sans,
                        fontSize: 10.5,
                        color: INK,
                        fontWeight: 600,
                      }}>
                        {k}
                      </span>
                    </span>
                  ))}
                </div>

                {/* Divider between sections */}
                {!isLast && (
                  <div className="flex items-center gap-3 mt-5">
                    <span style={{ flex: 1, height: 0.5, background: HAIR }} />
                    <SunOrnament size={11} color={DA.sunBright} opacity={0.65} />
                    <span style={{ flex: 1, height: 0.5, background: HAIR }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer stamp */}
        <div className="relative mt-6 flex justify-end" style={{ zIndex: 2 }}>
          <p style={{
            fontFamily: DA.mono,
            fontSize: 8,
            letterSpacing: '0.26em',
            color: DA.goldDeep,
            fontWeight: 800,
            textTransform: 'uppercase',
          }}>
            03 / 03 · Fin
          </p>
        </div>
      </article>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DesktopExperience — layout full-web pour ≥lg (1024px+)
// Header sticky · Hero 2 cols · Chantiers grid · Offers row · CTA
// ─────────────────────────────────────────────────────────────────────
function DesktopExperience() {
  const scrollToSection = (id) => {
    const t = document.getElementById(id);
    if (t) {
      const top = t.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <div className="w-full min-h-screen" style={{ background: '#FAFAFA', color: '#1a1a1a' }} data-testid="desktop-experience">
      <style>{`
        .desk-shine { position: absolute; inset: 0; background: linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.28) 50%, transparent 70%); mix-blend-mode: overlay; animation: desk-shine-anim 6s ease-in-out infinite; pointer-events: none; }
        @keyframes desk-shine-anim { 0% { transform: translateX(-120%) skewX(-18deg); opacity: 0; } 40% { opacity: 0.55; } 100% { transform: translateX(220%) skewX(-18deg); opacity: 0; } }
        @keyframes desk-pulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(0.85); } }
        .desk-card:hover { transform: translateY(-6px); box-shadow: 0 30px 60px -22px rgba(0,0,0,0.22) !important; }
        .desk-card { transition: transform 0.28s ease, box-shadow 0.28s ease; }
        .desk-nav-link { position: relative; }
        .desk-nav-link::after { content: ''; position: absolute; bottom: -6px; left: 0; width: 0; height: 2px; background: #A87C4F; transition: width 0.24s ease; }
        .desk-nav-link:hover::after { width: 100%; }
      `}</style>

      {/* ═══ HEADER ═══ */}
      <DesktopHeader onNav={scrollToSection} />

      {/* ═══ HERO ═══ */}
      <DesktopHero onCta={scrollToSection} />

      {/* ═══ 3 PILIERS ═══ */}
      <DesktopThreePillars />

      {/* ═══ TRUST BAR ═══ */}
      <DesktopTrustBar />

      {/* ═══ PARCOURS ═══ */}
      <DesktopCareer />

      {/* ═══ 12 CHANTIERS ═══ */}
      <DesktopChantiers />

      {/* ═══ OFFERS ═══ */}
      <DesktopOffers />

      {/* ═══ CONTACT ═══ */}
      <DesktopContact />

      {/* ═══ FOOTER ═══ */}
      <DesktopFooter />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Desktop Header — sticky translucent glass
// ─────────────────────────────────────────────────────────────────────
function DesktopHeader({ onNav }) {
  return (
    <header
      className="sticky top-0 z-50"
      style={{
        background: 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        borderBottom: '0.5px solid rgba(60,60,67,0.10)',
      }}
      data-testid="desktop-header"
    >
      <div className="max-w-7xl mx-auto px-8 flex items-center justify-between" style={{ height: 68 }}>
        {/* Logo + name */}
        <div className="flex items-center gap-3">
          <span
            className="inline-flex items-center justify-center"
            style={{
              width: 36, height: 36, borderRadius: 12,
              background: 'linear-gradient(180deg, #1a1a1a 0%, #000000 100%)',
              boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.10), 0 4px 10px -3px rgba(0,0,0,0.30)',
            }}
          >
            <span style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontStyle: 'italic',
              fontWeight: 900,
              fontSize: 18,
              color: '#FFFFFF',
              letterSpacing: '-0.05em',
              marginTop: -1,
            }}>S</span>
          </span>
          <div>
            <p style={{ fontFamily: theme.fontDisplay, fontSize: 15, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1 }}>
              Sabril Jurczeszyn
            </p>
            <p style={{ fontFamily: theme.fontMono, fontSize: 9, letterSpacing: '0.24em', textTransform: 'uppercase', color: '#8B6520', fontWeight: 700, marginTop: 3 }}>
              Directeur de marque
            </p>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex items-center gap-8">
          {[
            { id: 'desk-career', label: 'Parcours' },
            { id: 'desk-chantiers', label: '12 chantiers' },
            { id: 'desk-offers', label: 'Offres' },
            { id: 'desk-contact', label: 'Contact' },
          ].map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => onNav(n.id)}
              className="desk-nav-link"
              style={{
                fontFamily: theme.fontSans,
                fontSize: 13,
                fontWeight: 600,
                color: '#1a1a1a',
                letterSpacing: '-0.005em',
              }}
              data-testid={`desk-nav-${n.id}`}
            >
              {n.label}
            </button>
          ))}
        </nav>

        {/* CTAs */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => openSabril()}
            className="inline-flex items-center gap-1.5 active:scale-[0.97] transition-transform"
            style={{
              padding: '8px 14px',
              borderRadius: 999,
              background: theme.bgPaper,
              border: '0.5px solid rgba(60,60,67,0.20)',
              fontFamily: theme.fontSans, fontSize: 12.5, fontWeight: 700,
              color: '#1a1a1a',
            }}
            data-testid="desk-header-chat"
          >
            <MessageCircle className="w-3.5 h-3.5" strokeWidth={2.4} />
            Discuter
          </button>
          <button
            type="button"
            onClick={() => onNav('desk-contact')}
            className="inline-flex items-center gap-1.5 active:scale-[0.97] transition-transform"
            style={{
              padding: '8px 14px',
              borderRadius: 999,
              background: 'linear-gradient(180deg, #1a1a1a 0%, #000000 100%)',
              fontFamily: theme.fontSans, fontSize: 12.5, fontWeight: 700,
              color: '#FFFFFF',
              boxShadow: '0 8px 18px -6px rgba(0,0,0,0.35)',
            }}
            data-testid="desk-header-cta"
          >
            <Coffee className="w-3.5 h-3.5" strokeWidth={2.4} />
            Prendre un café
          </button>
        </div>
      </div>
    </header>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Desktop Hero — 2 cols · edito + journal photo
// ─────────────────────────────────────────────────────────────────────
function DesktopHero({ onCta }) {
  return (
    <section
      className="relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #FAFAFA 0%, #F0EEE9 100%)',
        minHeight: '92vh',
      }}
    >
      {/* Blur decor */}
      <span className="absolute rounded-full pointer-events-none" style={{ top: '10%', left: '-6%', width: 520, height: 520, background: 'radial-gradient(circle, rgba(168,124,79,0.20) 0%, transparent 70%)', filter: 'blur(70px)' }} aria-hidden />
      <span className="absolute rounded-full pointer-events-none" style={{ bottom: '-8%', right: '-6%', width: 600, height: 600, background: 'radial-gradient(circle, rgba(0,122,255,0.10) 0%, transparent 70%)', filter: 'blur(80px)' }} aria-hidden />

      <div className="relative max-w-7xl mx-auto px-8 pt-16 pb-24 grid grid-cols-12 gap-12 items-center">
        {/* LEFT col — 7/12 */}
        <div className="col-span-7">
          <div className="flex items-center gap-1.5 mb-5">
            <span className="block" style={{ width: 8, height: 8, borderRadius: 999, background: '#A87C4F', animation: 'desk-pulse 1.6s ease-in-out infinite' }} aria-hidden />
            <span style={{ fontFamily: theme.fontMono, fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase', color: '#8B6520', fontWeight: 700 }}>
              Édition 2026 · Guide privé · N° 01
            </span>
          </div>
          <p style={{ fontFamily: theme.fontMono, fontSize: 11, letterSpacing: '0.30em', textTransform: 'uppercase', color: 'rgba(60,60,67,0.65)', fontWeight: 700, marginBottom: 10 }}>
            Le directeur de marque
          </p>
          <h1 style={{
            fontFamily: theme.fontDisplay,
            fontSize: 92,
            fontWeight: 900,
            letterSpacing: '-0.048em',
            lineHeight: 0.92,
            color: '#1a1a1a',
          }}>
            Sabril<br />Jurczeszyn.
          </h1>
          <p style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontStyle: 'italic',
            fontSize: 22,
            lineHeight: 1.35,
            color: '#1a1a1a',
            marginTop: 28,
            letterSpacing: '-0.008em',
            maxWidth: 560,
          }}>
            « Je construis des marques rentables, structurées et pérennes. »
          </p>
          <p style={{ fontFamily: theme.fontSans, fontSize: 15, color: 'rgba(60,60,67,0.80)', marginTop: 12, lineHeight: 1.55, maxWidth: 560 }}>
            Le 360° qui structure votre restaurant — 12 chantiers Jour 0 → 90, 2 offres, du coaching dirigeant à la direction de marque permanente.
          </p>

          <div className="mt-8 flex items-center gap-3">
            <button
              type="button"
              onClick={() => onCta('desk-chantiers')}
              className="inline-flex items-center gap-2 active:scale-[0.97] transition-transform"
              style={{
                padding: '14px 24px',
                borderRadius: 999,
                background: 'linear-gradient(180deg, #1a1a1a 0%, #000000 100%)',
                fontFamily: theme.fontDisplay, fontSize: 14, fontWeight: 800,
                color: '#FFFFFF',
                boxShadow: '0 14px 28px -10px rgba(0,0,0,0.40)',
                letterSpacing: '-0.005em',
              }}
              data-testid="desk-hero-cta-primary"
            >
              Voir les 12 chantiers
              <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
            </button>
            <button
              type="button"
              onClick={() => onCta('desk-contact')}
              className="inline-flex items-center gap-2 active:scale-[0.97] transition-transform"
              style={{
                padding: '14px 24px',
                borderRadius: 999,
                background: 'linear-gradient(180deg, #FE8C00 0%, #A87C4F 100%)',
                fontFamily: theme.fontDisplay, fontSize: 14, fontWeight: 800,
                color: '#FFFFFF',
                boxShadow: '0 14px 28px -10px rgba(254,140,0,0.50)',
                letterSpacing: '-0.005em',
              }}
              data-testid="desk-hero-cta-cafe"
            >
              <Coffee className="w-4 h-4" strokeWidth={2.6} />
              Prendre un café
            </button>
          </div>

          <div className="mt-10 flex items-center gap-8">
            {[
              { k: '18', v: 'chantiers · J0 → J90' },
              { k: '2',  v: 'offres · 790€ → Salon Privé' },
              { k: '9',  v: 'clients · 4 places restantes' },
            ].map((s) => (
              <div key={s.k} className="flex items-baseline gap-2.5">
                <span style={{ fontFamily: theme.fontDisplay, fontSize: 36, fontWeight: 900, letterSpacing: '-0.035em', color: '#A87C4F' }}>{s.k}</span>
                <span style={{ fontFamily: theme.fontSans, fontSize: 12.5, color: '#1a1a1a', fontWeight: 500, maxWidth: 120, lineHeight: 1.3 }}>{s.v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT col — 5/12 · journal photo */}
        <div className="col-span-5">
          <div
            className="relative overflow-hidden desk-card"
            style={{
              borderRadius: 26,
              aspectRatio: '4 / 5',
              background: 'linear-gradient(180deg, #F7EDD8 0%, #EFE0C0 100%)',
              boxShadow: '0 40px 90px -30px rgba(0,0,0,0.35), 0 0 0 0.5px rgba(60,60,67,0.10)',
              transform: 'rotate(2.5deg)',
            }}
          >
            <img
              src={MAISON_AVOCADO_JOURNAL}
              alt="New Maison Avocado débarque à Lille"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: '50% 35%', display: 'block' }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <span className="desk-shine" aria-hidden />
            {/* Bottom overlay */}
            <span aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0" style={{ height: '48%', background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.85) 100%)' }} />
            <div className="absolute inset-x-0 bottom-0 px-5 pb-5">
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center" style={{ width: 46, height: 46, borderRadius: 999, padding: 2, background: 'conic-gradient(from 45deg, #FE8C00, #F83600, #C71585, #833AB4, #515BD4, #F77737, #FE8C00)', boxShadow: '0 6px 14px -4px rgba(0,0,0,0.30)' }}>
                  <span className="inline-flex items-center justify-center overflow-hidden" style={{ width: '100%', height: '100%', borderRadius: 999, background: theme.bgPaper, padding: 2 }}>
                    <img src={SABRIL_AVATAR} alt="Sabril" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 999, objectPosition: '50% 22%' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  </span>
                </span>
                <div className="flex items-center gap-1.5">
                  <p style={{ fontFamily: theme.fontDisplay, fontSize: 20, fontWeight: 900, letterSpacing: '-0.035em', color: '#FFFFFF', textShadow: '0 2px 8px rgba(0,0,0,0.30)' }}>
                    SABRIL J.
                  </p>
                  <span className="inline-flex items-center justify-center" style={{ width: 16, height: 16, borderRadius: 999, background: '#0095F6', boxShadow: '0 0 0 1.5px rgba(255,255,255,0.95)' }} aria-label="Verified">
                    <Check className="w-2 h-2" strokeWidth={4} style={{ color: '#FFFFFF' }} />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Desktop Trust Bar
// ─────────────────────────────────────────────────────────────────────
function DesktopTrustBar() {
  const items = ['Maison Avocado', 'Formatek', 'Motek', 'Bring Café', 'Roosevelt × LOSC', '40+ marques'];
  return (
    <section style={{ background: '#1a1a1a', color: '#FFFFFF', padding: '32px 0', borderTop: '0.5px solid rgba(255,255,255,0.10)' }}>
      <div className="max-w-7xl mx-auto px-8 flex items-center justify-between">
        <p style={{ fontFamily: theme.fontMono, fontSize: 10.5, letterSpacing: '0.30em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.60)', fontWeight: 700 }}>
          Marques accompagnées
        </p>
        <div className="flex items-center gap-8">
          {items.map((it) => (
            <span key={it} style={{ fontFamily: theme.fontDisplay, fontSize: 14, fontWeight: 700, letterSpacing: '-0.015em', color: '#FFFFFF', opacity: 0.85 }}>
              {it}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Desktop Career section — parcours horizontal
// ─────────────────────────────────────────────────────────────────────
function DesktopCareer() {
  return (
    <section id="desk-career" style={{ background: '#FAFAFA', padding: '110px 0' }}>
      <div className="max-w-7xl mx-auto px-8">
        <DesktopSectionHeader
          overline="Mon parcours"
          title="Avant de bosser pour vous, j'ai bossé là."
          subtitle="bein · BFM · HTBC · LOSC · PSG. J'ai monté Maison Avocado + Bring, revendu les concepts. Aujourd'hui : hôtels & restos partout dans le monde."
        />
        <div className="mt-14 grid grid-cols-3 gap-6">
          {SABRIL_CAREER.map((c) => (
            <div
              key={c.brand}
              className="desk-card relative overflow-hidden"
              style={{
                background: theme.bgPaper,
                borderRadius: 22,
                border: '0.5px solid rgba(60,60,67,0.10)',
                boxShadow: '0 10px 28px -14px rgba(0,0,0,0.20)',
              }}
              data-testid={`desk-career-${c.brand.replace(/\s+/g, '-').toLowerCase()}`}
            >
              {/* Photo */}
              <div className="relative overflow-hidden" style={{ width: '100%', aspectRatio: '4 / 3', background: '#F5F5F7' }}>
                <img
                  src={c.image}
                  alt={c.brand}
                  loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
                {/* Bottom gradient */}
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 bottom-0"
                  style={{
                    height: '55%',
                    background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0.78) 100%)',
                  }}
                />
                {/* Brand tint chip */}
                <div
                  className="absolute top-3 left-3 inline-flex items-center gap-1.5"
                  style={{
                    background: 'rgba(0,0,0,0.55)',
                    backdropFilter: 'blur(14px)',
                    WebkitBackdropFilter: 'blur(14px)',
                    borderRadius: 999,
                    padding: '4px 10px 4px 7px',
                    border: '0.5px solid rgba(255,255,255,0.20)',
                  }}
                >
                  <span aria-hidden style={{ width: 6, height: 6, borderRadius: 999, background: c.tint }} />
                  <span style={{ fontFamily: theme.fontMono, fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#FFFFFF', fontWeight: 700 }}>
                    {c.period}
                  </span>
                </div>
                {/* Brand name overlay */}
                <div className="absolute inset-x-0 bottom-0 px-5 pb-5">
                  <p style={{
                    fontFamily: theme.fontDisplay,
                    fontSize: 22,
                    fontWeight: 900,
                    letterSpacing: '-0.030em',
                    color: '#FFFFFF',
                    lineHeight: 1.08,
                    textShadow: '0 2px 8px rgba(0,0,0,0.35)',
                  }}>
                    {c.brand}
                  </p>
                  <p style={{
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    fontStyle: 'italic',
                    fontSize: 12.5,
                    color: 'rgba(255,255,255,0.90)',
                    marginTop: 4,
                    lineHeight: 1.35,
                    letterSpacing: '-0.005em',
                  }}>
                    {c.role}
                  </p>
                </div>
              </div>
              {/* Bullets body */}
              <div className="p-5">
                <ul className="flex flex-col gap-1.5">
                  {c.bullets.slice(0, 3).map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <span className="inline-flex items-center justify-center flex-shrink-0 mt-0.5" style={{ width: 14, height: 14, borderRadius: 999, background: '#0095F6' }} aria-hidden>
                        <Check className="w-2 h-2" strokeWidth={4} style={{ color: '#FFFFFF' }} />
                      </span>
                      <span style={{ fontFamily: theme.fontSans, fontSize: 12, color: '#1a1a1a', lineHeight: 1.45 }}>
                        {b}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Desktop Chantiers — grid 4x3
// ─────────────────────────────────────────────────────────────────────
function DesktopChantiers() {
  const scrollerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const update = () => {
      const max = el.scrollWidth - el.clientWidth;
      setCanScrollLeft(el.scrollLeft > 8);
      setCanScrollRight(el.scrollLeft < max - 8);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  const step = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    // Card width 380 + gap 24 → step ~404
    el.scrollBy({ left: dir * 404, behavior: 'smooth' });
  };

  const paletteColors = {
    gold:    { bg: '#FEF7E6', border: '#D4A74C', ink: '#8B6520' },
    indigo:  { bg: '#EEF2FF', border: '#6366F1', ink: '#4338CA' },
    rose:    { bg: '#FDF2F8', border: '#EC4899', ink: '#BE185D' },
    emerald: { bg: '#ECFDF5', border: '#10B981', ink: '#047857' },
    slate:   { bg: '#F1F5F9', border: '#64748B', ink: '#334155' },
    amber:   { bg: '#FFFBEB', border: '#F59E0B', ink: '#B45309' },
    sky:     { bg: '#F0F9FF', border: '#0EA5E9', ink: '#0369A1' },
    violet:  { bg: '#F5F3FF', border: '#8B5CF6', ink: '#6D28D9' },
    orange:  { bg: '#FFF7ED', border: '#F97316', ink: '#C2410C' },
    teal:    { bg: '#F0FDFA', border: '#14B8A6', ink: '#0F766E' },
    fuchsia: { bg: '#FDF4FF', border: '#D946EF', ink: '#A21CAF' },
    stone:   { bg: '#FAFAF9', border: '#A87C4F', ink: '#78350F' },
  };
  return (
    <section id="desk-chantiers" style={{ background: theme.bgPaper, padding: '110px 0', borderTop: '0.5px solid rgba(60,60,67,0.08)' }}>
      <div className="max-w-7xl mx-auto px-8">
        <DesktopSectionHeader
          overline="La méthode · 90 jours"
          title="Les 12 chantiers · Jour 0 → 90."
          subtitle="Un programme chronologique, mesurable, livré. Chaque chantier a ses livrables carrés."
        />
        <div className="relative mt-14">
          {/* Chevron controls — visible on desktop, positioned outside card area */}
          <button
            type="button"
            onClick={() => step(-1)}
            aria-label="Chantiers précédents"
            className="flex items-center justify-center absolute z-20 top-1/2 -translate-y-1/2 -left-4 transition-all active:scale-95"
            style={{
              width: 52, height: 52, borderRadius: 999,
              background: DA.paper,
              border: `0.5px solid ${DA.goldSoft}`,
              boxShadow: '0 14px 30px -10px rgba(11,43,36,0.35), 0 0 0 5px rgba(255,255,255,0.85)',
              cursor: canScrollLeft ? 'pointer' : 'not-allowed',
              opacity: canScrollLeft ? 1 : 0.35,
              pointerEvents: canScrollLeft ? 'auto' : 'none',
              color: DA.greenDeep,
            }}
            data-testid="desk-chantiers-scroll-left"
          >
            <ChevronRight className="w-6 h-6 rotate-180" strokeWidth={2.4} />
          </button>
          <button
            type="button"
            onClick={() => step(1)}
            aria-label="Chantiers suivants"
            className="flex items-center justify-center absolute z-20 top-1/2 -translate-y-1/2 -right-4 transition-all active:scale-95"
            style={{
              width: 52, height: 52, borderRadius: 999,
              background: DA.paper,
              border: `0.5px solid ${DA.goldSoft}`,
              boxShadow: '0 14px 30px -10px rgba(11,43,36,0.35), 0 0 0 5px rgba(255,255,255,0.85)',
              cursor: canScrollRight ? 'pointer' : 'not-allowed',
              opacity: canScrollRight ? 1 : 0.35,
              pointerEvents: canScrollRight ? 'auto' : 'none',
              color: DA.greenDeep,
            }}
            data-testid="desk-chantiers-scroll-right"
          >
            <ChevronRight className="w-6 h-6" strokeWidth={2.4} />
          </button>

          <div
            ref={scrollerRef}
            className="flex overflow-x-auto snap-x snap-mandatory gap-6 pb-6 scroll-smooth"
            style={{ scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch' }}
            data-testid="desk-chantiers-carousel"
          >
            {BRAND_ASSETS.map((b) => {
              const p = paletteColors[b.palette] || paletteColors.stone;
              return (
              <div
                key={b.key}
                className="desk-card relative overflow-hidden snap-start flex-shrink-0"
                style={{
                  width: 380,
                  background: theme.bgPaper,
                  borderRadius: 22,
                  border: '0.5px solid rgba(60,60,67,0.10)',
                  boxShadow: '0 10px 28px -14px rgba(0,0,0,0.15)',
                  scrollSnapAlign: 'start',
                }}
                data-testid={`desk-chantier-${b.key}`}
              >
                {/* Hero image — natural aspect ratio (full visible, no crop) */}
                {b.image && (
                  <div
                    className="relative"
                    style={{ width: '100%', overflow: 'hidden', background: p.bg }}
                  >
                    <img
                      src={b.image}
                      alt={b.title}
                      loading="lazy"
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                    {/* Number chip overlay top-left */}
                    <div
                      className="absolute top-4 left-4 inline-flex items-center gap-1.5"
                      style={{
                        background: 'rgba(255,255,255,0.94)',
                        backdropFilter: 'blur(12px) saturate(180%)',
                        WebkitBackdropFilter: 'blur(12px) saturate(180%)',
                        borderRadius: 999,
                        padding: '5px 12px 5px 6px',
                        border: '0.5px solid rgba(60,60,67,0.10)',
                        boxShadow: '0 4px 10px -3px rgba(0,0,0,0.15)',
                      }}
                    >
                      <span
                        style={{
                          width: 24, height: 24, borderRadius: 7,
                          background: p.border,
                          color: '#FFFFFF',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          fontFamily: theme.fontDisplay, fontSize: 11, fontWeight: 900, letterSpacing: '-0.02em',
                        }}
                      >
                        {b.num}
                      </span>
                      <span style={{ fontFamily: theme.fontMono, fontSize: 9.5, letterSpacing: '0.18em', textTransform: 'uppercase', color: p.ink, fontWeight: 700 }}>
                        {b.when}
                      </span>
                    </div>
                    {/* Bottom caption overlay */}
                    {b.imageCaption && (
                      <div
                        className="absolute inset-x-0 bottom-0"
                        style={{
                          background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.72) 100%)',
                          padding: '32px 20px 14px',
                        }}
                      >
                        <p style={{
                          fontFamily: theme.fontMono,
                          fontSize: 10,
                          letterSpacing: '0.16em',
                          textTransform: 'uppercase',
                          color: 'rgba(255,255,255,0.94)',
                          fontWeight: 700,
                          lineHeight: 1.3,
                        }}>
                          {b.imageCaption}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                {/* Body */}
                <div className="p-6">
                  <p style={{ fontFamily: theme.fontDisplay, fontSize: 20, fontWeight: 800, letterSpacing: '-0.025em', color: '#1a1a1a', lineHeight: 1.15 }}>
                    {b.title}
                  </p>
                  <p style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontStyle: 'italic', fontSize: 13, color: 'rgba(60,60,67,0.80)', marginTop: 8, lineHeight: 1.5 }}>
                    « {b.tagline} »
                  </p>
                  <div className="mt-5 pt-5" style={{ borderTop: '0.5px solid rgba(60,60,67,0.10)' }}>
                    <p style={{ fontFamily: theme.fontMono, fontSize: 9.5, letterSpacing: '0.22em', textTransform: 'uppercase', color: p.ink, fontWeight: 700, marginBottom: 10 }}>
                      Livrables
                    </p>
                    <ul className="flex flex-col gap-2">
                      {b.deliverables.map((d) => (
                        <li key={d} className="flex items-start gap-2.5">
                          <span className="inline-flex items-center justify-center flex-shrink-0 mt-0.5" style={{ width: 15, height: 15, borderRadius: 999, background: '#0095F6' }} aria-hidden>
                            <Check className="w-2 h-2" strokeWidth={4} style={{ color: '#FFFFFF' }} />
                          </span>
                          <span style={{ fontFamily: theme.fontSans, fontSize: 12.5, color: '#1a1a1a', lineHeight: 1.45 }}>
                            {d}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Desktop Offers — row of 5 cards
// ─────────────────────────────────────────────────────────────────────
function DesktopOffers() {
  const navigate = useNavigate();
  return (
    <section
      id="desk-offers"
      style={{
        background: 'linear-gradient(180deg, #1a1a1a 0%, #0A0A0F 100%)',
        color: '#FFFFFF',
        padding: '110px 0',
      }}
    >
      <div className="max-w-7xl mx-auto px-8">
        <DesktopSectionHeader
          overline="Les formats"
          title="2 offres. Vous choisissez."
          subtitle="Du Sabril Express 790€ à la direction de marque mensuelle. Chaque format est pensé, structuré, livré."
          dark
        />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {OFFERS.map((o) => (
            <article
              key={o.key}
              onClick={(e) => {
                if (e.target.closest('[data-cta-btn="true"]')) return;
                if (o.ctaAction === 'configurer-open') {
                  navigate('/configurer');
                } else {
                  navigate(`/offre/${o.key}`);
                }
              }}
              role="link"
              tabIndex={0}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !e.target.closest('[data-cta-btn="true"]')) {
                  e.preventDefault();
                  if (o.ctaAction === 'configurer-open') {
                    navigate('/configurer');
                  } else {
                    navigate(`/offre/${o.key}`);
                  }
                }
              }}
              className="relative overflow-hidden desk-card cursor-pointer"
              style={{
                background: o.cardBg || `linear-gradient(180deg, ${o.accent} 0%, #1a1a1a 100%)`,
                borderRadius: 22,
                padding: 20,
                color: '#0B2B24',
                border: `0.5px solid ${o.accent}55`,
                boxShadow: o.cardShadow || `0 18px 44px -18px ${o.accent}88`,
              }}
              data-testid={`desk-offer-${o.key}`}
            >
              <span className="desk-shine" aria-hidden />
              <p style={{ fontFamily: theme.fontMono, fontSize: 9.5, letterSpacing: '0.24em', textTransform: 'uppercase', color: '#8B6520', fontWeight: 700 }}>
                {o.pill}
              </p>
              <p style={{ fontFamily: theme.fontDisplay, fontSize: 18, fontWeight: 800, letterSpacing: '-0.025em', marginTop: 4, lineHeight: 1.15, color: '#0B2B24' }}>
                {o.short}
              </p>
              <p style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontStyle: 'italic', fontSize: 11.5, color: 'rgba(60,60,67,0.78)', marginTop: 4 }}>
                {o.tagline}
              </p>
              <div className="mt-4">
                <p style={{ fontFamily: theme.fontDisplay, fontSize: 26, fontWeight: 900, letterSpacing: '-0.035em', lineHeight: 1, color: o.accent }}>
                  {o.priceFrom}
                </p>
                <p style={{ fontFamily: theme.fontSans, fontSize: 11, color: 'rgba(60,60,67,0.72)', marginTop: 4 }}>
                  {o.priceSub}
                </p>
              </div>
              <ul className="mt-4 flex flex-col gap-1.5">
                {o.bullets.slice(0, 3).map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <span className="inline-flex items-center justify-center flex-shrink-0 mt-0.5" style={{ width: 14, height: 14, borderRadius: 999, background: o.accent, boxShadow: '0 0 0 1px rgba(0,0,0,0.06)' }} aria-hidden>
                      <Check className="w-2 h-2" strokeWidth={4} style={{ color: '#FFFFFF' }} />
                    </span>
                    <span style={{ fontFamily: theme.fontSans, fontSize: 11.5, color: 'rgba(60,60,67,0.92)', lineHeight: 1.4 }}>
                      {b}
                    </span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (o.ctaAction === 'configurer-open') {
                    navigate('/configurer');
                  } else {
                    navigate(`/offre/${o.key}`);
                  }
                }}
                data-cta-btn="true"
                className="w-full mt-5 inline-flex items-center justify-center gap-1.5 active:scale-[0.97] transition-transform"
                style={{
                  padding: '9px 14px',
                  borderRadius: 999,
                  background: theme.bgPaper,
                  color: '#1a1a1a',
                  fontFamily: theme.fontDisplay, fontSize: 12.5, fontWeight: 800,
                  letterSpacing: '-0.005em',
                  boxShadow: '0 6px 14px -4px rgba(0,0,0,0.25)',
                }}
                data-testid={`desk-offer-cta-${o.key}`}
              >
                Voir le détail
                <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.6} />
              </button>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Desktop Contact — 2 big CTA cards
// ─────────────────────────────────────────────────────────────────────
function DesktopContact() {
  return (
    <section id="desk-contact" style={{ background: '#FAFAFA', padding: '110px 0' }}>
      <div className="max-w-7xl mx-auto px-8">
        <DesktopSectionHeader
          overline="Prochaine étape"
          title="Prenons un café ou parlons avant."
          subtitle="Deux façons d'entrer en contact. Vous choisissez le format qui vous convient."
        />
        <div className="mt-14 grid grid-cols-2 gap-8">
          {/* Café card */}
          <article
            className="relative overflow-hidden desk-card"
            style={{
              background: 'linear-gradient(135deg, #FE8C00 0%, #A87C4F 100%)',
              borderRadius: 26,
              padding: 40,
              color: '#FFFFFF',
              boxShadow: '0 22px 52px -20px rgba(168,124,79,0.55)',
              minHeight: 340,
            }}
            data-testid="desk-contact-cafe"
          >
            <span className="desk-shine" aria-hidden />
            <div className="flex items-start gap-4">
              <span
                className="inline-flex items-center justify-center flex-shrink-0"
                style={{ width: 56, height: 56, borderRadius: 18, background: 'rgba(255,255,255,0.18)', border: '0.5px solid rgba(255,255,255,0.30)' }}
              >
                <Coffee className="w-6 h-6" strokeWidth={2.2} style={{ color: '#FFFFFF' }} />
              </span>
              <div>
                <p style={{ fontFamily: theme.fontMono, fontSize: 10.5, letterSpacing: '0.26em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.85)', fontWeight: 700 }}>
                  Le format que je préfère
                </p>
                <p style={{ fontFamily: theme.fontDisplay, fontSize: 34, fontWeight: 900, letterSpacing: '-0.035em', lineHeight: 1.08, color: '#FFFFFF', marginTop: 6 }}>
                  Prenons un café<br />ensemble.
                </p>
              </div>
            </div>
            <p style={{ fontFamily: theme.fontSans, fontSize: 14, color: 'rgba(255,255,255,0.92)', marginTop: 24, lineHeight: 1.55, maxWidth: 480 }}>
              On se voit en vrai — IRL dans une de mes villes (Marrakech · Marbella · Cannes · Paris · Miami). Café, discussion honnête, plan sur-mesure.
            </p>
            <button
              type="button"
              onClick={() => window.location.assign('#cafe-desktop')}
              className="mt-8 inline-flex items-center gap-2 active:scale-[0.97] transition-transform"
              style={{
                background: theme.bgPaper,
                color: '#A87C4F',
                borderRadius: 999,
                padding: '13px 22px',
                fontFamily: theme.fontDisplay, fontSize: 14, fontWeight: 800,
                boxShadow: '0 10px 22px -8px rgba(0,0,0,0.28)',
              }}
              data-testid="desk-contact-cafe-cta"
            >
              <Coffee className="w-4 h-4" strokeWidth={2.4} />
              Choisir ma date
            </button>
          </article>

          {/* Chat card */}
          <article
            className="relative overflow-hidden desk-card"
            style={{
              background: theme.bgPaper,
              borderRadius: 26,
              padding: 40,
              color: '#1a1a1a',
              border: '0.5px solid rgba(0,122,255,0.15)',
              boxShadow: '0 22px 52px -20px rgba(0,122,255,0.30)',
              minHeight: 340,
            }}
            data-testid="desk-contact-chat"
          >
            <div className="flex items-start gap-4">
              <span
                className="inline-flex items-center justify-center flex-shrink-0"
                style={{
                  width: 56, height: 56, borderRadius: 18,
                  background: 'linear-gradient(180deg, #007AFF 0%, #0A84FF 100%)',
                  boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.40), 0 6px 14px -3px rgba(0,122,255,0.45)',
                }}
              >
                <MessageCircle className="w-6 h-6" strokeWidth={2.2} style={{ color: '#FFFFFF' }} />
              </span>
              <div>
                <div className="flex items-center gap-1.5">
                  <span aria-hidden className="block" style={{ width: 8, height: 8, borderRadius: 999, background: '#34C759', boxShadow: '0 0 0 3px rgba(52,199,89,0.20)', animation: 'desk-pulse 1.4s ease-in-out infinite' }} />
                  <p style={{ fontFamily: theme.fontMono, fontSize: 10.5, letterSpacing: '0.26em', textTransform: 'uppercase', color: '#007AFF', fontWeight: 700 }}>
                    En ligne · réponse perso
                  </p>
                </div>
                <p style={{ fontFamily: theme.fontDisplay, fontSize: 34, fontWeight: 900, letterSpacing: '-0.035em', lineHeight: 1.08, color: '#1a1a1a', marginTop: 6 }}>
                  Discuter<br />avant.
                </p>
              </div>
            </div>
            <p style={{ fontFamily: theme.fontSans, fontSize: 14, color: 'rgba(60,60,67,0.78)', marginTop: 24, lineHeight: 1.55, maxWidth: 480 }}>
              Une question avant de vous engager ? Ouvre le chat — je réponds perso, à votre rythme. Pas de call. Juste des messages, clairs, directs.
            </p>
            <button
              type="button"
              onClick={() => openSabril()}
              className="mt-8 inline-flex items-center gap-2 active:scale-[0.97] transition-transform"
              style={{
                background: 'linear-gradient(180deg, #007AFF 0%, #0A84FF 100%)',
                color: '#FFFFFF',
                borderRadius: 999,
                padding: '13px 22px',
                fontFamily: theme.fontDisplay, fontSize: 14, fontWeight: 800,
                boxShadow: '0 10px 22px -8px rgba(0,122,255,0.55)',
              }}
              data-testid="desk-contact-chat-cta"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={2.4} />
              Ouvrir le chat
            </button>
          </article>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Desktop Footer
// ─────────────────────────────────────────────────────────────────────
function DesktopFooter() {
  return (
    <footer style={{ background: '#0A0A0F', color: 'rgba(255,255,255,0.65)', padding: '48px 0' }}>
      <div className="max-w-7xl mx-auto px-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span
            className="inline-flex items-center justify-center"
            style={{ width: 32, height: 32, borderRadius: 10, background: '#FFFFFF' }}
          >
            <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: 900, fontSize: 16, color: '#1a1a1a', letterSpacing: '-0.05em' }}>S</span>
          </span>
          <span style={{ fontFamily: theme.fontDisplay, fontSize: 14, fontWeight: 700, color: '#FFFFFF' }}>
            Sabril Jurczeszyn
          </span>
        </div>
        <p style={{ fontFamily: theme.fontMono, fontSize: 10.5, letterSpacing: '0.28em', textTransform: 'uppercase', fontWeight: 700 }}>
          Salon Privé © 2026 · Made with ♥ à Lille
        </p>
        <div className="flex items-center gap-5" style={{ fontFamily: theme.fontSans, fontSize: 12.5 }}>
          <span>@sabril.j</span>
          <span>hello@sabril.co</span>
        </div>
      </div>
    </footer>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Desktop Section Header
// ─────────────────────────────────────────────────────────────────────
function DesktopSectionHeader({ overline, title, subtitle, dark = false }) {
  const overlineColor = dark ? '#D4A74C' : '#8B6520';
  const titleColor = dark ? '#FFFFFF' : '#1a1a1a';
  const subColor = dark ? 'rgba(255,255,255,0.72)' : 'rgba(60,60,67,0.78)';
  return (
    <div className="max-w-3xl">
      <p style={{ fontFamily: theme.fontMono, fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase', color: overlineColor, fontWeight: 700 }}>
        {overline}
      </p>
      <h2 style={{ fontFamily: theme.fontDisplay, fontSize: 54, fontWeight: 900, letterSpacing: '-0.035em', lineHeight: 1.02, color: titleColor, marginTop: 12 }}>
        {title}
      </h2>
      {subtitle && (
        <p style={{ fontFamily: theme.fontSans, fontSize: 16, color: subColor, marginTop: 16, lineHeight: 1.55, maxWidth: 620 }}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

export default function SalonPrivePage() {
  const location = useLocation();

  useEffect(() => {
    const previous = document.title;
    document.title = 'Salon Privé · Sabril Jurczeszyn — Wallet';
    return () => { document.title = previous; };
  }, []);

  // Hash-scroll: if the URL contains #<id>, keep scrolling to it until either
  // (a) the element sits at viewport top OR (b) 3s elapse. We can't exit on
  // "position stable" because heavy async content (Instagram embeds, images)
  // keeps pushing the target down for a couple seconds.
  useEffect(() => {
    const hash = location.hash?.replace('#', '');
    if (!hash) return;

    const prevRestoration = window.history.scrollRestoration;
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    let cancelled = false;
    const deadline = Date.now() + 3000;

    const tick = () => {
      if (cancelled) return;
      const el = document.getElementById(hash);
      if (el) {
        const rect = el.getBoundingClientRect();
        // Consider "landed" only when element is within 2px of viewport top
        if (Math.abs(rect.top) < 2) {
          // Stop after we've been landed for 400ms (avoid content growing further)
          if (Date.now() > deadline) return;
        } else {
          const top = rect.top + window.scrollY;
          window.scrollTo({ top, left: 0, behavior: 'auto' });
        }
      }
      if (Date.now() < deadline) {
        setTimeout(tick, 60);
      }
    };
    // First scroll on next frame so React has painted the initial layout
    requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = prevRestoration;
      }
    };
  }, [location.hash, location.key]);

  return (
    <div
      data-testid="salon-prive-page"
      className="min-h-screen w-full relative"
      style={{
        background: DA.ivory,
        fontFamily: theme.fontSans,
      }}
    >
      {/* ═══════════════════════════════════════════════════════════════
          DESKTOP EXPERIENCE — full layout web sur ≥lg (1024px)
          ═══════════════════════════════════════════════════════════════ */}
      <div className="hidden lg:block">
        <DesktopExperience />
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          MOBILE EXPERIENCE — iPhone frame sur <lg
          ═══════════════════════════════════════════════════════════════ */}
      <div className="lg:hidden flex justify-center" style={{
        backgroundImage: `radial-gradient(circle at 50% 0%, ${DA.ivoryLight} 0%, ${DA.ivory} 60%, ${DA.ivoryDeep} 100%)`,
      }}>
      {/* iPhone frame — mockup feel on desktop, full-bleed on mobile */}
      <div
        className="w-full mx-auto relative bg-white sm:my-4 sm:rounded-[44px] sm:shadow-2xl overflow-hidden"
        style={{
          maxWidth: 414,
          background: theme.bgSecondary,
          minHeight: '100vh',
        }}
        data-testid="iphone-frame"
      >
        <NavBar />
        <ChapterProgress />

        <div className="pb-32">
          {/* ═══════════════════════════════════════════════════════════
              SECTION 1 · QUI JE SUIS — hero cover + parcours
              ═══════════════════════════════════════════════════════════ */}
          <HeroCover />
          <MobileThreePillars />
          <div id="section-career"><CareerSwipe /></div>

          {/* ═══════════════════════════════════════════════════════════
              SECTION 2 · COMMENT ÇA FONCTIONNE — parcours 4 étapes
              ═══════════════════════════════════════════════════════════ */}
          <CommentCaFonctionne />

          {/* ═══════════════════════════════════════════════════════════
              SECTION 3 · CE QUE JE VAIS FAIRE — les 12 chantiers
              ═══════════════════════════════════════════════════════════ */}
          <div id="section-chantiers"><ProgrammeDetails /></div>

          {/* ═══════════════════════════════════════════════════════════
              SECTION 3 · WHATSAPP DIRECT — contact humain unique
              ═══════════════════════════════════════════════════════════ */}
          <ChapterHeader
            num="05"
            title="Discutons."
            tagline="Un seul canal. Vous m'écrivez, je vous réponds — pas de bot, pas de bulle, direct."
            accent="#0B2B24"
          />
          <div id="section-whatsapp" className="px-4 pb-6">
            <a
              href="https://wa.me/33745522450"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-3 active:scale-[0.98] transition"
              style={{
                background: '#25D366',
                color: '#FFFFFF',
                padding: '18px 22px',
                borderRadius: 14,
                fontFamily: DA.sans,
                fontSize: 14, fontWeight: 800,
                letterSpacing: '0.10em',
                textTransform: 'uppercase',
                boxShadow: '0 14px 32px -10px rgba(37,211,102,0.55)',
                border: 'none',
                textDecoration: 'none',
              }}
              data-testid="whatsapp-cta"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M20.52 3.48A11.86 11.86 0 0 0 12.05 0C5.5 0 .2 5.3.2 11.83c0 2.09.55 4.13 1.6 5.94L.1 24l6.4-1.68a11.83 11.83 0 0 0 5.55 1.4h.01c6.55 0 11.85-5.3 11.85-11.83 0-3.17-1.23-6.13-3.4-8.41Zm-8.47 18.22h-.01a9.83 9.83 0 0 1-5.01-1.38l-.36-.21-3.72.97 1-3.62-.24-.37a9.79 9.79 0 0 1-1.51-5.25c0-5.43 4.42-9.85 9.85-9.85 2.63 0 5.1 1.03 6.96 2.89a9.75 9.75 0 0 1 2.89 6.97c0 5.43-4.42 9.85-9.85 9.85Zm5.42-7.36c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.95 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.9-.79-1.5-1.77-1.67-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.09 4.49.71.31 1.26.49 1.7.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2.01-1.42.25-.7.25-1.29.17-1.42-.07-.12-.27-.2-.57-.35Z"/>
              </svg>
              Écrire à Sabril sur WhatsApp
            </a>
            <p className="mt-3 text-center" style={{ fontSize: 11, color: DA.inkMuted, letterSpacing: '0.10em', fontFamily: DA.sans }}>
              +33 7 45 52 24 50 · réponse sous 24h · pas de bot
            </p>
          </div>

          {/* ─── Sabril Brand Seal — iconic stamp + cursive signature ─── */}
          <div className="px-4 py-10 flex flex-col items-center gap-4" data-testid="sabril-brand-seal">
            <SunOrnament size={26} color={DA.sunBright} />
            <SabrilStamp size={64} />
            <p style={{
              fontFamily: theme.fontMono,
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: '0.32em',
              textTransform: 'uppercase',
              color: DA.goldMain,
              textAlign: 'center',
              marginTop: 4,
              lineHeight: 1.75,
            }}>
              Sabril · Audit resto<br />
              <span style={{ color: DA.inkMuted, fontWeight: 700 }}>International</span>
            </p>
            <div style={{ marginTop: 6 }}>
              <SabrilSignature size={44} color={DA.greenDeep} />
            </div>
            <p style={{
              fontFamily: theme.fontMono,
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              color: DA.goldMain,
              textAlign: 'center',
              lineHeight: 1.85,
              marginTop: 2,
            }}>
              Créer. Optimiser. Développer.<br />
              Des restaurants qui <span style={{ fontWeight: 800 }}>marquent</span>.
            </p>
          </div>

          <Footer />
        </div>

        <HomeIndicator />
      </div>
      </div>

      <EconomieCheckoutModal />
      <QuoteRequestModal />
      <ContractSignModal />
    </div>
  );
}
